// Customer Record — single-customer deep-dive page.
//
// Reached from the mini-CRM popup's "Full record" button (shell.ts wires
// the click → navigate("customer", { id })) or directly via
// `#/customer?id=<id>` / `#/customer?name=<urlencoded>`.
//
// Source-of-truth reference: /var/www/kitchenos/portal-standalone.html
//   page section markup:    line 2674 (#page-customer)
//   styles (#cust-record-styles): lines 19085–19333 (ported verbatim
//                                                 to ./styles.css)
//   base renderer:          lines 19385–19821 (renderCustomerRecord)
//   wrapper #1 (qsync):     lines 20100–20116 — re-syncs quote statuses
//                            after each render; modular doesn't need the
//                            wrapper pattern because the page re-renders
//                            on navigation.
//   wrapper #2 (orders):    lines 20717–20851 — injects a per-supplier
//                            Orders card driven by localStorage
//                            `kos_customer_orders_v1`. NOT PORTED —
//                            there is no orders backend in modular, and
//                            the rule says no localStorage for business
//                            data. Empty state instead.
//   wrapper #3 (designs+msgs replace): lines 28448–28598 — replaces
//                            Plans card with the "Designs & Plans" card
//                            and replaces Messages card with a richer
//                            multi-channel rendering. Modular ports the
//                            visual structure of both cards directly
//                            into the base render.
//   wrapper #4 (events Add): lines 35465–35582 — adds an "+ Add New"
//                            button to the Bookings card that opens
//                            a customer-event modal and injects
//                            user-saved events from
//                            `kos_customer_events_v1`. Modular keeps
//                            the button (wired to a simple booking-creation
//                            flow against POST /portal/calendar) but
//                            drops the localStorage-backed user-events
//                            list.
//   wrapper #5 (workflow):  lines 36489–36572 — `kosWfInjectIntoRecord`
//                            adds a "Workflow & Operations" section
//                            (follow-ups, checklist, payments, timeline,
//                            handover, post-install). NO modular backend
//                            exists for any of these sub-cards — the
//                            section renders as an empty state.
//
// Backend endpoints used:
//   GET  /api/crm/contacts                — locate the customer
//   GET  /api/portal/bookings             — bookings filtered by customer
//   POST /api/portal/calendar             — create a new calendar event
//                                            with `customer: <name>`
//   GET  /api/portal/calendar             — calendar events (filtered by
//                                            customer name)
//   GET  /api/portal/deliveries           — deliveries filtered by customer
//
// Endpoints intentionally not called (no retailer-portal mount exists):
//   /api/portal/quotes        → empty state
//   /api/portal/designs       → empty state
//   /api/portal/documents     → empty state
//   /api/portal/notes         → empty state (no notes table)
//   /api/portal/orders        → empty state
//   /api/portal/workflow      → empty state
//
// Constraints honoured:
//   - No globals on `window`. All helpers are module-scope or closures.
//   - All handlers use addEventListener.
//   - No localStorage for business data.
//   - TypeScript strict + exactOptionalPropertyTypes.

import { ApiError } from "@tkos/api-client";
import { confirmDialog, createModal, iconMarkup, promptDialog, repaintPreservingScroll, type MiniCrmCustomer } from "@tkos/shared-ui";

import { registerPage, navigate } from "../../shell/router";
import * as realtime from "../../shell/realtime";
import { customerCache } from "../../shell/customer-cache";
import { openCustomerComposeModal } from "../../shell/customer-compose-modal";
import { attachAutoGrow } from "../../shell/auto-grow";
import {
  ATTACH_ACCEPT_ATTR,
  uploadMessageAttachments,
  renderAttachChipHtml,
  normaliseAttachments,
  type MessageAttachment,
} from "../../shell/message-attachments";
import { openLightbox, isImageUrl, type LightboxImage } from "../../shell/lightbox";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

// Re-use defensive jsonb-or-string formatter from CRM. Means the address row
// never renders `[object Object]` (master-doc priority bug #1).
import { formatAddress } from "../crm/page";

import "./styles.css";
import { autoSendConfirmText, normaliseStripeItem, paidForQuote, txBelongsTo, type WireStripeItem, type WireTransaction } from "./payments-calc";

// ── Types ────────────────────────────────────────────────────────────────────

type AddressLike =
  | string
  | null
  | undefined
  | { [k: string]: unknown };

interface WizardPhoto {
  name?: string;
  url?: string;
  sizeBytes?: number;
}

interface ServerContact {
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
  stage?: string;
  notes?: string;
  address?: AddressLike;
  street?: string;
  town?: string;
  postcode?: string;
  county?: string;
  project?: string;
  kitchenType?: string;
  value?: number;
  budget?: string;
  nextAction?: string;
  lastTouch?: string;
  lastTouchAt?: number | string;
  score?: number;
  hot?: boolean;
  /** ISO timestamp the contact/lead was created — used as the wizard
   *  submission date on the Customer Brief card. */
  created?: string;
  source?: string;
  /** Immutable "retailer typed this contact in" marker (migration 0068). Gates
   *  the contact email/phone edit + unmask carve-out. NOT derived from the
   *  editable `source`. */
  manualEntry?: boolean;
  /** Optional second contact (couples / joint customers, migration 0071). Always
   *  retailer-entered → always editable; revealed raw via the dedicated
   *  reveal-second-contact endpoint regardless of manualEntry. */
  secondaryName?: string;
  secondaryEmail?: string;
  secondaryPhone?: string;
  /** Dashboard Job Pipeline stage (leads.job_stage) — distinct from `stage`. */
  jobStage?: string;
  /** Customer-journey controls (migration 0034) — drive the customer portal's
   *  journey bar. supplyType: '' | 'supply_install' | 'supply_only'. */
  supplyType?: string;
  journeyComplete?: boolean;
  data?: {
    wizard_data?: {
      photos?: WizardPhoto[];
      [k: string]: unknown;
    };
    [k: string]: unknown;
  };
}

interface ServerBooking {
  id?: string;
  customer?: string;
  type?: string;
  status?: string;
  when?: number | string;
  scheduled_at?: number | string;
  booking_type?: string;
  notes?: string | null;
}

/** Wire shape from GET /api/portal/customer-bookings (id-filtered, friendly
 *  labels). Distinct from ServerBooking, which is the global /portal/bookings
 *  list shape. */
interface CustomerBooking {
  id?: string;
  type?: string;
  typeLabel?: string;
  status?: string;
  scheduledAt?: number | null;
  notes?: string;
  createdAt?: number | null;
}

interface ServerCalendarEvent {
  id?: string;
  title?: string;
  type?: string;
  date?: string;
  time?: string;
  end_time?: string;
  all_day?: boolean;
  notes?: string | null;
  customer?: string;
}

interface ServerDelivery {
  id?: string;
  ref?: string;
  customer?: string | { name?: string };
  supplier?: string;
  status?: string;
  eta?: number | string | null;
  description?: string;
}

interface Customer {
  id: string;
  name: string;
  hot: boolean;
  stage: string;
  email: string;
  phone: string;
  project: string;
  value: number;
  /** Budget band string as captured by the wizard (e.g. "£10k–£15k"). Shown
   *  verbatim on the record; `value` carries the midpoint number for stats. */
  budget: string;
  nextAction: string;
  lastTouch: string;
  score: number;
  address: string;
  town: string;
  postcode: string;
  street: string;
  /** Customer-journey controls — '' means not yet set (defaults to install). */
  supplyType: "" | "supply_install" | "supply_only";
  journeyComplete: boolean;
  /** Lead origin (customers.source) — wizard/phone/walk-in/referral/whatsapp/other.
   *  DISTINCT from wizard_data.leadSource ("how they heard"). */
  source: string;
  /** Immutable "retailer typed this contact in" marker (0068). TRUE → email/
   *  phone are visible + editable on this record; FALSE (customer-provided) →
   *  masked + read-only. NOT derived from the editable `source`. */
  manualEntry: boolean;
  /** Optional second contact (couples / joint customers, migration 0071). Always
   *  editable; email/phone revealed raw via reveal-second-contact. */
  secondaryName: string;
  secondaryEmail: string;
  secondaryPhone: string;
  /** Dashboard Job Pipeline stage (leads.job_stage) — distinct from `stage`. */
  jobStage: string;
  /** Wizard-collected customer photos (URL-form only — data URLs filtered out). */
  wizardPhotos: Array<{ name: string; url: string; sizeBytes: number }>;
  /** Full wizard submission payload (the `project` object) for the Customer
   *  Brief card. Null when this isn't a wizard-sourced lead. */
  wizardData: Record<string, unknown> | null;
  /** ISO date the lead was created — shown as the brief's submission date. */
  submittedAt: string;
}

interface CalItem {
  title: string;
  dateMs: number | null;
  typeLabel: string;
  color: string;
}

// ── Constants & helpers ──────────────────────────────────────────────────────

const STAGE_COLORS: Record<string, string> = {
  Lead:      "#3b82f6",
  Qualified: "#8b5cf6",
  Quoted:    "#e58200",
  Approved:  "#22c55e",
  Design:    "#14b8a6",
  Won:       "#22c55e",
  Lost:      "#ef4444",
  "On site": "#f59e0b",
  Installed: "#10b981",
};

function stageColor(s: string): string {
  return STAGE_COLORS[s] ?? "#888";
}

const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

function fmtMoney(v: number | string | null | undefined): string {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return "£0";
  return GBP.format(n);
}

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function fmtDate(d: number | string | null | undefined): string {
  if (d == null || d === "") return "—";
  const t = typeof d === "number" ? d : Date.parse(d);
  if (!Number.isFinite(t)) return "—";
  return DATE_FMT.format(new Date(t));
}

const DATETIME_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function fmtDateTime(d: number | string | null | undefined): string {
  if (d == null || d === "") return "—";
  const t = typeof d === "number" ? d : Date.parse(d);
  if (!Number.isFinite(t)) return "—";
  return DATETIME_FMT.format(new Date(t));
}

// Compact stamp for "Viewed"/"Seen" indicators, e.g. "26 Jun 14:32" (no year).
const SEEN_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
});
function fmtSeen(t: number): string {
  return SEEN_FMT.format(new Date(t)).replace(",", "");
}

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0] ?? "")
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

function esc(s: string | number | null | undefined): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function toMs(d: number | string | null | undefined): number | null {
  if (d == null || d === "") return null;
  const t = typeof d === "number" ? d : Date.parse(d);
  return Number.isFinite(t) ? t : null;
}

function coerceStage(raw: unknown): string {
  if (typeof raw !== "string") return "Lead";
  const t = raw.trim();
  if (!t) return "Lead";
  // Tolerate lower-case keys from /api/crm/contacts.
  const lc = t.toLowerCase();
  const TITLE: Record<string, string> = {
    lead: "Lead",
    qualified: "Qualified",
    quoted: "Quoted",
    won: "Won",
    lost: "Lost",
    approved: "Approved",
    design: "Design",
    installed: "Installed",
    "on site": "On site",
  };
  return TITLE[lc] ?? t;
}

// ── Normalisation ───────────────────────────────────────────────────────────

function buildAddress(c: ServerContact): AddressLike {
  if (c.address != null && (typeof c.address !== "string" || c.address.trim())) {
    return c.address;
  }
  const obj: { line1?: string; city?: string; county?: string; postcode?: string } = {};
  if (typeof c.street === "string" && c.street.trim()) obj.line1 = c.street.trim();
  if (typeof c.town === "string" && c.town.trim()) obj.city = c.town.trim();
  if (typeof c.county === "string" && c.county.trim()) obj.county = c.county.trim();
  if (typeof c.postcode === "string" && c.postcode.trim()) obj.postcode = c.postcode.trim();
  return obj;
}

function normalise(c: ServerContact): Customer {
  const id = String(c.id ?? `tmp-${Math.random().toString(36).slice(2)}`);
  const name = String(c.name ?? "").trim() || "Unnamed customer";
  const score =
    typeof c.score === "number" && Number.isFinite(c.score)
      ? Math.max(0, Math.min(100, Math.round(c.score)))
      : 50;
  const value =
    typeof c.value === "number" && Number.isFinite(c.value)
      ? c.value
      : Number(String(c.budget ?? "0").replace(/[^0-9.]/g, "")) || 0;

  // Pull town/postcode/street back out of the jsonb so we can put them in the
  // header subline (matches source's behaviour).
  let town = String(c.town ?? "").trim();
  let postcode = String(c.postcode ?? "").trim();
  let street = String(c.street ?? "").trim();
  if (c.address && typeof c.address === "object" && !Array.isArray(c.address)) {
    const a = c.address as Record<string, unknown>;
    if (!town && typeof a["city"] === "string") town = String(a["city"]).trim();
    if (!postcode && typeof a["postcode"] === "string") postcode = String(a["postcode"]).trim();
    if (!street && typeof a["line1"] === "string") street = String(a["line1"]).trim();
  }

  // Extract wizard photos. Only URL-form entries are shown; data-URL legacy
  // entries (pre-2026-05-27 wizard submits) are filtered out — the persistence
  // path now always writes URLs into customers.wizard_data.photos[].
  const rawPhotos = c.data?.wizard_data?.photos;
  const wizardPhotos = Array.isArray(rawPhotos)
    ? rawPhotos
        .filter((p): p is WizardPhoto => !!p && typeof p === "object" && typeof p.url === "string" && p.url.startsWith("/"))
        .map((p) => ({
          name: String(p.name ?? ""),
          url: String(p.url ?? ""),
          sizeBytes: typeof p.sizeBytes === "number" ? p.sizeBytes : 0,
        }))
    : [];

  return {
    id,
    name,
    hot: Boolean(c.hot),
    stage: coerceStage(c.stage),
    email: String(c.email ?? "").trim(),
    phone: String(c.phone ?? "").trim(),
    project: String(c.project ?? c.kitchenType ?? "").trim() || "—",
    value,
    budget: String(c.budget ?? "").trim(),
    nextAction: String(c.nextAction ?? c.notes ?? "").trim() || "—",
    lastTouch: String(c.lastTouch ?? "").trim() || "—",
    score,
    address: formatAddress(buildAddress(c)),
    town,
    postcode,
    street,
    supplyType:
      c.supplyType === "supply_only" || c.supplyType === "supply_install"
        ? c.supplyType
        : "",
    journeyComplete: c.journeyComplete === true,
    source: String(c.source ?? "").trim().toLowerCase(),
    manualEntry: c.manualEntry === true,
    secondaryName: String(c.secondaryName ?? "").trim(),
    secondaryEmail: String(c.secondaryEmail ?? "").trim(),
    secondaryPhone: String(c.secondaryPhone ?? "").trim(),
    jobStage: String(c.jobStage ?? "new").trim().toLowerCase() || "new",
    wizardData:
      c.data && c.data.wizard_data && typeof c.data.wizard_data === "object"
        ? (c.data.wizard_data as Record<string, unknown>)
        : null,
    submittedAt: String(c.created ?? ""),
    wizardPhotos,
  };
}

function toMiniCrmShape(c: Customer): MiniCrmCustomer {
  const m: MiniCrmCustomer = {
    id: c.id,
    name: c.name,
    hot: c.hot,
    stage: c.stage,
    project: c.project,
    value: c.value,
    phone: c.phone,
    email: c.email,
    nextAction: c.nextAction,
    lastTouch: c.lastTouch,
    score: c.score,
    focusId: c.id,
  };
  if (c.street) m.street = c.street;
  if (c.town) m.town = c.town;
  if (c.postcode) m.postcode = c.postcode;
  return m;
}

// ── SVG icons used by section heads + empty states ──────────────────────────

const SVG_BACK     = iconMarkup("chevron-left", { size: "sm" });
const SVG_MSG      = iconMarkup("chat", { size: "sm" });
const SVG_PHONE    = iconMarkup("phone", { size: "sm" });
const SVG_DOC      = iconMarkup("file", { size: "sm" });
const SVG_PLAN     = iconMarkup("dashboard", { size: "sm" });
const SVG_TRUCK    = iconMarkup("truck", { size: "sm" });
const SVG_CAL      = iconMarkup("calendar", { size: "sm" });
const SVG_NOTE     = iconMarkup("note", { size: "sm" });
const SVG_PIN      = iconMarkup("pin", { size: "sm" });
const SVG_BOLT     = iconMarkup("chart", { size: "sm" });
const SVG_KIT      = iconMarkup("sparkle", { size: "sm" });
const SVG_PLUS     = iconMarkup("plus", { size: "sm" });
const SVG_PENCIL   = iconMarkup("pencil", { size: "sm" });
const SVG_TRASH    = iconMarkup("trash", { size: "sm" });
const SVG_SEND     = iconMarkup("send", { size: "sm" });
const SVG_PAPERCLIP = iconMarkup("paperclip", { size: "sm" });
const SVG_UPLOAD   = iconMarkup("upload", { size: "sm" });
const SVG_EYE      = iconMarkup("eye", { size: "sm" });
const SVG_EYE_OFF  = iconMarkup("eye-off", { size: "sm" });
const SVG_CHEVR    = iconMarkup("chevron-right", { size: "sm" });
const SVG_CHECK    = iconMarkup("check", { size: "sm" });
const SVG_CLOSE    = iconMarkup("close", { size: "sm" });
const SVG_SNAG     = iconMarkup("alert-triangle", { size: "sm" });
const SVG_CARD     = iconMarkup("card", { size: "sm" });
const SVG_IMAGE    = iconMarkup("image", { size: "sm" });

const PIPELINE_STAGES: ReadonlyArray<string> = [
  "Lead", "Qualified", "Quoted", "Approved", "Design", "Won", "Lost", "On site", "Installed",
];

// Delivery status values mirror the backend enum (portal_deliveries.js).
const DELIVERY_STATUSES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "scheduled",        label: "Scheduled"        },
  { value: "out_for_delivery", label: "Out for delivery" },
  { value: "delivered",        label: "Delivered"        },
  { value: "failed",           label: "Failed"           },
];

// Carrier list mirrors the backend coerceCarrier allowlist; "own_vehicle"
// is the retailer-owned driver path, which the Add Delivery form labels
// just "Own driver" for the in-house team.
const CARRIER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "",            label: "— Select driver —" },
  { value: "own_vehicle", label: "Own driver"        },
  { value: "dpd",         label: "DPD"               },
  { value: "apc",         label: "APC"               },
  { value: "dhl",         label: "DHL"               },
  { value: "royal_mail",  label: "Royal Mail"        },
];

function deliveryStatusLabel(v: string): string {
  return DELIVERY_STATUSES.find((s) => s.value === v)?.label ?? v;
}

function carrierLabel(v: string | null): string {
  if (!v) return "—";
  return CARRIER_OPTIONS.find((c) => c.value === v)?.label ?? v;
}

const FILE_ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf,.dwg,.svg,image/jpeg,image/png,image/webp,application/pdf,image/svg+xml";
const FILE_MAX_BYTES = 20 * 1024 * 1024;
// Documents widget — Steve's spec (2026-05-22) restricts to pdf/jpg/png/webp/docx.
// Backend allowlist includes more (svg, dwg, jpeg) but the Documents card
// only advertises this subset to keep the user-facing intent clear: written
// documents + customer-supplied photos, not CAD/design assets.
const DOC_FILE_ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.docx,application/pdf,image/jpeg,image/png,image/webp,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

// ── Page ────────────────────────────────────────────────────────────────────

const customerPage: Page = {
  name: "customer",
  label: "Customer",
  icon: "",
  hidden: true,

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();

    // ── Shell ───────────────────────────────────────────────────────────────
    container.innerHTML = `
      <section id="page-customer">
        <div id="cust-record-root">
          <div class="cr-loading">Loading customer record…</div>
        </div>
      </section>
    `;

    const root = container.querySelector<HTMLDivElement>("#cust-record-root");
    if (!root) return () => {};

    // ── Resolve id / name from route params ────────────────────────────────
    const idParam = (ctx.params["id"] ?? "").trim();
    const nameParam = (ctx.params["name"] ?? "").trim();

    if (!idParam && !nameParam) {
      renderNotFound(root, "");
      return () => ac.abort();
    }

    // Optimistically render from the cache if we have a hit — UX wins on
    // round-trip latency since the mini-CRM popup already had this customer
    // loaded.
    const cacheHit = customerCache.get(idParam || nameParam);
    let customer: Customer | null = null;
    if (cacheHit) {
      customer = miniCrmToCustomer(cacheHit);
      renderEverything(root, ctx, ac, customer);
    }

    // Track the record's current customer so the realtime handler below can
    // re-pull the right thread. Updated when the fresh fetch resolves.
    let currentCustomer: Customer | null = customer;

    // `customer` (the optimistic cache render) is handed to loadFreshAndRender
    // so the authoritative render can keep its non-empty address fields when
    // the fresh /crm/contacts row comes back blank (see preserveAddress).
    void loadFreshAndRender(root, ctx, ac, idParam, nameParam, customer, (c) => {
      currentCustomer = c;
    });

    // Live customer→retailer messages: the server broadcasts `message.received`
    // to this retailer when a customer replies. Re-pull just the messages card
    // so the new bubble appears without a manual refresh. Inbound only — the
    // retailer's own sends are shown optimistically and aren't echoed back.
    const offMessageReceived = realtime.onEvent<{ direction?: string }>(
      "message.received",
      (ev) => {
        if (ev.data && ev.data.direction === "supplier-to-retailer") return;
        if (!currentCustomer || ac.signal.aborted) return;
        void populateMessages(root, ctx, new AbortController(), currentCustomer);
        // Kit's running notes refresh too — the new message may add a point.
        void populateKitSummary(root, ctx, new AbortController(), currentCustomer);
      },
    );

    return () => {
      ac.abort();
      offMessageReceived();
    };
  },
};

function miniCrmToCustomer(m: MiniCrmCustomer): Customer {
  const street = m.street ?? "";
  const town = m.town ?? "";
  const postcode = m.postcode ?? "";
  const obj: { line1?: string; city?: string; postcode?: string } = {};
  if (street) obj.line1 = street;
  if (town) obj.city = town;
  if (postcode) obj.postcode = postcode;
  return {
    id: m.id,
    name: m.name,
    hot: Boolean(m.hot),
    stage: coerceStage(m.stage),
    email: m.email ?? "",
    phone: m.phone ?? "",
    project: m.project ?? "—",
    value: typeof m.value === "number" ? m.value : 0,
    // Mini-CRM cache carries only the numeric value; the budget band fills in
    // when loadFreshAndRender re-renders with the full ServerContact.
    budget: "",
    nextAction: m.nextAction ?? "—",
    lastTouch: m.lastTouch ?? "—",
    score: typeof m.score === "number" ? m.score : 50,
    address: formatAddress(obj),
    town,
    postcode,
    street,
    // Mini-CRM cache doesn't carry wizard photos / journey controls — they
    // appear after loadFreshAndRender re-renders with the full ServerContact.
    supplyType: "",
    journeyComplete: false,
    source: "",
    // Default masked until the fresh /crm/contacts load confirms manual_entry.
    manualEntry: false,
    // Mini-CRM cache doesn't carry the second contact — filled in on fresh load
    // via reveal-second-contact.
    secondaryName: "",
    secondaryEmail: "",
    secondaryPhone: "",
    jobStage: "new",
    wizardData: null,
    submittedAt: "",
    wizardPhotos: [],
  };
}

/** True when an address string carries real content (not blank and not the
 *  `formatAddress` "—" placeholder). */
function hasAddress(s: string): boolean {
  const t = (s || "").trim();
  return t !== "" && t !== "—";
}

/** Demo-safe guard (fix #1, 2026-06-17): the fresh /crm/contacts render is
 *  authoritative and rebuilds the whole header, so an empty address from the
 *  server's PG-first union (PG row with NULL `customers.address` shadows a
 *  populated file-store row) would blank the address the optimistic cache
 *  render just showed. Until the server-side union fallback lands (roadmap
 *  fix #2), keep the prior non-empty address fields when the fresh contact's
 *  are empty. Mutates + returns `fresh`. */
function preserveAddress(fresh: Customer, prior: Customer | null): Customer {
  if (!prior) return fresh;
  if (!hasAddress(fresh.address) && hasAddress(prior.address)) fresh.address = prior.address;
  if (!fresh.street.trim()   && prior.street.trim())   fresh.street = prior.street;
  if (!fresh.town.trim()     && prior.town.trim())     fresh.town = prior.town;
  if (!fresh.postcode.trim() && prior.postcode.trim()) fresh.postcode = prior.postcode;
  return fresh;
}

async function loadFreshAndRender(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  idParam: string,
  nameParam: string,
  prior: Customer | null,
  onCustomer?: (c: Customer) => void,
): Promise<void> {
  // Try /api/crm/contacts — same endpoint the CRM page uses.
  let contacts: ServerContact[] = [];
  try {
    const resp = await ctx.api<{ contacts?: ServerContact[] } | ServerContact[]>(
      "/crm/contacts",
      { method: "GET", signal: ac.signal },
    );
    if (Array.isArray(resp)) contacts = resp;
    else if (resp && Array.isArray(resp.contacts)) contacts = resp.contacts;
  } catch (err) {
    if (isAbortError(err)) return;
    // 404 means CRM endpoint absent — fall through with empty list.
    if (!(err instanceof ApiError && err.status === 404)) {
      console.error("[customer] load contacts failed", err);
    }
  }

  // Find by id first (preferred), then by exact-name (case-insensitive).
  const targetId = idParam.toLowerCase();
  const targetName = nameParam.toLowerCase().trim();
  const match =
    (idParam &&
      contacts.find((c) => String(c.id ?? "").toLowerCase() === targetId)) ||
    (nameParam &&
      contacts.find(
        (c) => String(c.name ?? "").trim().toLowerCase() === targetName,
      )) ||
    null;

  if (!match) {
    renderNotFound(root, idParam || nameParam);
    return;
  }

  // Keep the optimistic render's address fields if the fresh row came back
  // blank (PG-first union shadowing — see preserveAddress).
  const customer = preserveAddress(normalise(match), prior);

  // Warm the shared cache so subsequent mini-CRM popups for this customer hit
  // it directly without a fetch. Done with the MASKED values, BEFORE the reveal
  // below — the shared cache must never hold raw contact (the mini-CRM re-masks
  // anyway, but we keep raw out of it as defence-in-depth).
  customerCache.merge([toMiniCrmShape(customer)]);

  // Manually-created contacts: the list endpoint returns MASKED email/phone, but
  // the retailer typed these themselves, so the full record reveals + edits the
  // real values. Wizard/portal contacts stay masked (the server 403s the
  // reveal). Best-effort — on any error we keep the masked strings. This only
  // mutates the local `customer` used to render the full record.
  if (customer.manualEntry) {
    try {
      const rev = await ctx.api<{ ok?: boolean; email?: string; phone?: string }>(
        `/crm/contacts/${encodeURIComponent(customer.id)}/reveal-contact`,
        { method: "GET", signal: ac.signal },
      );
      if (rev?.ok) {
        customer.email = String(rev.email ?? "");
        customer.phone = String(rev.phone ?? "");
      }
    } catch (err) {
      if (isAbortError(err)) return;
      // 403 (not manual) / network — leave masked values; non-fatal.
    }
  }

  // Always reveal the optional SECOND contact (couples / joint customers). Unlike
  // the primary, this isn't gated on manualEntry — the second contact is always
  // retailer-entered, so it's revealed + editable on every record. Best-effort:
  // on any error the masked list values stand.
  try {
    const rev2 = await ctx.api<{ ok?: boolean; secondaryName?: string; secondaryEmail?: string; secondaryPhone?: string }>(
      `/crm/contacts/${encodeURIComponent(customer.id)}/reveal-second-contact`,
      { method: "GET", signal: ac.signal },
    );
    if (rev2?.ok) {
      customer.secondaryName  = String(rev2.secondaryName  ?? "");
      customer.secondaryEmail = String(rev2.secondaryEmail ?? "");
      customer.secondaryPhone = String(rev2.secondaryPhone ?? "");
    }
  } catch (err) {
    if (isAbortError(err)) return;
    // network — leave values as-is; non-fatal.
  }

  onCustomer?.(customer);
  renderEverything(root, ctx, ac, customer);
}

// ── Top-level render ────────────────────────────────────────────────────────

function renderNotFound(root: HTMLDivElement, soughtFor: string): void {
  root.innerHTML = `
    <button class="cr-back" data-cr-back="1">
      ${SVG_BACK}Back
    </button>
    <div class="cr-not-found">
      <p class="cr-nf-title">Customer not found</p>
      <p>${esc(soughtFor)} isn't in your CRM.</p>
    </div>
  `;
  bindBackButton(root);
}

function bindBackButton(root: HTMLDivElement): void {
  const btn = root.querySelector<HTMLButtonElement>("[data-cr-back]");
  btn?.addEventListener("click", () => {
    // Prefer real browser back when there's history; fall back to CRM.
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate("crm");
    }
  });
}

function renderEverything(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): void {
  const scoreCol = c.score >= 70 ? "#22c55e" : c.score >= 40 ? "#e58200" : "#ef4444";

  root.innerHTML = `
    <button class="cr-back" data-cr-back="1">
      ${SVG_BACK}Back to CRM
    </button>

    ${headerHtml(c, scoreCol)}

    <div class="cr-grid">
      <div class="cr-col-main">
        ${customerBriefHtml(c)}
        <div class="cr-card" data-section="messages">${cardLoadingHtml("Messages", SVG_MSG)}</div>
        <div class="cr-card cr-kit-summary" data-section="kit-summary">${cardLoadingHtml("Conversation Summary", SVG_KIT)}</div>
        <div class="cr-card" data-section="quotes">${cardLoadingHtml("Quotes &amp; Orders", SVG_DOC)}</div>
        <div class="cr-card" data-section="payments">${cardLoadingHtml("Payments &amp; Invoices", SVG_CARD)}</div>
        <div class="cr-card" data-section="plans">${cardLoadingHtml("Plans &amp; 3D Renders", SVG_PLAN)}</div>
        <div class="cr-card" data-section="before-after">${cardLoadingHtml("Before &amp; After", SVG_IMAGE)}</div>
      </div>
      <div class="cr-col-side">
        <div class="cr-card cr-next-action" data-section="next-action">${cardLoadingHtml("Next Action", SVG_BOLT)}</div>
        <div class="cr-card cr-pinned-notes" data-section="pinned-notes">${cardLoadingHtml("Pinned Notes", SVG_PIN)}</div>
        ${journeyControlHtml(c)}
        <div class="cr-card" data-section="bookings">${cardLoadingHtml("Bookings", SVG_CAL)}</div>
        <div class="cr-card" data-section="timeline">${cardLoadingHtml("Activity Timeline", SVG_BOLT)}</div>
        ${scoreCardHtml(c, scoreCol)}
        <div class="cr-card" data-section="deliveries">${cardLoadingHtml("Deliveries", SVG_TRUCK)}</div>
        <div class="cr-card" data-section="snags">${cardLoadingHtml("Snags", SVG_SNAG)}</div>
        <div class="cr-card" data-section="notes">${cardLoadingHtml("Notes", SVG_NOTE)}</div>
        <div class="cr-card" data-section="documents">${cardLoadingHtml("Documents", SVG_DOC)}</div>
        <div class="cr-card" data-section="wizard-photos">${cardLoadingHtml("Wizard Photos", SVG_DOC)}</div>
      </div>
    </div>
  `;

  bindBackButton(root);
  bindHeaderActions(root, c, ctx);

  // Populate each section independently. None of these block each other.
  void populateMessages(root, ctx, ac, c);
  void populateKitSummary(root, ctx, ac, c);
  void populateQuotes(root, ctx, ac, c);
  void populatePayments(root, ctx, ac, c);
  void populatePlans(root, ctx, ac, c);
  void populateBeforeAfter(root, ctx, ac, c);
  void populateDeliveries(root, ctx, ac, c);
  void populateBookingsAndCalendar(root, ctx, ac, c);
  void populateSnags(root, ctx, ac, c);
  void populatePinnedNotes(root, ctx, ac, c);
  void populateNotes(root, ctx, ac, c);
  void populateDocuments(root, ctx, ac, c);
  populateWizardPhotos(root, c);
  void populateTimeline(root, ctx, ac, c);
  void populateNextAction(root, ctx, ac, c);
  bindJourneyControl(root, ctx, c);
}

// ── Header ─────────────────────────────────────────────────────────────────
//
// Every primary contact field is rendered through `editableField()` which
// emits a `.cr-editable[data-field=…]` span. The bind step finds those
// spans and replaces them with an inline `<input>` or `<select>` on click
// — Enter commits (calls PUT /api/crm/contacts/:id), Escape cancels,
// blur commits. There is no separate edit form or modal anywhere on the
// header: it is read-mode-by-default with inline edit affordances on hover.

// ── Editable-field option lists + helpers (Task #1) ──────────────────────────
const SOURCE_VALUES   = ["wizard", "phone", "walk-in", "referral", "whatsapp", "other"];
const SOURCE_LABELS   = ["Wizard", "Phone", "Walk-in", "Referral", "WhatsApp", "Other"];
const JOBSTAGE_VALUES = ["new", "design", "quoted", "approved", "installed", "complete"];
const JOBSTAGE_LABELS = ["New Leads", "Design", "Quoted", "Approved", "Installed", "Complete"];
const BUDGET_BANDS    = ["Under £5k", "£5k-£10k", "£10k-£15k", "£15k-£20k", "£20k+"];
const TIMELINE_OPTS   = ["ASAP", "1-3 months", "3-6 months", "6-12 months", "Just browsing"];
const LEADSOURCE_OPTS = ["Google", "Social media", "Recommendation", "Showroom visit", "Other"];
const WORKTOP_OPTS    = ["Laminate", "Solid Wood", "Quartz", "Granite", "Composite", "Dekton", "Porcelain"];

// Handles + flooring (added 2026-08-20). These bind to the FLAT wizard_data
// keys `handle_style` / `handle_finish` / `flooring_type`, NOT to styleChoices.
//
// That is deliberate and is the whole reason leads captured before today light
// up with no backfill: the flat keys are where the wizard has always written
// these two answers, and where the AI render engine + layout engine read them.
// Mirroring a second copy under styleChoices would have created two sources of
// truth for one fact — a retailer edit would update the copy the CRM shows
// while the render engine kept using the stale original, which renders the
// wrong kitchen silently. One key, one writer.
//
// `handle_style` is a COMPOSITE token for bar handles (the metal is baked into
// the value) because that is the vocabulary the render engine's descriptor maps
// are keyed on. Cup and knob have no finished variants in those maps, so for
// those two the finish lives in `handle_finish` — which is why the Finish row
// below is only rendered for cup/knob. Showing it for bar as well would give
// the retailer two controls over one fact and let them disagree.
const HANDLE_VALUES = ["brass_bar", "chrome_bar", "black_bar", "nickel_bar", "cup", "knob", "handleless"];
const HANDLE_LABELS = [
  "Bar — Brass", "Bar — Chrome", "Bar — Black", "Bar — Steel",
  "Cup Handle", "Knob", "Handleless (J-pull)",
];
const HANDLE_FINISH_VALUES = ["brass", "steel", "black", "chrome"];
const HANDLE_FINISH_LABELS = ["Brass", "Steel", "Black", "Chrome"];
/** Shapes whose composite token cannot carry a finish — the only ones that
 *  need the separate Finish row. */
const HANDLE_NEEDS_FINISH_ROW = new Set(["cup", "knob"]);

/** The dropdown preselects its first option when the stored value is empty, so
 *  a blank lead would commit "Bar — Brass" just from opening the editor and
 *  pressing Enter. These lists put an explicit "not set" first. Kept separate
 *  from the display lists above so `labelFor("")` still returns "" and the row
 *  shows its "Click to add" empty state rather than a dash. */
const withBlank = (values: readonly string[]): string[] => ["", ...values];
const withBlankLabels = (labels: readonly string[]): string[] => ["— not set —", ...labels];
const FLOORING_VALUES = ["light_oak", "dark_oak", "herringbone", "stone_tile", "porcelain", "concrete", "lvt"];
const FLOORING_LABELS = [
  "Light Oak", "Dark Oak", "Herringbone Wood", "Stone Tile",
  "Porcelain Tile", "Concrete / Polished", "Vinyl / LVT",
];

function labelFor(value: string, values: readonly string[], labels: readonly string[]): string {
  const i = values.indexOf(String(value || "").toLowerCase());
  return i >= 0 ? labels[i]! : "";
}

/** Build a nested object from a dotted path: ("styleChoices.worktop","X") →
 *  { styleChoices: { worktop: "X" } }. Used to patch wizard_data. */
function nestPath(path: string, value: unknown): Record<string, unknown> {
  const keys = path.split(".");
  const root: Record<string, unknown> = {};
  let cur = root;
  for (let i = 0; i < keys.length - 1; i++) {
    const next: Record<string, unknown> = {};
    cur[keys[i]!] = next;
    cur = next;
  }
  cur[keys[keys.length - 1]!] = value;
  return root;
}

/** Client deep-merge mirroring the server's wizard_data merge (objects merge,
 *  scalars + arrays replace). Mutates + returns `target`. */
function deepMergeClient(target: Record<string, unknown>, src: Record<string, unknown>): Record<string, unknown> {
  for (const k of Object.keys(src)) {
    const sv = src[k];
    if (sv && typeof sv === "object" && !Array.isArray(sv)) {
      const tv = target[k];
      target[k] = deepMergeClient(
        tv && typeof tv === "object" && !Array.isArray(tv) ? (tv as Record<string, unknown>) : {},
        sv as Record<string, unknown>,
      );
    } else {
      target[k] = sv;
    }
  }
  return target;
}

type EditField =
  | "name"
  | "phone"
  | "email"
  | "stage"
  | "address"
  | "project"
  | "source"
  | "jobStage"
  | "budget"
  | "secondaryName"
  | "secondaryEmail"
  | "secondaryPhone"
  | "wizard";

interface EditableOpts {
  field: EditField;
  /** Raw value to seed the editor with. */
  value: string;
  /** What renders in read mode (may be masked). */
  display: string;
  /** Optional dropdown option VALUES — when present the editor is a <select>. */
  options?: ReadonlyArray<string>;
  /** Optional parallel display labels for `options` (value≠label dropdowns). */
  optionLabels?: ReadonlyArray<string>;
  /** Inline style attribute (e.g. for the stage colour swatch). */
  style?: string;
  /** When set, the edit patches wizard_data at this dotted path instead of a
   *  top-level Customer field. */
  wzPath?: string;
  /** Coercion for a wizard_data value on commit. */
  wzType?: "string" | "number" | "array";
  /** Render a <textarea> instead of an <input> (e.g. notes). */
  multiline?: boolean;
}

function editableField(opts: EditableOpts): string {
  const tag = opts.multiline ? "textarea" : opts.options ? "select" : "input";
  const optsAttr = opts.options ? ` data-cr-edit-options="${esc(opts.options.join("|"))}"` : "";
  const labelsAttr = opts.optionLabels ? ` data-cr-edit-labels="${esc(opts.optionLabels.join("|"))}"` : "";
  const wzAttr = opts.wzPath ? ` data-cr-wz="${esc(opts.wzPath)}" data-cr-wztype="${esc(opts.wzType || "string")}"` : "";
  const style = opts.style ? ` style="${opts.style}"` : "";
  return `<span class="cr-editable" tabindex="0" role="button"
                data-cr-edit="${esc(opts.field)}"
                data-cr-edit-tag="${tag}"
                data-cr-raw="${esc(opts.value)}"${optsAttr}${labelsAttr}${wzAttr}${style}>
            <span class="cr-editable-text">${opts.display || `<span class="cr-editable-empty">Click to add</span>`}</span>
            <span class="cr-edit-icon" aria-hidden="true">${SVG_PENCIL}</span>
          </span>`;
}

/**
 * Wizard leads arrive with the whole brief crammed into one pipe-separated
 * string ("Budget: £X | Timeline: Y | Source: Z | Doors: … | Worktop: … |
 * Storage: … | Kitchen Notes: …"), which surfaces verbatim in the Next action
 * field. Parse it into labelled rows so each fact sits on its own line.
 * Returns null when the value isn't a brief dump (plain text falls through to
 * the normal single-line render).
 */
function briefLinesHtml(raw: string): string | null {
  const text = (raw ?? "").trim();
  if (!text || text === "—") return null;
  // Only treat as a brief when it carries "Label: value" pairs.
  if (!/[A-Za-z][^:|]*:\s*\S/.test(text)) return null;
  const parts = text.split("|").map((s) => s.trim()).filter(Boolean);
  const rows: string[] = [];
  for (const p of parts) {
    const idx = p.indexOf(":");
    if (idx === -1) {
      rows.push(`<div class="cr-na-brief-row"><span class="cr-na-brief-v">${esc(p)}</span></div>`);
      continue;
    }
    let key = p.slice(0, idx).trim();
    const val = p.slice(idx + 1).trim();
    if (/notes$/i.test(key)) key = "Notes";
    rows.push(
      `<div class="cr-na-brief-row"><span class="cr-na-brief-k">${esc(key)}</span><span class="cr-na-brief-v">${esc(val)}</span></div>`,
    );
  }
  return rows.length ? `<div class="cr-na-brief">${rows.join("")}</div>` : null;
}

function headerHtml(c: Customer, scoreCol: string): string {
  const naBrief = briefLinesHtml(c.nextAction);
  const stageCol = stageColor(c.stage);
  const scoreLabel = c.score >= 70 ? "Strong lead" : c.score >= 40 ? "Warm lead" : "Cold lead";
  const townLine = [c.town, c.postcode].filter(Boolean).join(" ");
  // Contact-masking carve-out (defraud-sacred): email/phone are editable in
  // place ONLY for contacts the retailer entered themselves — keyed off the
  // immutable `manualEntry` flag (0068), NOT the editable `source`. For
  // customer-provided (wizard/website/portal) leads the values stay masked +
  // read-only (the reveal endpoint 403s them, so c.email/c.phone here are the
  // server-masked strings).
  const isManual = c.manualEntry;
  return `
    <div class="cr-header">
      <div class="cr-avatar" data-cr-avatar="1">${esc(initialsOf(c.name))}</div>
      <div class="cr-header-info">
        <div class="cr-name">
          ${editableField({ field: "name", value: c.name, display: esc(c.name) })}
          ${c.hot ? `<span style="color:#ef4444" title="Hot lead">${iconMarkup("pulse-dot", { size: "sm" })}</span>` : ""}
        </div>
        <div style="margin-top:4px;display:flex;align-items:center;gap:8px;flex-wrap:wrap">
          ${editableField({
            field: "stage",
            value: c.stage,
            display: esc(c.stage),
            options: PIPELINE_STAGES,
            style: `background:${stageCol}22;color:${stageCol};border:1px solid ${stageCol}44;font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px`,
          })}
          ${townLine ? `<span style="font-size:12px;color:var(--text-muted)">${esc(townLine)}</span>` : ""}
        </div>
        <div class="cr-meta-row">
          <div class="cr-meta-item"><span class="cr-meta-label">Project</span><span class="cr-meta-val">${editableField({ field: "project", value: c.project, display: esc(c.project) })}</span></div>
          <div class="cr-meta-item"><span class="cr-meta-label">Budget</span><span class="cr-meta-val" style="color:var(--orange)">${editableField({ field: "budget", value: c.budget, display: esc(c.budget || ""), options: BUDGET_BANDS })}</span></div>
          <div class="cr-meta-item"><span class="cr-meta-label">Source</span><span class="cr-meta-val">${editableField({ field: "source", value: c.source, display: esc(labelFor(c.source, SOURCE_VALUES, SOURCE_LABELS)), options: SOURCE_VALUES, optionLabels: SOURCE_LABELS })}</span></div>
          <div class="cr-meta-item"><span class="cr-meta-label">Job stage</span><span class="cr-meta-val">${editableField({ field: "jobStage", value: c.jobStage, display: esc(labelFor(c.jobStage, JOBSTAGE_VALUES, JOBSTAGE_LABELS)), options: JOBSTAGE_VALUES, optionLabels: JOBSTAGE_LABELS })}</span></div>
          <div class="cr-meta-item" style="grid-column:1/-1">
            <span class="cr-meta-label">Address</span>
            <span class="cr-meta-val">${editableField({ field: "address", value: c.address, display: esc(c.address) })}</span>
          </div>
          <div class="cr-meta-item">
            <span class="cr-meta-label">Email</span>
            <span class="cr-meta-val">${
              isManual
                ? editableField({ field: "email", value: c.email, display: c.email ? esc(c.email) : "" })
                : `<span title="Hidden to protect the customer's contact details">${c.email ? esc(c.email) : "—"}</span>`
            }</span>
          </div>
          <div class="cr-meta-item">
            <span class="cr-meta-label">Phone</span>
            <span class="cr-meta-val">${
              isManual
                ? editableField({ field: "phone", value: c.phone, display: c.phone ? esc(c.phone) : "" })
                : `<span title="Hidden to protect the customer's contact details">${c.phone ? esc(c.phone) : "—"}</span>`
            }</span>
          </div>
          ${(() => {
            // Optional second contact (couples / joint customers). Always editable
            // — never the customer's masked PII. Single-contact records stay
            // minimal: just one subtle "Second contact" add row until a name is
            // entered, then the email/phone fields appear.
            const has2 = !!(c.secondaryName || c.secondaryEmail || c.secondaryPhone);
            if (!has2) {
              return `<div class="cr-meta-item" style="grid-column:1/-1">
                <span class="cr-meta-label">Second contact</span>
                <span class="cr-meta-val">${editableField({ field: "secondaryName", value: "", display: "" })}</span>
              </div>`;
            }
            return `
              <div class="cr-meta-item">
                <span class="cr-meta-label">2nd contact</span>
                <span class="cr-meta-val">${editableField({ field: "secondaryName", value: c.secondaryName, display: c.secondaryName ? esc(c.secondaryName) : "" })}</span>
              </div>
              <div class="cr-meta-item">
                <span class="cr-meta-label">2nd email</span>
                <span class="cr-meta-val">${editableField({ field: "secondaryEmail", value: c.secondaryEmail, display: c.secondaryEmail ? esc(c.secondaryEmail) : "" })}</span>
              </div>
              <div class="cr-meta-item">
                <span class="cr-meta-label">2nd phone</span>
                <span class="cr-meta-val">${editableField({ field: "secondaryPhone", value: c.secondaryPhone, display: c.secondaryPhone ? esc(c.secondaryPhone) : "" })}</span>
              </div>`;
          })()}
          <div class="cr-meta-item"${naBrief ? ` style="grid-column:1/-1"` : ""}><span class="cr-meta-label">Next action</span>${naBrief ?? `<span class="cr-meta-val">${esc(c.nextAction)}</span>`}</div>
          <div class="cr-meta-item"><span class="cr-meta-label">Last touch</span><span class="cr-meta-val">${esc(c.lastTouch)}</span></div>
          <div class="cr-meta-item"><span class="cr-meta-label">Lead score</span>
            <span class="cr-meta-val">
              <span style="color:${scoreCol};font-weight:800">${c.score}</span>
              <span style="color:var(--text-muted);font-weight:500">/100 · ${scoreLabel}</span>
            </span>
          </div>
        </div>
      </div>
      <div class="cr-header-actions">
        <button class="cr-action-btn" data-cr-act="message">${SVG_MSG}Message</button>
        <button class="cr-action-btn" data-cr-act="call">${SVG_PHONE}Call customer</button>
        <button class="cr-action-btn primary" data-cr-act="new-quote">${SVG_PLUS}New Quote</button>
        <button class="cr-action-btn" data-cr-act="resend-link">${iconMarkup("send", { size: "sm" })}Resend portal link</button>
        <button class="cr-action-btn danger" data-cr-act="delete">${SVG_TRASH}Delete</button>
      </div>
    </div>
  `;
}

/**
 * PUT /api/crm/contacts/:id — patches the customer in place. Re-renders
 * the header (and the page-header subline + avatar initials) on success.
 * The shared customer cache is also refreshed so the next mini-CRM
 * popup sees the new values without round-tripping the network.
 *
 * On failure, the patch is reverted: caller's `c` is restored from
 * `prev` and the header re-rendered. The user sees a toast.
 */
async function patchCustomer(
  root: HTMLDivElement,
  ctx: PageContext,
  c: Customer,
  patch: Partial<Customer>,
): Promise<boolean> {
  // `wizardData` in a patch is a NESTED PARTIAL to deep-merge (not a wholesale
  // replacement), so handle it separately from the flat Customer fields.
  const wizardPatch = patch.wizardData as Record<string, unknown> | undefined | null;
  const flat = { ...patch };
  delete (flat as Partial<Customer>).wizardData;

  const prev: Partial<Customer> = {};
  for (const k of Object.keys(flat) as Array<keyof Customer>) {
    (prev as Record<string, unknown>)[k] = c[k];
  }
  const prevWizard = wizardPatch ? (JSON.parse(JSON.stringify(c.wizardData || {})) as Record<string, unknown>) : null;

  Object.assign(c, flat);
  if (wizardPatch) {
    c.wizardData = deepMergeClient((c.wizardData as Record<string, unknown>) || {}, wizardPatch);
  }
  try {
    const body: Record<string, unknown> = {};
    if (patch.name      !== undefined) body["name"]    = patch.name;
    if (patch.phone     !== undefined) body["phone"]   = patch.phone;
    if (patch.email     !== undefined) body["email"]   = patch.email;
    if (patch.stage     !== undefined) body["stage"]   = patch.stage;
    if (patch.address   !== undefined) body["address"] = patch.address;
    if (patch.project   !== undefined) body["project"] = patch.project;
    if (patch.supplyType      !== undefined) body["supplyType"]      = patch.supplyType;
    if (patch.journeyComplete !== undefined) body["journeyComplete"] = patch.journeyComplete;
    if (patch.source    !== undefined) body["source"]   = patch.source;
    if (patch.jobStage  !== undefined) body["jobStage"] = patch.jobStage;
    if (patch.budget    !== undefined) body["budget"]   = patch.budget;
    if (patch.secondaryName  !== undefined) body["secondaryName"]  = patch.secondaryName;
    if (patch.secondaryEmail !== undefined) body["secondaryEmail"] = patch.secondaryEmail;
    if (patch.secondaryPhone !== undefined) body["secondaryPhone"] = patch.secondaryPhone;
    if (wizardPatch     !== undefined) body["wizardData"] = wizardPatch;
    await ctx.api<{ ok: boolean; contact?: ServerContact }>(
      `/crm/contacts/${encodeURIComponent(c.id)}`,
      { method: "PUT", body: JSON.stringify(body) },
    );
    // Refresh the shared cache. Cheap — single merge.
    customerCache.merge([toMiniCrmShape(c)]);
    // Re-render the brief card (wizard fields) + header in place so the new
    // values and masked display reflect the change.
    rerenderBrief(root, c, ctx);
    rerenderHeader(root, c, ctx);
    ctx.showToast("Saved", "success");
    return true;
  } catch (err) {
    // Roll back local copy + re-render so the user sees the original
    // value preserved.
    Object.assign(c, prev);
    if (prevWizard) c.wizardData = prevWizard;
    rerenderBrief(root, c, ctx);
    rerenderHeader(root, c, ctx);
    const msg = (err instanceof ApiError && err.status === 404)
      ? "Customer not found"
      : "Could not save change";
    ctx.showToast(msg, "error");
    return false;
  }
}

/** Replace the Customer Brief card in place with fresh HTML. Re-binding of its
 *  editable spans happens via bindHeaderActions→bindEditableFields, called by
 *  the rerenderHeader that always follows this. No-op if the card isn't mounted. */
function rerenderBrief(root: HTMLDivElement, c: Customer, _ctx: PageContext): void {
  const old = root.querySelector<HTMLElement>('[data-section="customer-brief"]');
  if (!old) return;
  const wrapper = document.createElement("div");
  wrapper.innerHTML = customerBriefHtml(c).trim();
  const fresh = wrapper.firstElementChild;
  if (fresh) old.replaceWith(fresh);
}

function rerenderHeader(root: HTMLDivElement, c: Customer, ctx: PageContext): void {
  const old = root.querySelector<HTMLDivElement>(".cr-header");
  if (!old) return;
  const scoreCol = c.score >= 70 ? "#22c55e" : c.score >= 40 ? "#e58200" : "#ef4444";
  const wrapper = document.createElement("div");
  wrapper.innerHTML = headerHtml(c, scoreCol).trim();
  const fresh = wrapper.firstElementChild;
  if (fresh) old.replaceWith(fresh);
  bindHeaderActions(root, c, ctx);
}

function bindHeaderActions(root: HTMLDivElement, c: Customer, ctx: PageContext): void {
  // Comms is platform-only: Message + the Twilio-masked "Call customer" button
  // (mirrored by the global Call FAB). The customer's real number is never
  // shown — Phone/Email fields stay masked and calling goes through the
  // click-to-connect endpoint that dials the retailer first, then bridges.
  root.querySelector<HTMLButtonElement>('[data-cr-act="message"]')?.addEventListener("click", () => {
    // In-place compose popup — retailer stays on the customer record. Sends
    // via /portal/customer-message with attachment support. Other surfaces
    // in this file ("Open full conversation") still navigate to the
    // messages page on purpose.
    const ownRetailerId =
      (typeof localStorage !== "undefined" ? localStorage.getItem("kos_retailer_id") : null) ??
      ctx.user?.retailerId ?? "";
    const retailerName = ctx.user?.retailerName ?? "Retailer";
    openCustomerComposeModal(
      {
        customer: {
          id: c.id, name: c.name, email: c.email,
          // Optional second contact (couples / joint customers) — surfaces both
          // recipients in the composer so the retailer knows both are notified.
          secondaryName: c.secondaryName, secondaryEmail: c.secondaryEmail,
        },
        ownRetailerId,
        retailerName,
        onSent: () => {
          // Refresh the in-card mini-thread so the new message shows up
          // immediately without a page reload.
          void populateMessages(root, ctx, new AbortController(), c);
        },
      },
      ctx,
    );
  });
  root.querySelector<HTMLButtonElement>('[data-cr-act="call"]')?.addEventListener("click", () => {
    // DEFRAUD-SACRED: never `tel:` ([[feedback-contact-masking]]). Post ONLY
    // the customerId — the server resolves the customer's real (masked-to-us)
    // number and rings the retailer first, then bridges the call so the
    // customer's number never reaches the browser. Same click-to-connect
    // endpoint the Call FAB / Contacts / mini-CRM popup use, so this button
    // lights up the moment Twilio is switched on. Returns 503 until then.
    const btn = root.querySelector<HTMLButtonElement>('[data-cr-act="call"]');
    void (async () => {
      if (btn) btn.disabled = true;
      try {
        await ctx.api<{ ok: boolean }>("/portal/calls/dial", {
          method: "POST",
          body: JSON.stringify({ customerId: c.id }),
        });
        ctx.showToast(`Connecting your call to ${c.name || "the customer"}…`, "success");
      } catch (err) {
        const status = err instanceof ApiError ? err.status : undefined;
        if (status === 503) {
          ctx.showToast("Click-to-call goes live with Twilio (Phase 3).", "info");
        } else {
          ctx.showToast("Could not start call", "error");
        }
      } finally {
        if (btn) btn.disabled = false;
      }
    })();
  });
  root.querySelector<HTMLButtonElement>('[data-cr-act="new-quote"]')?.addEventListener("click", () => {
    // Open the Quote Builder with this customer pre-assigned. The QB reads
    // `customer`/`customerId` from nav params on load (fill-only). Same
    // mechanism the Next Action card uses.
    //
    // fresh=1 so this is genuinely a NEW quote: the QB otherwise rehydrates the
    // last working copy from localStorage, which is why "New quote" reopened the
    // quote you had just closed. The reset runs before the fill-only assignment
    // above, so the customer still lands on the blank quote.
    ctx.navigate("quote-builder", { customer: c.name, customerId: c.id, fresh: "1" });
  });
  root.querySelector<HTMLButtonElement>('[data-cr-act="resend-link"]')?.addEventListener("click", () => {
    if (!c.email) {
      ctx.showToast("No email on file for this customer.", "error");
      return;
    }
    const btn = root.querySelector<HTMLButtonElement>('[data-cr-act="resend-link"]');
    void (async () => {
      if (btn) btn.disabled = true;
      try {
        // Find-or-create the portal account and (re)send the welcome email with
        // a working login + portal link. retailerId omitted → server resolves
        // the canonical retailer + real branding. (No Stripe/commission.)
        // The portal only holds the MASKED email, so we pass customerId — the
        // server resolves the real address (resolveContactById) before sending.
        const res = await ctx.api<{ ok: boolean; error?: string }>("/customers/create", {
          method: "POST",
          body: JSON.stringify({ email: c.email, name: c.name, phone: c.phone, customerId: c.id }),
        });
        if (res && res.ok === false) {
          ctx.showToast(res.error || "Could not send portal link.", "error");
          return;
        }
        ctx.showToast(`Portal link sent to ${c.name || "customer"} ✓`, "success");
      } catch (err) {
        // The server returns { ok:false, error } as the response body; ApiError
        // carries that body as its message. Surface the real reason (e.g. masked
        // email) rather than a bare HTTP code.
        let msg = "Could not send portal link";
        if (err instanceof ApiError) {
          try { msg = JSON.parse(err.message)?.error || `Send failed (HTTP ${err.status})`; }
          catch { msg = `Send failed (HTTP ${err.status})`; }
        }
        ctx.showToast(msg, "error");
      } finally {
        if (btn) btn.disabled = false;
      }
    })();
  });
  root.querySelector<HTMLButtonElement>('[data-cr-act="delete"]')?.addEventListener("click", () => {
    if (!c.id) return;
    const cid = c.id;
    // NOTE: the spec restricts hard delete to the account owner. There is no
    // session role plumbed to the client (or enforced server-side) yet, so both
    // options are shown for now; the typed-DELETE confirm + audit log guard the
    // erasure. Owner-only enforcement is a follow-up once the team-member role
    // is surfaced on AuthUser / GET /api/portal/me and checked server-side.

    // Leave the record — it's been removed/scrubbed. Evict the cache first so no
    // stale mini-CRM popup can resurrect it in this tab.
    const leave = (): void => {
      customerCache.remove(cid);
      if (window.history.length > 1) window.history.back();
      else navigate("crm");
    };

    // Anonymise — removes PII, keeps job history/quotes/payments. Recommended.
    const anonymise = async (): Promise<void> => {
      const ok = await confirmDialog(
        `Anonymise ${c.name}? All personal details (name, email, phone, address) are permanently removed. Job history, quotes and payments are kept for your business records. This cannot be undone.`,
        { title: "Anonymise customer", confirmLabel: "Anonymise", destructive: true },
      );
      if (!ok) return;
      try {
        await ctx.api<{ ok: boolean }>(`/crm/contacts/${encodeURIComponent(cid)}/anonymise`, { method: "POST" });
        ctx.showToast("Customer anonymised — personal data removed", "success");
        leave();
      } catch {
        ctx.showToast("Could not anonymise customer", "error");
      }
    };

    // Hard delete — full erasure, double-confirmed by typing DELETE.
    const hardDelete = async (): Promise<void> => {
      const typed = await promptDialog(
        `Permanently delete ${c.name} and their CRM record? This erases the customer entirely and cannot be undone. Type DELETE to confirm.`,
        { title: "Delete permanently", confirmLabel: "Delete forever", placeholder: "DELETE" },
      );
      if (typed == null) return;
      if (typed.trim().toUpperCase() !== "DELETE") {
        ctx.showToast("Deletion cancelled — confirmation didn't match", "info");
        return;
      }
      try {
        await ctx.api<{ ok: boolean }>(`/crm/contacts/${encodeURIComponent(cid)}`, { method: "DELETE" });
        ctx.showToast("Customer permanently deleted", "success");
        leave();
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          ctx.showToast("Customer already removed", "info");
          leave();
          return;
        }
        ctx.showToast("Could not delete customer", "error");
      }
    };

    const modal = createModal({
      title: "Remove customer",
      subtitle: c.name,
      size: "sm",
      content: (body) => {
        body.innerHTML = `
          <p style="margin:0 0 8px;line-height:1.5">Choose how to remove this customer's data.</p>
          <ul style="margin:0;padding-left:18px;line-height:1.5;color:var(--color-text-secondary)">
            <li><strong>Anonymise</strong> removes all personal details but keeps job history, quotes and payments for your records.</li>
            <li><strong>Delete permanently</strong> erases the customer entirely (GDPR right to erasure).</li>
          </ul>`;
      },
      actions: [
        { label: "Cancel", variant: "ghost" },
        { label: "Anonymise (recommended)", variant: "primary", onClick: () => { void anonymise(); } },
        { label: "Delete permanently", variant: "danger", onClick: () => { void hardDelete(); } },
      ],
    });
    modal.open();
  });
  bindEditableFields(root, c, ctx);
}

/**
 * Click-to-edit handler for every `[data-cr-edit]` span in the header.
 * Spawns an `<input>` or `<select>` in place; Enter / blur commits via
 * `patchCustomer()`, Escape cancels. Only one editor is active at a
 * time — opening a second commits the first.
 */
function bindEditableFields(root: HTMLDivElement, c: Customer, ctx: PageContext): void {
  const spans = root.querySelectorAll<HTMLSpanElement>(".cr-editable");
  let active: HTMLElement | null = null;
  spans.forEach((span) => {
    const open = (): void => {
      if (active === span) return;
      active = span;
      const field = (span.dataset["crEdit"] as EditField | undefined) ?? "name";
      const tagAttr = span.dataset["crEditTag"];
      const raw = span.dataset["crRaw"] ?? "";
      const optsList = span.dataset["crEditOptions"]?.split("|") ?? [];
      const labelList = span.dataset["crEditLabels"]?.split("|") ?? [];
      const wzPath = span.dataset["crWz"] || "";
      const wzType = span.dataset["crWztype"] || "string";

      let el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (tagAttr === "select") {
        const sel = document.createElement("select");
        sel.className = "cr-edit-input";
        optsList.forEach((o, i) => {
          const opt = document.createElement("option");
          opt.value = o;
          opt.textContent = labelList[i] ?? o;
          if (o === raw) opt.selected = true;
          sel.appendChild(opt);
        });
        el = sel;
      } else if (tagAttr === "textarea") {
        const ta = document.createElement("textarea");
        ta.className = "cr-edit-input cr-edit-input--textarea";
        ta.value = raw;
        ta.rows = 3;
        el = ta;
      } else {
        const inp = document.createElement("input");
        inp.type = field === "email" ? "email" : field === "phone" ? "tel"
          : wzType === "number" ? "number" : "text";
        inp.className = "cr-edit-input";
        inp.value = raw;
        // Avoid the browser's auto-`tel:` and address-autofill bleed.
        inp.autocomplete = "off";
        el = inp;
      }

      // Stash the original span markup so cancel can restore it exactly.
      const original = span.cloneNode(true) as HTMLElement;
      span.replaceWith(el);
      el.focus();
      if (el instanceof HTMLInputElement) el.select();

      let done = false;
      const commit = async (save: boolean): Promise<void> => {
        if (done) return;
        done = true;
        const newVal = el.value.trim();
        if (!save || newVal === raw) {
          // Restore the original span; bind handlers again so this field
          // can be re-edited.
          el.replaceWith(original);
          active = null;
          bindEditableFields(root, c, ctx);
          return;
        }
        // Show a transient "Saving…" state then re-bind on resolution.
        el.disabled = true;
        const patch: Partial<Customer> = {};
        if (wzPath) {
          // Wizard_data field — coerce + build the nested patch.
          let val: unknown = newVal;
          if (wzType === "number") val = newVal === "" ? null : Number(newVal);
          else if (wzType === "array") val = newVal.split(",").map((s) => s.trim()).filter(Boolean);
          patch.wizardData = nestPath(wzPath, val) as Customer["wizardData"];
        } else {
          if (field === "name")     patch.name     = newVal;
          if (field === "phone")    patch.phone    = newVal;
          if (field === "email")    patch.email    = newVal;
          if (field === "address")  patch.address  = newVal;
          if (field === "stage")    patch.stage    = newVal;
          if (field === "project")  patch.project  = newVal;
          if (field === "source")   patch.source   = newVal;
          if (field === "jobStage") patch.jobStage = newVal;
          if (field === "budget")   patch.budget   = newVal;
          if (field === "secondaryName")  patch.secondaryName  = newVal;
          if (field === "secondaryEmail") patch.secondaryEmail = newVal;
          if (field === "secondaryPhone") patch.secondaryPhone = newVal;
        }
        await patchCustomer(root, ctx, c, patch);
        active = null;
        // rerenderHeader already rebinds handlers.
      };

      const onKey = (ev: Event): void => {
        const e = ev as KeyboardEvent;
        if (e.key === "Enter") { e.preventDefault(); void commit(true); }
        if (e.key === "Escape") { e.preventDefault(); void commit(false); }
      };
      el.addEventListener("keydown", onKey);
      el.addEventListener("blur", () => { void commit(true); });
      if (el instanceof HTMLSelectElement) {
        // Selects also commit on change (covers click-pick without blur).
        el.addEventListener("change", () => { void commit(true); });
      }
    };
    span.addEventListener("click", open);
    span.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); }
    });
  });
}

// ── Card scaffolds ─────────────────────────────────────────────────────────

function cardLoadingHtml(title: string, icon: string): string {
  return `
    <div class="cr-card-head">
      <div class="cr-card-title">${icon}${title}</div>
      <span class="cr-card-count">…</span>
    </div>
    <div class="cr-card-body" aria-busy="true">
      <div class="skeleton skeleton--line"></div>
      <div class="skeleton skeleton--line" style="width:82%"></div>
      <div class="skeleton skeleton--line" style="width:64%"></div>
    </div>
  `;
}

function cardHtml(opts: {
  title: string;
  icon: string;
  count: number | string;
  body: string;
  headExtra?: string;
}): string {
  const extra = opts.headExtra ?? "";
  return `
    <div class="cr-card-head">
      <div class="cr-card-title">${opts.icon}${opts.title}</div>
      <div style="display:flex;align-items:center;gap:8px">
        <span class="cr-card-count">${opts.count}</span>
        ${extra}
      </div>
    </div>
    <div class="cr-card-body">${opts.body}</div>
  `;
}

function emptyStateHtml(icon: string, text: string): string {
  return `<div class="cr-empty">${icon}${esc(text)}</div>`;
}

// ── 2. Messages ─────────────────────────────────────────────────────────────

interface CustomerMessageRow {
  id: string;
  text: string;
  isOut: boolean;
  ts: number;
  attachments?: MessageAttachment[];
  /** When the customer read this outbound message → "Seen" indicator. */
  readAt?: number | null;
}

/** Mutable per-card state shared between render + the compose handlers, so a
 *  re-render (optimistic send, live receive) never loses the queued
 *  attachments or the empty-state hint. */
interface MessageState {
  msgs: CustomerMessageRow[];
  pending: MessageAttachment[];
  emptyHint: string;
}

/** Read-only attachment chips shown under a message bubble (images render as
 *  thumbnails, other files as a labelled download link). */
function messageAttachmentsHtml(atts: MessageAttachment[] | undefined): string {
  if (!atts || atts.length === 0) return "";
  const chips = atts
    .map((a) =>
      a.kind === "image"
        ? `<a class="cr-msg-att cr-msg-att--img" href="${esc(a.url)}" target="_blank" rel="noopener" title="${esc(a.name)}"><img src="${esc(a.url)}" alt="${esc(a.name)}"></a>`
        : `<a class="cr-msg-att" href="${esc(a.url)}" target="_blank" rel="noopener">${SVG_DOC}<span>${esc(a.name)}</span></a>`,
    )
    .join("");
  return `<div class="cr-msg-atts">${chips}</div>`;
}

function renderMessagesCard(card: HTMLDivElement, state: MessageState): void {
  // Cap visible thread at the most recent 10 — this is a *mini* inbox; the
  // "Open full conversation" link below the compose box jumps to the full
  // messages page when the user wants the entire thread.
  const visible = state.msgs.slice(-10);
  const bubbles = visible.length === 0
    ? `<div class="cr-empty" style="padding:18px 0">${SVG_MSG}${esc(state.emptyHint)}</div>`
    : visible
        .map((m) => {
          const ts = new Date(m.ts).toLocaleString("en-GB", {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          });
          const textBubble = m.text
            ? `<div class="cr-msg-bubble ${m.isOut ? "outbound" : "inbound"}">${esc(m.text)}</div>`
            : "";
          const seen = (m.isOut && typeof m.readAt === "number" && m.readAt > 0)
            ? ` · <span class="cr-msg-seen" title="Customer read this message">Seen ${esc(fmtSeen(m.readAt))}</span>`
            : "";
          return `
            <div data-msg-id="${esc(m.id)}" style="display:flex;flex-direction:column;align-items:${m.isOut ? "flex-end" : "flex-start"}">
              ${textBubble}
              ${messageAttachmentsHtml(m.attachments)}
              <div class="cr-msg-meta" style="text-align:${m.isOut ? "right" : "left"}">${esc(ts)}${seen}</div>
            </div>
          `;
        })
        .join("");

  const tray = state.pending.map(renderAttachChipHtml).join("");

  const body = `
    <div class="cr-msg-thread" data-cr-msg-thread="1">${bubbles}</div>
    <div class="cr-msg-tray" data-cr-msg-tray="1"${state.pending.length ? "" : " hidden"}>${tray}</div>
    <div class="cr-msg-compose">
      <button class="cr-msg-attach" data-cr-msg-attach="1" type="button"
              title="Attach a file (JPG, PNG, WebP, PDF)" aria-label="Attach a file">${SVG_PAPERCLIP}</button>
      <input type="file" data-cr-msg-file="1" accept="${ATTACH_ACCEPT_ATTR}" multiple hidden>
      <textarea class="cr-msg-input" data-cr-msg-input="1" rows="1"
                placeholder="Send a message — Enter to send, Shift+Enter for newline"></textarea>
      <button class="cr-msg-send" data-cr-msg-send="1" type="button">
        ${SVG_SEND}<span>Send</span>
      </button>
    </div>
    <div class="cr-msg-foot">
      <a href="#/messages" class="cr-msg-link" data-cr-msg-open="1">Open full conversation</a>
    </div>
  `;

  card.innerHTML = cardHtml({
    title: "Messages",
    icon: SVG_MSG,
    count: state.msgs.length,
    body,
  });

  // Auto-scroll the thread to the newest message so the retailer always sees
  // the latest bubble without scrolling — fires on first load, after sending,
  // and on live receive (the realtime handler re-runs populateMessages, which
  // re-renders through here).
  const threadEl = card.querySelector<HTMLElement>("[data-cr-msg-thread]");
  if (threadEl) threadEl.scrollTop = threadEl.scrollHeight;
}

async function populateMessages(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  const card = root.querySelector<HTMLDivElement>('[data-section="messages"]');
  if (!card) return;

  let msgs: CustomerMessageRow[] = [];
  let backendMissing = false;

  // /api/portal/communications returns the message history; the customer
  // record always shows the compose box even when the thread is empty.
  try {
    // Messages live in the file-store keyed on the customer's REAL email. On
    // the retailer record `c.email` is MASKED (s••••@g•••••.com) for contact
    // privacy — sending that as customerEmail matches nothing and the server
    // returns []. So only forward the email when it's unmasked; otherwise omit
    // it and let the server resolve the real email from PG by customerId (uuid).
    const emailMasked = /[•·●]/.test(c.email ?? "");
    const emailQs = c.email && !emailMasked
      ? `&customerEmail=${encodeURIComponent(c.email)}`
      : "";
    const resp = await ctx.api<{ messages?: unknown[] }>(
      `/portal/communications?customerId=${encodeURIComponent(c.id)}${emailQs}`,
      { method: "GET", signal: ac.signal },
    );
    if (resp && Array.isArray(resp.messages)) {
      msgs = resp.messages
        .map((raw): CustomerMessageRow | null => {
          if (!raw || typeof raw !== "object") return null;
          const r = raw as Record<string, unknown>;
          const text = String(r["text"] ?? r["body"] ?? "").trim();
          const attachments = normaliseAttachments(r["attachments"]);
          if (!text && attachments.length === 0) return null;
          const tsRaw = r["ts"] ?? r["createdAt"] ?? r["created_at"] ?? null;
          const ts = toMs(tsRaw as number | string | null) ?? Date.now();
          const dirOrFlag = r["isOut"] ?? r["direction"] ?? r["from"] ?? "";
          const isOut = dirOrFlag === true
            || dirOrFlag === "me"
            || dirOrFlag === "out"
            || dirOrFlag === "outbound"
            || dirOrFlag === "retailer-to-customer"
            || dirOrFlag === "supplier-to-retailer"; // sent-by-retailer marker
          const id = String(r["id"] ?? `m-${ts}`);
          const readAtRaw = r["readAt"];
          const readAt = typeof readAtRaw === "number" && readAtRaw > 0 ? readAtRaw : null;
          return { id, text, isOut, ts, attachments, readAt };
        })
        .filter((m): m is CustomerMessageRow => m !== null)
        .sort((a, b) => a.ts - b.ts);
    }
  } catch (err) {
    if (isAbortError(err)) return;
    if (err instanceof ApiError && err.status === 404) {
      backendMissing = true;
    } else {
      console.error("[customer] messages load failed", err);
      backendMissing = true;
    }
  }

  const state: MessageState = {
    msgs,
    pending: [],
    emptyHint: backendMissing
      ? "No messages with this customer yet — send one below."
      : "No messages yet — send one below.",
  };

  renderMessagesCard(card, state);
  bindMessageHandlers(card, state, root, ctx, c);
}

// ── Kit conversation summary ────────────────────────────────────────────────
//
// Read-only running notes Kit keeps from the message thread. Sits directly
// below the Messages card. Auto-refreshes on send/receive (see renderEverything
// + the realtime handler). Each note links back to the message it came from.

interface KitSummaryEntry {
  point: string;
  category: string;
  messageId: string | null;
  ts: number | null;
  /** Who the note came from, derived from its source message ('retailer' | 'customer'). */
  source?: "retailer" | "customer" | null;
}

const KIT_SRC_LABEL: Record<string, string> = { retailer: "You", customer: "Customer" };

const KIT_CAT_LABEL: Record<string, string> = {
  decision: "Decision",
  change: "Change",
  budget: "Budget",
  action: "Action",
  question: "Question",
  other: "Note",
};

function kitSummaryEntryHtml(e: KitSummaryEntry): string {
  const cat = KIT_CAT_LABEL[e.category] ? e.category : "other";
  const label = KIT_CAT_LABEL[cat];
  const when = e.ts
    ? new Date(e.ts).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
    : "";
  const linkable = e.messageId ? ` data-kit-jump="${esc(e.messageId)}" role="button" tabindex="0"` : "";
  const src = e.source === "retailer" || e.source === "customer" ? e.source : "";
  const srcTag = src
    ? `<span class="cr-kit-src cr-kit-src--${src}" title="From ${src === "retailer" ? "you / your team" : "the customer"}">${KIT_SRC_LABEL[src]}</span>`
    : "";
  return `
    <li class="cr-kit-item${e.messageId ? " is-linkable" : ""}"${linkable}>
      <span class="cr-kit-cat cr-kit-cat--${cat}">${label}</span>
      <span class="cr-kit-point">${esc(e.point)}</span>
      ${srcTag}
      ${when ? `<span class="cr-kit-when">${esc(when)}</span>` : ""}
    </li>`;
}

function jumpToMessage(root: HTMLDivElement, ctx: PageContext, messageId: string): void {
  const msgCard = root.querySelector<HTMLElement>('[data-section="messages"]');
  const target = msgCard?.querySelector<HTMLElement>(`[data-msg-id="${cssEscape(messageId)}"]`);
  if (target) {
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    const bubble = target.querySelector<HTMLElement>(".cr-msg-bubble") ?? target;
    bubble.classList.add("cr-msg-flash");
    window.setTimeout(() => bubble.classList.remove("cr-msg-flash"), 1600);
  } else {
    // Older than the last-10 shown in the mini thread.
    msgCard?.scrollIntoView({ behavior: "smooth", block: "start" });
    ctx.showToast("That message is earlier in the thread — open the full conversation to see it.", "info");
  }
}

// Minimal CSS.escape fallback for attribute selectors (message ids are simple
// but may contain characters that need escaping in older engines).
function cssEscape(s: string): string {
  const ce = (globalThis as { CSS?: { escape?: (v: string) => string } }).CSS;
  if (ce && typeof ce.escape === "function") return ce.escape(s);
  return s.replace(/["\\\]]/g, "\\$&");
}

function renderKitSummaryCard(
  card: HTMLDivElement,
  root: HTMLDivElement,
  ctx: PageContext,
  data: { entries: KitSummaryEntry[]; generatedAt?: string | undefined; unavailable?: boolean | undefined },
): void {
  const entries = data.entries;
  let body: string;
  if (data.unavailable) {
    body = emptyStateHtml(SVG_KIT, "Kit's summary isn't available right now.");
  } else if (entries.length === 0) {
    body = emptyStateHtml(SVG_KIT, "Kit will summarise the key points here once you've exchanged a few messages.");
  } else {
    const updated = data.generatedAt
      ? new Date(data.generatedAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
      : "";
    body = `
      <div class="cr-kit-wrap">
        <ul class="cr-kit-list">${entries.map(kitSummaryEntryHtml).join("")}</ul>
        <span class="cr-kit-more">More below</span>
      </div>
      <div class="cr-kit-foot">${SVG_KIT}<span>Kept by Kit${updated ? ` · updated ${esc(updated)}` : ""}</span></div>`;
  }

  card.innerHTML = cardHtml({
    title: "Conversation Summary",
    icon: SVG_KIT,
    count: data.unavailable ? "" : entries.length,
    body,
  });

  // Flag the wrapper as scrollable (shows the fade + "more" cue) only when the
  // capped list actually overflows — keeps the page from growing unbounded.
  const listEl = card.querySelector<HTMLElement>(".cr-kit-list");
  const wrapEl = card.querySelector<HTMLElement>(".cr-kit-wrap");
  if (listEl && wrapEl && listEl.scrollHeight > listEl.clientHeight + 2) {
    wrapEl.classList.add("is-scrollable");
  }

  for (const el of Array.from(card.querySelectorAll<HTMLElement>("[data-kit-jump]"))) {
    const id = el.dataset["kitJump"];
    if (!id) continue;
    const go = (): void => jumpToMessage(root, ctx, id);
    el.addEventListener("click", go);
    el.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); }
    });
  }
}

async function populateKitSummary(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  const card = root.querySelector<HTMLDivElement>('[data-section="kit-summary"]');
  if (!card) return;
  try {
    // Mirror populateMessages: only forward the email when it's unmasked; the
    // server resolves the real email from PG by uuid otherwise.
    const emailMasked = /[•·●]/.test(c.email ?? "");
    const emailQs = c.email && !emailMasked ? `&customerEmail=${encodeURIComponent(c.email)}` : "";
    const resp = await ctx.api<{
      ok?: boolean;
      entries?: KitSummaryEntry[];
      generatedAt?: string;
      unavailable?: boolean;
    }>(
      `/portal/conversation-summary?customerId=${encodeURIComponent(c.id)}${emailQs}`,
      { method: "GET", signal: ac.signal },
    );
    if (ac.signal.aborted) return;
    const entries = Array.isArray(resp.entries) ? resp.entries : [];
    renderKitSummaryCard(card, root, ctx, {
      entries,
      generatedAt: resp.generatedAt,
      unavailable: resp.unavailable === true,
    });
  } catch (err) {
    if (isAbortError(err)) return;
    // Non-fatal — the rest of the record is unaffected.
    renderKitSummaryCard(card, root, ctx, { entries: [], unavailable: true });
  }
}

function bindMessageHandlers(
  card: HTMLDivElement,
  state: MessageState,
  root: HTMLDivElement,
  ctx: PageContext,
  c: Customer,
): void {
  const input = card.querySelector<HTMLTextAreaElement>('[data-cr-msg-input]');
  const sendBtn = card.querySelector<HTMLButtonElement>('[data-cr-msg-send]');
  const openLink = card.querySelector<HTMLAnchorElement>('[data-cr-msg-open]');
  const attachBtn = card.querySelector<HTMLButtonElement>('[data-cr-msg-attach]');
  const fileInput = card.querySelector<HTMLInputElement>('[data-cr-msg-file]');
  const tray = card.querySelector<HTMLDivElement>('[data-cr-msg-tray]');
  if (!input || !sendBtn) return;
  attachAutoGrow(input);

  const repaintTray = (): void => {
    if (!tray) return;
    tray.hidden = state.pending.length === 0;
    tray.innerHTML = state.pending.map(renderAttachChipHtml).join("");
  };

  openLink?.addEventListener("click", (e) => {
    e.preventDefault();
    navigate("messages", { contact: c.name });
  });

  // ── Attachments ────────────────────────────────────────────────────────
  attachBtn?.addEventListener("click", () => fileInput?.click());
  fileInput?.addEventListener("change", () => {
    const files = Array.from(fileInput.files ?? []);
    fileInput.value = "";
    if (files.length === 0) return;
    void (async () => {
      try {
        const uploaded = await uploadMessageAttachments(files);
        state.pending.push(...uploaded);
        repaintTray();
      } catch (err) {
        ctx.showToast(err instanceof Error ? err.message : "Upload failed.", "error");
      }
    })();
  });
  tray?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>('[data-role="attach-remove"]');
    const id = btn?.dataset["attachId"];
    if (!id) return;
    const idx = state.pending.findIndex((a) => a.id === id);
    if (idx >= 0) state.pending.splice(idx, 1);
    repaintTray();
  });

  const send = async (): Promise<void> => {
    const text = input.value.trim();
    const outgoing = [...state.pending];
    // Allow an attachment-only message (no text) once files are queued.
    if (!text && outgoing.length === 0) return;
    sendBtn.disabled = true;
    input.disabled = true;
    const optimisticTs = Date.now();
    state.msgs.push({ id: `tmp-${optimisticTs}`, text, isOut: true, ts: optimisticTs, attachments: outgoing });
    state.pending = [];
    renderMessagesCard(card, state);
    bindMessageHandlers(card, state, root, ctx, c);
    try {
      const ownRetailerId =
        (typeof localStorage !== "undefined" ? localStorage.getItem("kos_retailer_id") : null) ??
        ctx.user?.retailerId ?? "";
      const retailerName = ctx.user?.retailerName ?? "Retailer";
      await ctx.api<{ ok: boolean }>("/portal/customer-message", {
        method: "POST",
        body: JSON.stringify({
          retailerId: ownRetailerId,
          retailerName,
          customerId: c.id,
          customerEmail: c.email,
          customerName: c.name,
          subject: `Message from ${retailerName}`,
          body: text,
          attachmentIds: outgoing.map((a) => a.id),
        }),
      });
      ctx.showToast(outgoing.length ? "Message & files sent" : "Message sent", "success");
      // Let Kit re-read the thread so its running notes pick up what we just sent.
      void populateKitSummary(root, ctx, new AbortController(), c);
    } catch (err) {
      if (isAbortError(err)) return;
      // Roll back the optimistic bubble and restore the queued attachments.
      const idx = state.msgs.findIndex((m) => m.id === `tmp-${optimisticTs}`);
      if (idx >= 0) state.msgs.splice(idx, 1);
      state.pending = outgoing;
      renderMessagesCard(card, state);
      bindMessageHandlers(card, state, root, ctx, c);
      ctx.showToast("Could not send message", "error");
    }
  };

  sendBtn.addEventListener("click", () => { void send(); });
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  });
}

// ── 3. Quotes ───────────────────────────────────────────────────────────────

/** Wire the "New quote" button on the Quotes & Orders card head.
 *
 *  Same destination as the header's New Quote action, so the retailer can start
 *  a quote from the section they are already looking at instead of going up to
 *  the header. fresh=1 is what guarantees a BLANK quote — without it the Quote
 *  Builder rehydrates the last working copy from localStorage. */
function wireNewQuoteButton(card: HTMLDivElement, ctx: PageContext, c: Customer): void {
  card.querySelector<HTMLButtonElement>('[data-cr-new-quote="1"]')?.addEventListener("click", () => {
    ctx.navigate("quote-builder", { customer: c.name, customerId: c.id, fresh: "1" });
  });
}

async function populateQuotes(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  const card = root.querySelector<HTMLDivElement>('[data-section="quotes"]');
  if (!card) return;

  interface WireQuote {
    id: string;
    reference: string;
    projectName: string;
    status: string;
    totalIncVat: number;
    sentAt: number;
    createdAt: number;
    /** First time the customer opened this quote in their portal — null
     *  until then. Drives the "Seen" indicator. */
    openedAt?: number | null;
  }

  let quotes: WireQuote[] = [];
  try {
    // Pass customerName as a fallback match — many drafts in the wild
    // were saved without a customer_id FK on the quotes row but do have
    // customer_name populated. The backend ORs the two conditions so
    // unlinked drafts still appear on the record page.
    const qs = new URLSearchParams({ customerId: c.id });
    if (c.name) qs.set("customerName", c.name);
    const resp = await ctx.api<{ quotes?: WireQuote[] }>(
      `/portal/quotes?${qs.toString()}`,
      { method: "GET", signal: ac.signal },
    );
    if (resp && Array.isArray(resp.quotes)) quotes = resp.quotes;
  } catch (err) {
    if (isAbortError(err)) return;
    if (!(err instanceof ApiError && err.status === 404)) {
      console.error("[customer] quotes load failed", err);
    }
  }

  if (quotes.length === 0) {
    card.innerHTML = cardHtml({
      title: "Quotes &amp; Orders",
      icon: SVG_DOC,
      count: 0,
      body: emptyStateHtml(SVG_DOC, "No quotes recorded for this customer yet."),
      headExtra: `<button class="cr-action-btn primary" data-cr-new-quote="1" type="button" style="padding:5px 10px;font-size:11px">${SVG_PLUS}New quote</button>`,
    });
    wireNewQuoteButton(card, ctx, c);
    return;
  }

  // Approved quotes act as "orders" in modular today (no separate orders
  // table). Approved/won status → navigate to the operations page with
  // a `focus` hint; every other status (draft / sent / rejected /
  // expired) re-opens the quote in the builder so the retailer can keep
  // working on it.
  const isOrderStatus = (s: string): boolean =>
    s === "approved" || s === "won";

  // Friendly badge labels + a colour-per-status so the retailer can
  // tell at a glance where each quote sits in the pipeline. Anything
  // unrecognised falls through to a neutral grey label.
  const statusMeta = (raw: string): { label: string; color: string } => {
    switch (raw) {
      case "draft":    return { label: "Draft",    color: "#94a3b8" }; // slate
      case "sent":     return { label: "Sent",     color: "#e58200" }; // amber
      case "approved": return { label: "Approved", color: "#22c55e" }; // green
      case "won":      return { label: "Won",      color: "#22c55e" };
      case "rejected": return { label: "Declined", color: "#ef4444" }; // red
      case "lost":     return { label: "Lost",     color: "#ef4444" };
      case "expired":  return { label: "Expired",  color: "#a16207" }; // muted
      case "ordered":  return { label: "Ordered",  color: "#22c55e" };
      default:         return { label: raw || "—", color: "#6b7280" };
    }
  };

  const rows = quotes
    .map((q) => {
      const meta = statusMeta(q.status);
      const kind = isOrderStatus(q.status) ? "order" : "quote";
      return `
        <div class="cr-del-row cr-row-clickable"
             data-cr-row-kind="${kind}"
             data-cr-quote-id="${esc(q.id)}"
             data-cr-quote-ref="${esc(q.reference)}"
             role="button" tabindex="0">
          <div class="cr-del-dot" style="background:${meta.color}"></div>
          <div class="cr-del-info">
            <div class="cr-del-name">${esc(q.reference)}${q.projectName ? ` · ${esc(q.projectName)}` : ""}</div>
            <div class="cr-del-sub">${esc(fmtMoney(q.totalIncVat))} · ${esc(fmtDate(q.createdAt))}${
              typeof q.openedAt === "number" && q.openedAt > 0
                ? ` · <span class="cr-quote-seen" title="Customer opened this quote">Opened ${esc(DATETIME_FMT.format(new Date(q.openedAt)))}</span>`
                : ""
            }</div>
          </div>
          <span class="cr-del-status" style="background:${meta.color}22;color:${meta.color}">${esc(meta.label)}</span>
          ${isOrderStatus(q.status)
            ? `<button type="button" class="cr-action-btn" data-cr-unapprove="${esc(q.reference)}" style="padding:4px 9px;font-size:11px;margin-left:6px">Unapprove</button>`
            : ""}
          <span class="cr-row-chevron" aria-hidden="true">${SVG_CHEVR}</span>
        </div>
      `;
    })
    .join("");

  card.innerHTML = cardHtml({
    title: "Quotes &amp; Orders",
    icon: SVG_DOC,
    count: quotes.length,
    body: rows,
    headExtra: `<button class="cr-action-btn primary" data-cr-new-quote="1" type="button" style="padding:5px 10px;font-size:11px">${SVG_PLUS}New quote</button>`,
  });
  wireNewQuoteButton(card, ctx, c);

  // Wire up navigation. EVERY row — quotes and approved "orders" alike —
  // opens the saved quote in the quote-builder (it reads the quote id / ref
  // from the hash on mount). Orders previously routed to the operations page,
  // which was the wrong destination: clicking a quote should open that quote.
  card.querySelectorAll<HTMLElement>("[data-cr-row-kind]").forEach((row) => {
    const trigger = (): void => {
      const ref = row.dataset["crQuoteRef"] ?? "";
      const quoteId = row.dataset["crQuoteId"] ?? "";
      // Pass the PG quote id so the builder loads the full saved quote from the
      // database (BUG 1 & 2 — duplicated/sent quotes opened from the record
      // were coming up empty because only the reference was passed). `quote`
      // (reference) kept as a fallback for older builds.
      navigate("quote-builder", { quoteId, quote: ref, customer: c.name, customerId: c.id });
    };
    row.addEventListener("click", trigger);
    row.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); trigger(); }
    });
  });

  // Unapprove — returns an approved quote to "Sent" (before payment). Stops
  // propagation so it doesn't open the row's order/quote view.
  card.querySelectorAll<HTMLButtonElement>("[data-cr-unapprove]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const ref = btn.dataset["crUnapprove"] ?? "";
      void (async () => {
        const ok = await confirmDialog(
          `Unapprove quote ${ref}? It returns to "Sent" so you can make changes, and the customer is notified. Only possible before a payment has been made.`,
          { title: "Unapprove quote", confirmLabel: "Unapprove", destructive: true },
        );
        if (!ok) return;
        btn.disabled = true;
        try {
          await ctx.api(`/portal/quotes/${encodeURIComponent(ref)}/unapprove`, { method: "POST" });
          ctx.showToast("Quote unapproved — returned to Sent", "success");
          void populateQuotes(root, ctx, ac, c);
        } catch (err) {
          const msg = err instanceof ApiError && err.status === 409
            ? "Can't unapprove — a payment has already been made on this quote."
            : "Could not unapprove the quote — please try again.";
          ctx.showToast(msg, "error");
          btn.disabled = false;
        }
      })();
    });
  });
}

// ── 4. Plans & 3D ───────────────────────────────────────────────────────────

/** The three design-asset categories shown on the Plans card (the paperwork
 *  bucket 'Other' lives in the separate Documents card). Backed by the
 *  migration-0016 design_files.kind column. */
type PlanKind = "Plan" | "Elevation" | "3D Image";

interface CustomerFile {
  id: string;
  name: string;
  type: string;
  sizeBytes: number;
  url: string;
  visibility: "hidden" | "shared";
  uploadedAt: number;
  /** Category — drives which labelled sub-section the file appears under. */
  kind: PlanKind;
  /** When the customer first opened it in their portal (null = not yet). */
  firstViewedAt?: number | null;
}

/** Ordered, labelled sub-sections for the Plans card. */
const PLAN_SECTIONS: ReadonlyArray<{ kind: PlanKind; label: string }> = [
  { kind: "Plan", label: "Plans" },
  { kind: "Elevation", label: "Elevations" },
  { kind: "3D Image", label: "3D Images" },
];

/** Best-effort auto-classify an upload by filename (mirrors the Quote Builder's
 *  guessKindFromName). Unknown → Plan, since this card is only for design
 *  assets — paperwork belongs in the Documents card. */
function guessPlanKind(name: string): PlanKind {
  const n = name.toLowerCase();
  if (/3d|render|persp/.test(n)) return "3D Image";
  if (/elev/.test(n)) return "Elevation";
  return "Plan";
}

/** Coerce any server/select value to a valid Plan-card kind (fallback Plan). */
function coercePlanKind(v: unknown): PlanKind {
  return v === "Elevation" || v === "3D Image" ? v : "Plan";
}

function fmtSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

// ── AI layout status chip ────────────────────────────────────────────────
// Shows the state of the customer's AI cabinet layout on the Plans card header.
// Data comes from GET /api/portal/customers/:id/layout-status. The status is stashed on the
// card element's dataset so it survives the card's innerHTML re-renders (upload /
// delete) without threading it through every render call.
function layoutChipHtml(status: string): string {
  const map: Record<string, { label: string; bg: string; fg: string; icon: string }> = {
    ready:        { label: "Layout ready",        bg: "rgba(16,185,129,0.14)", fg: "#10B981", icon: iconMarkup("check", { size: "sm" }) },
    generating:   { label: "Layout generating…",  bg: "rgba(59,130,246,0.14)", fg: "#3B82F6", icon: iconMarkup("pulse-dot", { size: "sm" }) },
    needs_review: { label: "Needs a quick check", bg: "rgba(245,158,11,0.16)", fg: "#F59E0B", icon: iconMarkup("alert-triangle", { size: "sm" }) },
  };
  const s = map[status];
  if (!s) return ""; // 'none' / unknown → no chip
  return `<span class="cr-layout-chip" title="AI cabinet layout status" style="display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px;background:${s.bg};color:${s.fg};white-space:nowrap">${s.icon}<span>${esc(s.label)}</span></span>`;
}

function applyLayoutChip(card: HTMLDivElement): void {
  const chip = layoutChipHtml(card.dataset.layoutStatus || "");
  const countEl = card.querySelector(".cr-card-count");
  if (!countEl) return;
  countEl.parentElement?.querySelector(".cr-layout-chip")?.remove(); // avoid dupes on re-render
  if (chip) countEl.insertAdjacentHTML("afterend", chip);
}

async function loadLayoutStatus(
  card: HTMLDivElement,
  ctx: PageContext,
  c: Customer,
  ac: AbortController,
): Promise<void> {
  try {
    const r = await ctx.api<{ status?: string }>(
      `/portal/customers/${encodeURIComponent(c.id)}/layout-status`,
      { method: "GET", signal: ac.signal },
    );
    card.dataset.layoutStatus = (r && r.status) || "none";
    applyLayoutChip(card);
  } catch (err) {
    if (isAbortError(err)) return;
    // Non-fatal — the chip simply doesn't show.
  }
}

function renderPlansCard(
  card: HTMLDivElement,
  files: CustomerFile[],
): void {
  const dropZone = `
    <label class="cr-plans-drop" data-cr-files-drop="1">
      ${SVG_UPLOAD}
      <strong>Click to upload</strong> or drag and drop here<br>
      <span style="font-size:11px;opacity:.7">Images, PDFs, DWG, SVG · max 20 MB each</span>
      <input type="file" data-cr-files-input="1"
             accept="${FILE_ACCEPT}" multiple style="display:none">
    </label>
  `;

  const renderRow = (f: CustomerFile): string => {
    const isShared = f.visibility === "shared";
    const privacyLabel = isShared ? "Shared" : "Private";
    const privacyIcon = isShared ? SVG_EYE : SVG_EYE_OFF;
    const privacyClass = isShared ? "shared" : "private";
    // Subtle "Viewed" indicator — only once the customer has opened it.
    const viewedTag = (typeof f.firstViewedAt === "number" && f.firstViewedAt > 0)
      ? `<span class="cr-file-viewed" title="Customer opened this in their portal">${SVG_EYE}<span>Viewed ${esc(fmtSeen(f.firstViewedAt))}</span></span>`
      : "";
    // Re-categorise selector — move a file between Plans / Elevations / 3D.
    const kindOptions = PLAN_SECTIONS.map((s) =>
      `<option value="${esc(s.kind)}"${s.kind === f.kind ? " selected" : ""}>${esc(s.kind)}</option>`,
    ).join("");
    return `
      <div class="cr-file-row" data-cr-file-id="${esc(f.id)}">
        <div class="cr-plan-icon">${SVG_DOC}</div>
        <div class="cr-file-info">
          <a class="cr-file-name" href="${esc(f.url)}" target="_blank" rel="noopener">${esc(f.name)}</a>
          <div class="cr-file-sub">
            <span>${esc(fmtSize(f.sizeBytes))} · ${esc(fmtDate(f.uploadedAt))}</span>
            <span class="cr-file-privacy ${privacyClass}">
              ${privacyIcon}<span>${esc(privacyLabel)}</span>
            </span>
            ${viewedTag}
          </div>
        </div>
        <select class="cr-file-kind" data-cr-file-kind="${esc(f.id)}" title="Change category">
          ${kindOptions}
        </select>
        <button class="cr-file-btn"
                data-cr-file-toggle="${esc(f.id)}"
                type="button"
                title="${isShared ? "Make private" : "Share with customer"}">
          ${isShared ? SVG_EYE_OFF : SVG_EYE}
        </button>
        <button class="cr-file-btn danger"
                data-cr-file-del="${esc(f.id)}"
                type="button" aria-label="Remove file">
          ${SVG_TRASH}
        </button>
      </div>
    `;
  };

  // Group files into labelled sub-sections so retailers can tell plans,
  // elevations and 3D renders apart at a glance. Empty sections are hidden.
  const body = files.length === 0
    ? `${dropZone}<div class="cr-empty" style="padding:12px 0">${SVG_PLAN}No plans or 3D renders uploaded yet.</div>`
    : `${dropZone}${PLAN_SECTIONS.map((sec) => {
        const group = files.filter((f) => f.kind === sec.kind);
        if (group.length === 0) return "";
        return `
          <div class="cr-plan-group">
            <div class="cr-plan-group__head">
              <span class="cr-plan-group__label">${esc(sec.label)}</span>
              <span class="cr-plan-group__count">${group.length}</span>
            </div>
            <div class="cr-file-list" data-cr-file-list="1">${group.map(renderRow).join("")}</div>
          </div>`;
      }).join("")}`;

  card.innerHTML = cardHtml({
    title: "Plans &amp; 3D Renders",
    icon: SVG_PLAN,
    count: files.length,
    body,
  });
  applyLayoutChip(card); // re-apply the layout-status chip from the persisted dataset
}

// Plans card shows CAD / design / rendering kinds. Documents shows the
// 'Other' bucket (paperwork). Backend separates by the migration-0016
// `kind` column.
const PLAN_KINDS = "Plan,Elevation,3D Image";

async function populatePlans(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  const card = root.querySelector<HTMLDivElement>('[data-section="plans"]');
  if (!card) return;
  // Reads from /portal/documents (returns design_files for the customer);
  // uploads/share-toggle/delete go through /portal/customer-files (also
  // backed by design_files so the list stays in sync).

  let files: CustomerFile[] = [];
  try {
    interface WireDoc {
      id: string; name: string; type: string;
      sizeBytes: number; url: string;
      visibility?: string; uploadedAt: number;
      kind?: string;
      firstViewedAt?: number | null;
    }
    const resp = await ctx.api<{ documents?: WireDoc[] }>(
      `/portal/documents?customerId=${encodeURIComponent(c.id)}&kind=${encodeURIComponent(PLAN_KINDS)}`,
      { method: "GET", signal: ac.signal },
    );
    if (resp && Array.isArray(resp.documents)) {
      files = resp.documents.map((d) => ({
        id: d.id,
        name: d.name,
        type: d.type,
        sizeBytes: d.sizeBytes,
        url: d.url,
        visibility: d.visibility === "shared" ? "shared" : "hidden",
        kind: coercePlanKind(d.kind),
        uploadedAt: d.uploadedAt,
        firstViewedAt: d.firstViewedAt ?? null,
      }));
    }
  } catch (err) {
    if (isAbortError(err)) return;
    if (!(err instanceof ApiError && err.status === 404)) {
      console.error("[customer] documents load failed", err);
    }
  }

  renderPlansCard(card, files);
  bindPlansHandlers(card, files, ctx, c);
  void loadLayoutStatus(card, ctx, c, ac); // fetch + show the AI-layout status chip
}

function bindPlansHandlers(
  card: HTMLDivElement,
  files: CustomerFile[],
  ctx: PageContext,
  c: Customer,
): void {
  const drop = card.querySelector<HTMLLabelElement>('[data-cr-files-drop]');
  const fileInput = card.querySelector<HTMLInputElement>('[data-cr-files-input]');

  const upload = async (list: FileList | File[]): Promise<void> => {
    const arr = Array.from(list);
    for (const f of arr) {
      if (f.size > FILE_MAX_BYTES) {
        ctx.showToast(`${f.name} is over the 20 MB limit`, "error");
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      // Auto-categorise by filename so the file lands in the right sub-section;
      // the retailer can re-assign it afterwards with the per-file selector.
      const guessedKind = guessPlanKind(f.name);
      try {
        const res = await fetch(
          `/api/portal/customer-files?customerId=${encodeURIComponent(c.id)}&visibility=hidden&kind=${encodeURIComponent(guessedKind)}`,
          { method: "POST", credentials: "include", body: fd },
        );
        const data = (await res.json().catch(() => ({}))) as
          { ok?: boolean; file?: CustomerFile; error?: string };
        if (!res.ok || !data.ok || !data.file) {
          throw new Error(data.error || `HTTP ${res.status}`);
        }
        files.unshift({ ...data.file, kind: coercePlanKind(data.file.kind) });
        ctx.showToast(`Uploaded ${f.name}`, "success");
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed";
        ctx.showToast(`${f.name}: ${msg}`, "error");
      }
    }
    renderPlansCard(card, files);
    bindPlansHandlers(card, files, ctx, c);
  };

  if (drop) {
    // Drag and drop wiring — the label already triggers the file input on
    // click (because the input is inside the label), so we only handle
    // drag events here.
    const stop = (e: DragEvent): void => { e.preventDefault(); e.stopPropagation(); };
    ["dragenter", "dragover"].forEach((evt) => {
      drop.addEventListener(evt as keyof HTMLElementEventMap, (e) => {
        stop(e as DragEvent);
        drop.classList.add("is-dragover");
      });
    });
    ["dragleave", "dragend"].forEach((evt) => {
      drop.addEventListener(evt as keyof HTMLElementEventMap, (e) => {
        stop(e as DragEvent);
        drop.classList.remove("is-dragover");
      });
    });
    drop.addEventListener("drop", (e) => {
      stop(e);
      drop.classList.remove("is-dragover");
      const list = e.dataTransfer?.files;
      if (list && list.length) void upload(list);
    });
  }

  fileInput?.addEventListener("change", () => {
    if (fileInput.files && fileInput.files.length) {
      void upload(fileInput.files);
      fileInput.value = ""; // Allow re-picking the same file later.
    }
  });

  // Image plans / 3D renders open in the in-portal lightbox (prev/next cycles
  // the image files in this card). Non-images (PDF, DWG…) keep their
  // open-in-new-tab anchor behaviour.
  const planImages = files.filter((f) => isImageUrl(f.url) || isImageUrl(f.name));
  if (planImages.length) {
    const lb: LightboxImage[] = planImages.map((f) => ({ url: f.url, name: f.name }));
    card.querySelectorAll<HTMLDivElement>(".cr-file-row").forEach((row) => {
      const id = row.dataset["crFileId"];
      const idx = planImages.findIndex((f) => f.id === id);
      if (idx < 0) return;
      row.querySelector<HTMLAnchorElement>(".cr-file-name")?.addEventListener("click", (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        openLightbox({ images: lb, startIndex: idx });
      });
    });
  }

  // Per-file privacy toggle.
  card.querySelectorAll<HTMLButtonElement>("[data-cr-file-toggle]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset["crFileToggle"];
      if (!id) return;
      const f = files.find((x) => x.id === id);
      if (!f) return;
      const next: "hidden" | "shared" = f.visibility === "shared" ? "hidden" : "shared";
      void (async () => {
        const prev = f.visibility;
        f.visibility = next; // Optimistic.
        renderPlansCard(card, files);
        bindPlansHandlers(card, files, ctx, c);
        try {
          await ctx.api<{ ok: boolean }>(
            `/portal/customer-files/${encodeURIComponent(id)}`,
            {
              method: "PATCH",
              body: JSON.stringify({ visibility: next }),
            },
          );
          ctx.showToast(
            next === "shared" ? "File shared with customer" : "File set to private",
            "success",
          );
        } catch (err) {
          f.visibility = prev;
          renderPlansCard(card, files);
          bindPlansHandlers(card, files, ctx, c);
          const msg = err instanceof ApiError && err.status === 503
            ? "Database unavailable"
            : "Could not change privacy";
          ctx.showToast(msg, "error");
        }
      })();
    });
  });

  // Per-file re-categorise — move a file between Plans / Elevations / 3D.
  card.querySelectorAll<HTMLSelectElement>("[data-cr-file-kind]").forEach((sel) => {
    sel.addEventListener("change", () => {
      const id = sel.dataset["crFileKind"];
      if (!id) return;
      const f = files.find((x) => x.id === id);
      if (!f) return;
      const nextKind = coercePlanKind(sel.value);
      const prev = f.kind;
      if (nextKind === prev) return;
      f.kind = nextKind; // Optimistic — re-render moves it to the new section.
      renderPlansCard(card, files);
      bindPlansHandlers(card, files, ctx, c);
      void (async () => {
        try {
          await ctx.api<{ ok: boolean }>(
            `/portal/customer-files/${encodeURIComponent(id)}`,
            { method: "PATCH", body: JSON.stringify({ kind: nextKind }) },
          );
          const label = PLAN_SECTIONS.find((s) => s.kind === nextKind)?.label ?? nextKind;
          ctx.showToast(`Moved to ${label}`, "success");
        } catch (err) {
          f.kind = prev;
          renderPlansCard(card, files);
          bindPlansHandlers(card, files, ctx, c);
          const msg = err instanceof ApiError && err.status === 503
            ? "Database unavailable"
            : "Could not change category";
          ctx.showToast(msg, "error");
        }
      })();
    });
  });

  // Per-file delete.
  card.querySelectorAll<HTMLButtonElement>("[data-cr-file-del]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset["crFileDel"];
      if (!id) return;
      const f = files.find((x) => x.id === id);
      if (!f) return;
      void (async () => {
        if (!(await confirmDialog(`Remove "${f.name}"? This can't be undone.`, {
          title: "Remove file",
          confirmLabel: "Remove",
          destructive: true,
        }))) return;
        const idx = files.findIndex((x) => x.id === id);
        const snapshot = idx >= 0 ? files[idx] : null;
        if (idx >= 0) files.splice(idx, 1);
        renderPlansCard(card, files);
        bindPlansHandlers(card, files, ctx, c);
        try {
          await ctx.api<{ ok: boolean }>(
            `/portal/customer-files/${encodeURIComponent(id)}`,
            { method: "DELETE" },
          );
          ctx.showToast("File removed", "success");
        } catch (err) {
          if (snapshot) {
            files.splice(Math.min(idx, files.length), 0, snapshot);
            renderPlansCard(card, files);
            bindPlansHandlers(card, files, ctx, c);
          }
          const msg = err instanceof ApiError && err.status === 404
            ? "File already removed"
            : "Could not remove file";
          ctx.showToast(msg, "error");
        }
      })();
    });
  });
}

// ── 4b. Before & After ──────────────────────────────────────────────────────
//
// Photographs live on the JOB RECORD (design_files kinds Before/After,
// migration 0086) — the record is the single source of truth, so the v1.5
// blog-post generator and social auto-post read the same store rather than
// each feature owning a copy.
//
// A "pair" is a selection over two of those photos plus the customer's
// permission. Kitchengine never publishes anything: an approved pair is
// EXPORTED for the retailer to use on their own site and socials. There is no
// public link here by design — see the no-public-hosting rule.

interface JobPhoto {
  id: string;
  kind: "Before" | "After";
  url: string;
  sizeBytes: number;
}

interface PhotoPair {
  id: string;
  title: string;
  beforeFileId: string;
  afterFileId: string;
  beforeUrl: string;
  afterUrl: string;
  isApproved: boolean;
  consentConfirmed: boolean;
}

/** Mirrors the server's fileFilter — raster only. SVG is refused there because
 *  it can carry script; keeping the picker in step avoids a pointless round
 *  trip that ends in an error toast. */
const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif";
const PHOTO_MAX_BYTES = 12 * 1024 * 1024;

/** Which two photos the retailer has picked, held across re-renders of the
 *  card so selecting a second photo doesn't discard the first. */
interface PairPick { before: string | null; after: string | null }

function renderBeforeAfterCard(
  card: HTMLDivElement,
  photos: JobPhoto[],
  pairs: PhotoPair[],
  pick: PairPick,
): void {
  const dropZone = (kind: "Before" | "After"): string => `
    <label class="cr-ba-drop" data-cr-ba-drop="${kind}">
      ${SVG_UPLOAD}<span>Add ${kind.toLowerCase()} photo</span>
      <input type="file" data-cr-ba-input="${kind}" accept="${PHOTO_ACCEPT}" multiple style="display:none">
    </label>`;

  const thumb = (p: JobPhoto): string => {
    const selected = (p.kind === "Before" ? pick.before : pick.after) === p.id;
    return `
      <div class="cr-ba-thumb${selected ? " is-picked" : ""}" data-cr-ba-pick="${esc(p.id)}" data-cr-ba-kind="${p.kind}">
        <img src="${esc(p.url)}" alt="${p.kind} photo" loading="lazy">
        <span class="cr-ba-tick">${SVG_CHECK}</span>
        <button class="cr-ba-del" type="button" data-cr-ba-del="${esc(p.id)}" aria-label="Remove photo">${SVG_TRASH}</button>
      </div>`;
  };

  const column = (kind: "Before" | "After"): string => {
    const group = photos.filter((p) => p.kind === kind);
    return `
      <div class="cr-ba-col">
        <div class="cr-ba-col__head"><span>${kind}</span><span class="cr-ba-col__n">${group.length}</span></div>
        <div class="cr-ba-grid">${group.map(thumb).join("")}</div>
        ${dropZone(kind)}
      </div>`;
  };

  const ready = !!(pick.before && pick.after);
  const pairBar = `
    <div class="cr-ba-bar${ready ? " is-ready" : ""}">
      <span class="cr-ba-bar__hint">${ready
        ? "Two photos selected."
        : "Select one before and one after photo to pair them."}</span>
      <input class="cr-ba-title" data-cr-ba-title="1" type="text" maxlength="160"
             placeholder="Title (optional) — e.g. Sheffield galley rebuild"${ready ? "" : " disabled"}>
      <button class="cr-action-btn sm" type="button" data-cr-ba-create="1"${ready ? "" : " disabled"}>Create pair</button>
    </div>`;

  const pairRow = (p: PhotoPair): string => `
    <div class="cr-ba-pair${p.isApproved ? " is-approved" : ""}" data-cr-ba-pair="${esc(p.id)}">
      <div class="cr-ba-pair__shots">
        <div class="cr-ba-shot"><img src="${esc(p.beforeUrl)}" alt="Before" loading="lazy"><span>Before</span></div>
        <div class="cr-ba-shot"><img src="${esc(p.afterUrl)}" alt="After" loading="lazy"><span>After</span></div>
      </div>
      <div class="cr-ba-pair__meta">
        <div class="cr-ba-pair__title">${esc(p.title || "Untitled pair")}</div>
        <label class="cr-ba-consent">
          <input type="checkbox" data-cr-ba-consent="${esc(p.id)}"${p.consentConfirmed ? " checked" : ""}>
          <span>I have the customer&rsquo;s permission to use these photos</span>
        </label>
        <div class="cr-ba-pair__actions">
          <button class="cr-action-btn sm${p.isApproved ? " is-on" : ""}" type="button"
                  data-cr-ba-approve="${esc(p.id)}"${p.consentConfirmed ? "" : " disabled"}
                  title="${p.consentConfirmed ? "" : "Confirm permission first"}">
            ${p.isApproved ? `${SVG_CHECK}Approved` : "Approve for use"}
          </button>
          <button class="cr-action-btn sm" type="button" data-cr-ba-dl="${esc(p.id)}" data-side="before"${p.isApproved ? "" : " disabled"}>Before</button>
          <button class="cr-action-btn sm" type="button" data-cr-ba-dl="${esc(p.id)}" data-side="after"${p.isApproved ? "" : " disabled"}>After</button>
          <button class="cr-file-btn danger" type="button" data-cr-ba-pairdel="${esc(p.id)}" aria-label="Delete pair">${SVG_TRASH}</button>
        </div>
      </div>
    </div>`;

  const pairsHtml = pairs.length
    ? `<div class="cr-ba-pairs">${pairs.map(pairRow).join("")}</div>`
    : `<div class="cr-ba-none">No pairs yet. Approved pairs can be downloaded for your own website and social posts.</div>`;

  card.innerHTML = cardHtml({
    title: "Before &amp; After",
    icon: SVG_IMAGE,
    count: pairs.length,
    body: `
      <div class="cr-ba-cols">${column("Before")}${column("After")}</div>
      ${pairBar}
      ${pairsHtml}
    `,
  });
}

async function populateBeforeAfter(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  const card = root.querySelector<HTMLDivElement>('[data-section="before-after"]');
  if (!card) return;

  let photos: JobPhoto[] = [];
  let pairs: PhotoPair[] = [];
  try {
    const [pr, gr] = await Promise.all([
      ctx.api<{ photos?: JobPhoto[] }>(
        `/portal/customer-photos?customerId=${encodeURIComponent(c.id)}`,
        { method: "GET", signal: ac.signal },
      ),
      ctx.api<{ pairs?: PhotoPair[] }>(
        `/portal/gallery?customerId=${encodeURIComponent(c.id)}`,
        { method: "GET", signal: ac.signal },
      ),
    ]);
    if (pr && Array.isArray(pr.photos)) photos = pr.photos;
    if (gr && Array.isArray(gr.pairs)) pairs = gr.pairs;
  } catch (err) {
    if (isAbortError(err)) return;
    if (!(err instanceof ApiError && err.status === 404)) {
      console.error("[customer] before/after load failed", err);
    }
  }

  const pick: PairPick = { before: null, after: null };
  renderBeforeAfterCard(card, photos, pairs, pick);
  bindBeforeAfterHandlers(card, photos, pairs, pick, ctx, c);
}

function bindBeforeAfterHandlers(
  card: HTMLDivElement,
  photos: JobPhoto[],
  pairs: PhotoPair[],
  pick: PairPick,
  ctx: PageContext,
  c: Customer,
): void {
  const redraw = (): void => {
    renderBeforeAfterCard(card, photos, pairs, pick);
    bindBeforeAfterHandlers(card, photos, pairs, pick, ctx, c);
  };

  // ── uploads ──────────────────────────────────────────────────────────
  card.querySelectorAll<HTMLInputElement>("[data-cr-ba-input]").forEach((input) => {
    input.addEventListener("change", () => {
      const kind = input.dataset.crBaInput === "After" ? "After" : "Before";
      const files = input.files ? Array.from(input.files) : [];
      input.value = "";
      void (async (): Promise<void> => {
        for (const f of files) {
          if (f.size > PHOTO_MAX_BYTES) {
            ctx.showToast(`${f.name} is over the 12 MB limit`, "error");
            continue;
          }
          const fd = new FormData();
          fd.append("file", f);
          fd.append("customerId", c.id);
          fd.append("kind", kind);
          try {
            // Raw fetch, as the Plans card does: the typed api client doesn't
            // carry multipart bodies.
            const res = await fetch("/api/portal/customer-photos", {
              method: "POST", credentials: "include", body: fd,
            });
            const data = (await res.json().catch(() => ({}))) as
              { ok?: boolean; photo?: JobPhoto; error?: string };
            if (!res.ok || !data.ok || !data.photo) throw new Error(data.error || `HTTP ${res.status}`);
            photos.unshift(data.photo);
          } catch (err) {
            ctx.showToast(err instanceof Error ? err.message : "Upload failed", "error");
          }
        }
        redraw();
      })();
    });
  });

  // ── pick / unpick a photo ────────────────────────────────────────────
  card.querySelectorAll<HTMLDivElement>("[data-cr-ba-pick]").forEach((el) => {
    el.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).closest("[data-cr-ba-del]")) return; // delete handled below
      const id = el.dataset.crBaPick || "";
      const kind = el.dataset.crBaKind === "After" ? "after" : "before";
      pick[kind] = pick[kind] === id ? null : id;
      redraw();
    });
  });

  // ── delete a photo ───────────────────────────────────────────────────
  card.querySelectorAll<HTMLButtonElement>("[data-cr-ba-del]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const id = btn.dataset.crBaDel || "";
      void (async (): Promise<void> => {
        const usedBy = pairs.filter((p) => p.beforeFileId === id || p.afterFileId === id).length;
        const ok = await confirmDialog(
          usedBy
            ? `Remove this photo? ${usedBy} pair${usedBy > 1 ? "s" : ""} built on it will be removed too.`
            : "Remove this photo?",
          { title: "Remove photo", confirmLabel: "Remove", destructive: true },
        );
        if (!ok) return;
        try {
          await ctx.api(`/portal/customer-photos/${encodeURIComponent(id)}`, { method: "DELETE" });
          photos.splice(photos.findIndex((p) => p.id === id), 1);
          for (let i = pairs.length - 1; i >= 0; i--) {
            if (pairs[i].beforeFileId === id || pairs[i].afterFileId === id) pairs.splice(i, 1);
          }
          if (pick.before === id) pick.before = null;
          if (pick.after === id) pick.after = null;
          redraw();
        } catch (err) {
          ctx.showToast(err instanceof Error ? err.message : "Could not remove that photo", "error");
        }
      })();
    });
  });

  // ── create a pair from the two picked photos ─────────────────────────
  const createBtn = card.querySelector<HTMLButtonElement>("[data-cr-ba-create]");
  if (createBtn) {
    createBtn.addEventListener("click", () => {
      if (!pick.before || !pick.after) return;
      const title = (card.querySelector<HTMLInputElement>("[data-cr-ba-title]")?.value || "").trim();
      void (async (): Promise<void> => {
        try {
          const resp = await ctx.api<{ pair?: PhotoPair }>("/portal/gallery", {
            method: "POST",
            body: JSON.stringify({ beforeFileId: pick.before, afterFileId: pick.after, title }),
          });
          if (resp && resp.pair) pairs.unshift(resp.pair);
          pick.before = null;
          pick.after = null;
          ctx.showToast("Pair created", "success");
          redraw();
        } catch (err) {
          ctx.showToast(err instanceof Error ? err.message : "Could not create that pair", "error");
        }
      })();
    });
  }

  // ── consent ──────────────────────────────────────────────────────────
  card.querySelectorAll<HTMLInputElement>("[data-cr-ba-consent]").forEach((box) => {
    box.addEventListener("change", () => {
      const id = box.dataset.crBaConsent || "";
      const wanted = box.checked;
      void (async (): Promise<void> => {
        // Withdrawing consent also withdraws approval server-side, so warn
        // before doing it rather than surprising them with a pair going dark.
        if (!wanted) {
          const p = pairs.find((x) => x.id === id);
          if (p?.isApproved) {
            const ok = await confirmDialog(
              "Remove permission for these photos? They will stop being approved for use.",
              { title: "Remove permission", confirmLabel: "Remove permission", destructive: true },
            );
            if (!ok) { box.checked = true; return; }
          }
        }
        try {
          const resp = await ctx.api<{ pair?: PhotoPair }>(
            `/portal/gallery/${encodeURIComponent(id)}`,
            { method: "PATCH", body: JSON.stringify({ consentConfirmed: wanted }) },
          );
          const i = pairs.findIndex((x) => x.id === id);
          if (i >= 0 && resp && resp.pair) pairs[i] = resp.pair;
          redraw();
        } catch (err) {
          ctx.showToast(err instanceof Error ? err.message : "Could not save that", "error");
          redraw();
        }
      })();
    });
  });

  // ── approve / un-approve ─────────────────────────────────────────────
  card.querySelectorAll<HTMLButtonElement>("[data-cr-ba-approve]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.crBaApprove || "";
      const p = pairs.find((x) => x.id === id);
      if (!p) return;
      void (async (): Promise<void> => {
        try {
          const resp = await ctx.api<{ pair?: PhotoPair }>(
            `/portal/gallery/${encodeURIComponent(id)}`,
            { method: "PATCH", body: JSON.stringify({ isApproved: !p.isApproved }) },
          );
          const i = pairs.findIndex((x) => x.id === id);
          if (i >= 0 && resp && resp.pair) pairs[i] = resp.pair;
          redraw();
        } catch (err) {
          ctx.showToast(err instanceof Error ? err.message : "Could not update that pair", "error");
        }
      })();
    });
  });

  // ── export ───────────────────────────────────────────────────────────
  card.querySelectorAll<HTMLButtonElement>("[data-cr-ba-dl]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.crBaDl || "";
      const side = btn.dataset.side === "before" ? "before" : "after";
      // A plain navigation: the endpoint answers with Content-Disposition, so
      // the browser saves rather than navigates away.
      window.open(
        `/api/portal/gallery/${encodeURIComponent(id)}/download?side=${side}`,
        "_blank",
        "noopener",
      );
    });
  });

  // ── delete a pair ────────────────────────────────────────────────────
  card.querySelectorAll<HTMLButtonElement>("[data-cr-ba-pairdel]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.crBaPairdel || "";
      void (async (): Promise<void> => {
        const ok = await confirmDialog(
          "Delete this pair? The photos stay on the job record.",
          { title: "Delete pair", confirmLabel: "Delete", destructive: true },
        );
        if (!ok) return;
        try {
          await ctx.api(`/portal/gallery/${encodeURIComponent(id)}`, { method: "DELETE" });
          const i = pairs.findIndex((x) => x.id === id);
          if (i >= 0) pairs.splice(i, 1);
          redraw();
        } catch (err) {
          ctx.showToast(err instanceof Error ? err.message : "Could not delete that pair", "error");
        }
      })();
    });
  });
}

// ── 5. Deliveries ───────────────────────────────────────────────────────────

// Local copy of the backend's ServerDelivery shape, extended with the
// fields the CRUD form actually edits.
interface WireDelivery {
  id: string;
  ref: string;
  customer: string;
  description: string;
  address: string;
  eta: number | null;
  status: string;
  value: number;
  notes: string;
  carrier: string | null;
}

function deliveryStatusColor(status: string): string {
  if (status === "delivered") return "#22c55e";
  if (status === "out_for_delivery" || status === "in_transit") return "#e58200";
  if (status === "failed") return "#ef4444";
  return "#3b82f6";
}

/**
 * Build a `datetime-local` input value (`YYYY-MM-DDTHH:MM`) in LOCAL time.
 *
 * When there's no stored ETA (a brand-new delivery) we default to tomorrow at
 * 10:00 rather than leaving the field blank — that guarantees a real time is
 * captured and stops deliveries from being stored at midnight (which surfaced
 * on the customer portal as a meaningless "00:00 / 01:00"). Matches the 10:00
 * default used by the standalone Deliveries and Bookings pages.
 */
function dateTimeInputValue(ts: number | null): string {
  let d: Date;
  if (ts != null && Number.isFinite(new Date(ts).getTime())) {
    d = new Date(ts);
  } else {
    d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
  }
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function deliveryFormHtml(d: Partial<WireDelivery> | null): string {
  const id = d?.id ?? "";
  const status = d?.status ?? "scheduled";
  const carrier = d?.carrier ?? "";
  const dateVal = dateTimeInputValue(d?.eta ?? null);
  const statusOpts = DELIVERY_STATUSES
    .map((s) => `<option value="${esc(s.value)}"${s.value === status ? " selected" : ""}>${esc(s.label)}</option>`)
    .join("");
  const carrierOpts = CARRIER_OPTIONS
    .map((cOpt) => `<option value="${esc(cOpt.value)}"${cOpt.value === carrier ? " selected" : ""}>${esc(cOpt.label)}</option>`)
    .join("");
  return `
    <form class="cr-del-form" data-cr-del-form-id="${esc(id)}">
      <div class="cr-del-form-grid">
        <label>
          <span>Delivery date &amp; time</span>
          <input type="datetime-local" name="eta" value="${esc(dateVal)}">
        </label>
        <label>
          <span>Status</span>
          <select name="status">${statusOpts}</select>
        </label>
        <label class="cr-del-form-full">
          <span>Items / description</span>
          <input type="text" name="description" value="${esc(d?.description ?? "")}"
                 placeholder="e.g. Cabinets + worktops" maxlength="400">
        </label>
        <label>
          <span>Driver</span>
          <select name="carrier">${carrierOpts}</select>
        </label>
        <label>
          <span>Reference</span>
          <input type="text" name="ref" value="${esc(d?.ref ?? "")}"
                 placeholder="auto-generated if blank" maxlength="60">
        </label>
        <label class="cr-del-form-full">
          <span>Notes</span>
          <textarea name="notes" rows="2" maxlength="4000"
                    placeholder="Internal notes (optional)">${esc(d?.notes ?? "")}</textarea>
        </label>
      </div>
      <div class="cr-del-form-actions">
        <button type="submit" class="cr-action-btn primary" data-cr-del-save="1">
          ${SVG_CHECK}<span>${id ? "Save changes" : "Add delivery"}</span>
        </button>
        <button type="button" class="cr-action-btn" data-cr-del-cancel="1">
          ${SVG_CLOSE}<span>Cancel</span>
        </button>
      </div>
    </form>
  `;
}

interface DeliveriesViewState {
  /** Which delivery id is being edited, or "new" for the add form, or null. */
  editing: string | null;
}

function renderDeliveriesCard(
  card: HTMLDivElement,
  list: WireDelivery[],
  state: DeliveriesViewState,
): void {
  const rows = list.length === 0
    ? `<div class="cr-empty" style="padding:14px 0">${SVG_TRUCK}No deliveries scheduled yet.</div>`
    : list.map((d) => {
        const stCol = deliveryStatusColor(d.status);
        const statusLabel = deliveryStatusLabel(d.status);
        const eta = d.eta ? `ETA ${fmtDateTime(d.eta)}` : "Not scheduled";
        const sub = [d.ref, eta, d.description].filter(Boolean).join(" · ");
        const editingThis = state.editing === d.id;
        return `
          <div class="cr-del-row" data-cr-del-id="${esc(d.id)}">
            <div class="cr-del-dot" style="background:${stCol}"></div>
            <div class="cr-del-info">
              <div class="cr-del-name">${esc(d.description || d.ref || "Delivery")}</div>
              <div class="cr-del-sub">${esc(sub)}${d.carrier ? ` · ${esc(carrierLabel(d.carrier))}` : ""}</div>
            </div>
            <span class="cr-del-status" style="background:${stCol}22;color:${stCol}">${esc(statusLabel)}</span>
            <div class="cr-del-actions">
              <button class="cr-file-btn" data-cr-del-edit="${esc(d.id)}" type="button" aria-label="Edit delivery">${SVG_PENCIL}</button>
              <button class="cr-file-btn danger" data-cr-del-remove="${esc(d.id)}" type="button" aria-label="Remove delivery">${SVG_TRASH}</button>
            </div>
          </div>
          ${editingThis ? deliveryFormHtml(d) : ""}
        `;
      }).join("");

  const addBlock = state.editing === "new"
    ? deliveryFormHtml(null)
    : "";

  card.innerHTML = cardHtml({
    title: "Deliveries",
    icon: SVG_TRUCK,
    count: list.length,
    body: `${addBlock}${rows}`,
    headExtra: state.editing === "new"
      ? ""
      : `<button class="cr-action-btn primary" data-cr-del-add="1" type="button" style="padding:5px 10px;font-size:11px">
           ${SVG_PLUS}<span>Add Delivery</span>
         </button>`,
  });
}

async function populateDeliveries(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  const card = root.querySelector<HTMLDivElement>('[data-section="deliveries"]');
  if (!card) return;

  let dels: ServerDelivery[] = [];
  try {
    const resp = await ctx.api<{ deliveries?: ServerDelivery[] }>(
      "/portal/deliveries",
      { method: "GET", signal: ac.signal },
    );
    if (resp && Array.isArray(resp.deliveries)) dels = resp.deliveries;
  } catch (err) {
    if (isAbortError(err)) return;
    if (!(err instanceof ApiError && err.status === 404)) {
      console.error("[customer] deliveries load failed", err);
    }
  }

  // The deliveries endpoint returns the full retailer-wide list; the
  // record page only cares about this customer. We filter on name match
  // (the listing endpoint's customer_label is a "first last" string).
  const lc = c.name.toLowerCase();
  const firstName = lc.split(/\s+/)[0] ?? "";
  const mine: WireDelivery[] = dels
    .filter((d) => {
      const candidate =
        typeof d.customer === "string"
          ? d.customer
          : d.customer && typeof d.customer === "object"
          ? d.customer.name ?? ""
          : "";
      return candidate.toLowerCase().includes(firstName);
    })
    .map((d) => ({
      id: String(d.id ?? ""),
      ref: String(d.ref ?? ""),
      customer: typeof d.customer === "string" ? d.customer : (d.customer?.name ?? ""),
      description: String(d.description ?? ""),
      address: "",
      eta: toMs(d.eta ?? null),
      status: String(d.status ?? "scheduled"),
      value: 0,
      notes: "",
      carrier: (d as unknown as { carrier?: string | null }).carrier ?? null,
    }));

  const state: DeliveriesViewState = { editing: null };
  renderDeliveriesCard(card, mine, state);
  bindDeliveriesHandlers(card, mine, state, ctx, c);
}

function bindDeliveriesHandlers(
  card: HTMLDivElement,
  list: WireDelivery[],
  state: DeliveriesViewState,
  ctx: PageContext,
  c: Customer,
): void {
  const rerender = (): void => {
    renderDeliveriesCard(card, list, state);
    bindDeliveriesHandlers(card, list, state, ctx, c);
  };

  card.querySelector<HTMLButtonElement>("[data-cr-del-add]")?.addEventListener("click", () => {
    state.editing = "new";
    rerender();
  });

  card.querySelectorAll<HTMLButtonElement>("[data-cr-del-edit]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset["crDelEdit"];
      if (!id) return;
      state.editing = state.editing === id ? null : id;
      rerender();
    });
  });

  card.querySelector<HTMLButtonElement>("[data-cr-del-cancel]")?.addEventListener("click", () => {
    state.editing = null;
    rerender();
  });

  card.querySelectorAll<HTMLButtonElement>("[data-cr-del-remove]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset["crDelRemove"];
      if (!id) return;
      const d = list.find((x) => x.id === id);
      if (!d) return;
      void (async () => {
        if (!(await confirmDialog(`Remove delivery ${d.ref || ""}? This can't be undone.`, {
          title: "Remove delivery",
          confirmLabel: "Remove",
          destructive: true,
        }))) return;
        try {
          await ctx.api<{ ok: boolean }>(
            `/portal/deliveries/${encodeURIComponent(id)}`,
            { method: "DELETE" },
          );
          const idx = list.findIndex((x) => x.id === id);
          if (idx >= 0) list.splice(idx, 1);
          state.editing = null;
          rerender();
          ctx.showToast("Delivery removed", "success");
        } catch (err) {
          const msg = err instanceof ApiError && err.status === 404
            ? "Delivery already removed"
            : "Could not remove delivery";
          ctx.showToast(msg, "error");
        }
      })();
    });
  });

  const form = card.querySelector<HTMLFormElement>("[data-cr-del-form-id]");
  form?.addEventListener("submit", (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    const editingId = form.dataset["crDelFormId"] ?? "";
    const isNew = editingId === "";
    const etaStr = String(fd.get("eta") ?? "").trim();
    const etaMs = etaStr ? Date.parse(etaStr) : NaN;
    const body: Record<string, unknown> = {
      description: String(fd.get("description") ?? "").trim(),
      status: String(fd.get("status") ?? "scheduled"),
      notes: String(fd.get("notes") ?? "").trim(),
      ref: String(fd.get("ref") ?? "").trim(),
    };
    const carrier = String(fd.get("carrier") ?? "").trim();
    body["carrier"] = carrier || null;
    if (Number.isFinite(etaMs)) body["eta"] = etaMs;
    if (isNew) {
      body["customerId"] = c.id;
      body["customer"] = c.name;
      body["direction"] = "to_customer";
    }
    void (async () => {
      try {
        if (isNew) {
          const resp = await ctx.api<{ delivery?: ServerDelivery }>(
            "/portal/deliveries",
            { method: "POST", body: JSON.stringify(body) },
          );
          const d = resp.delivery;
          if (d) {
            list.unshift({
              id: String(d.id ?? ""),
              ref: String(d.ref ?? ""),
              customer: typeof d.customer === "string" ? d.customer : (d.customer?.name ?? ""),
              description: String(d.description ?? ""),
              address: "",
              eta: toMs(d.eta ?? null),
              status: String(d.status ?? "scheduled"),
              value: 0,
              notes: "",
              carrier: (d as unknown as { carrier?: string | null }).carrier ?? null,
            });
          }
          ctx.showToast("Delivery added", "success");
        } else {
          const resp = await ctx.api<{ delivery?: ServerDelivery }>(
            `/portal/deliveries/${encodeURIComponent(editingId)}`,
            { method: "PUT", body: JSON.stringify(body) },
          );
          const d = resp.delivery;
          const idx = list.findIndex((x) => x.id === editingId);
          if (d && idx >= 0) {
            list[idx] = {
              id: String(d.id ?? ""),
              ref: String(d.ref ?? ""),
              customer: typeof d.customer === "string" ? d.customer : (d.customer?.name ?? ""),
              description: String(d.description ?? ""),
              address: "",
              eta: toMs(d.eta ?? null),
              status: String(d.status ?? "scheduled"),
              value: 0,
              notes: "",
              carrier: (d as unknown as { carrier?: string | null }).carrier ?? null,
            };
          }
          ctx.showToast("Delivery updated", "success");
        }
        state.editing = null;
        rerender();
      } catch (err) {
        const msg = err instanceof ApiError && err.status === 503
          ? "Database unavailable"
          : "Could not save delivery";
        ctx.showToast(msg, "error");
      }
    })();
  });
}

// ── 6. Bookings / Calendar ─────────────────────────────────────────────────

async function populateBookingsAndCalendar(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  const card = root.querySelector<HTMLDivElement>('[data-section="bookings"]');
  if (!card) return;

  const items: CalItem[] = [];

  // GET /portal/customer-bookings?customerId=<id> — server filters by
  // customer_id (reliable; not name-matched) and returns friendly labels.
  // Surfaces wizard step-3 appointment requests (call back / showroom / home
  // visit) alongside any retailer/customer-created bookings.
  try {
    const resp = await ctx.api<{ bookings?: CustomerBooking[] }>(
      `/portal/customer-bookings?customerId=${encodeURIComponent(c.id)}`,
      { method: "GET", signal: ac.signal },
    );
    if (resp && Array.isArray(resp.bookings)) {
      for (const b of resp.bookings) {
        const status = String(b.status ?? "").trim();
        items.push({
          title: String(b.typeLabel ?? "Appointment"),
          dateMs: toMs(b.scheduledAt ?? b.createdAt ?? null),
          typeLabel: status
            ? status.charAt(0).toUpperCase() + status.slice(1)
            : "Booking",
          color: "#e58200",
        });
      }
    }
  } catch (err) {
    if (isAbortError(err)) return;
    if (!(err instanceof ApiError && err.status === 404)) {
      console.error("[customer] bookings load failed", err);
    }
  }

  // Bookings only — calendar events belong to the Calendar page, not here.

  items.sort((a, b) => (a.dateMs ?? 0) - (b.dateMs ?? 0));

  const body =
    items.length === 0
      ? emptyStateHtml(SVG_CAL, "No bookings yet.")
      : items
          .map(
            (ev) => `
              <div class="cr-cal-row">
                <div class="cr-cal-dot" style="background:${esc(ev.color)}"></div>
                <div class="cr-cal-info">
                  <div class="cr-cal-title">${esc(ev.title)}</div>
                  <div class="cr-cal-sub">${esc(ev.typeLabel)}${ev.dateMs != null ? ` · ${esc(fmtDateTime(ev.dateMs))}` : ""}</div>
                </div>
              </div>
            `,
          )
          .join("");

  card.innerHTML = cardHtml({
    title: "Bookings",
    icon: SVG_CAL,
    count: items.length,
    body,
    headExtra: `
      <button class="cr-action-btn primary cev-add-btn" data-cr-add-booking="1" style="padding:5px 10px;font-size:11px">
        ${SVG_PLUS}Add New
      </button>
    `,
  });

  card.querySelector<HTMLButtonElement>('[data-cr-add-booking]')?.addEventListener("click", () => {
    void promptAndCreateBooking(root, ctx, ac, c);
  });
}

async function promptAndCreateBooking(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  // Creates a real booking (PG `bookings`) via POST /portal/bookings. That
  // surfaces it on the retailer Bookings page + Calendar, in the customer
  // portal as an upcoming appointment, and notifies the customer (SMS/email/
  // bell) — all driven by the shared endpoint's customer cascade.
  const TYPES: Array<{ value: string; label: string }> = [
    { value: "design_consultation", label: "Call back / consultation" },
    { value: "showroom_visit", label: "Showroom visit" },
    { value: "site_visit", label: "Home visit" },
  ];
  let typeSel: HTMLSelectElement | null = null;
  let dateInput: HTMLInputElement | null = null;
  let timeInput: HTMLInputElement | null = null;

  const submit = async (): Promise<void> => {
    const type = typeSel?.value || "design_consultation";
    const date = (dateInput?.value || "").trim();
    const time = (timeInput?.value || "").trim() || "10:00";
    if (!date) {
      ctx.showToast("Pick a date for the booking", "error");
      return;
    }
    try {
      await ctx.api<unknown>("/portal/bookings", {
        method: "POST",
        signal: ac.signal,
        body: JSON.stringify({
          customer: c.name,
          customerId: c.id,
          type,
          when: `${date}T${time}:00`,
          status: "confirmed",
          telephone: c.phone || "",
        }),
      });
      ctx.showToast("Booking added — customer notified", "success");
      await populateBookingsAndCalendar(root, ctx, ac, c);
    } catch (err) {
      if (isAbortError(err)) return;
      console.error("[customer] create booking failed", err);
      ctx.showToast("Could not add booking", "error");
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const modal = createModal({
    title: "New booking",
    subtitle: c.name,
    size: "sm",
    content: (body) => {
      body.innerHTML = `
        <div style="display:flex;flex-direction:column;gap:12px">
          <label style="display:flex;flex-direction:column;gap:4px;font-size:12px;color:var(--color-text-secondary)">
            Type
            <select class="cr-edit-input" data-bk="type">
              ${TYPES.map((t) => `<option value="${t.value}">${esc(t.label)}</option>`).join("")}
            </select>
          </label>
          <label style="display:flex;flex-direction:column;gap:4px;font-size:12px;color:var(--color-text-secondary)">
            Date
            <input type="date" class="cr-edit-input" data-bk="date" value="${today}" min="${today}">
          </label>
          <label style="display:flex;flex-direction:column;gap:4px;font-size:12px;color:var(--color-text-secondary)">
            Time
            <input type="time" class="cr-edit-input" data-bk="time" value="10:00">
          </label>
        </div>`;
      typeSel = body.querySelector<HTMLSelectElement>('[data-bk="type"]');
      dateInput = body.querySelector<HTMLInputElement>('[data-bk="date"]');
      timeInput = body.querySelector<HTMLInputElement>('[data-bk="time"]');
    },
    actions: [
      { label: "Cancel", variant: "ghost" },
      { label: "Add booking", variant: "primary", onClick: () => { void submit(); } },
    ],
  });
  modal.open();
}

// ── 6b. Pinned Notes ─────────────────────────────────────────────────────────
// Short, always-visible customer-handling flags (kind='pinned' in
// customer_notes), kept SEPARATE from the timeline Notes card below. Internal
// to the retailer only. Also surfaced (read-only) above the message composer —
// see the Messages page.

interface WirePin {
  id: string;
  text: string;
  ts: number;
}

const PIN_MAX_LEN = 280;

function renderPinnedNotesCard(card: HTMLDivElement, pins: WirePin[]): void {
  const rows = pins
    .map((p) => `
      <div class="cr-pin" data-cr-pin-id="${esc(p.id)}">
        <span class="cr-pin__dot" aria-hidden="true">${SVG_PIN}</span>
        <span class="cr-pin__text">${esc(p.text)}</span>
        <button class="cr-pin__del" type="button" aria-label="Unpin note"
                data-cr-pin-del="${esc(p.id)}">&times;</button>
      </div>
    `)
    .join("");

  const list = pins.length === 0
    ? `<div class="cr-empty" style="padding:12px 0">${SVG_PIN}No pinned notes — pin a key reminder below.</div>`
    : `<div class="cr-pin-list">${rows}</div>`;

  const body = `
    ${list}
    <div class="cr-pin-compose">
      <input class="cr-pin-input" data-cr-pin-input="1" type="text"
             maxlength="${PIN_MAX_LEN}"
             placeholder="Pin a note (e.g. always email)…" />
      <button class="cr-action-btn primary cr-pin-add" data-cr-pin-add="1" type="button">
        ${SVG_PLUS}<span>Pin</span>
      </button>
    </div>
  `;

  card.innerHTML = cardHtml({ title: "Pinned Notes", icon: SVG_PIN, count: pins.length, body });
}

async function populatePinnedNotes(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  const card = root.querySelector<HTMLDivElement>('[data-section="pinned-notes"]');
  if (!card) return;

  let pins: WirePin[] = [];
  try {
    const resp = await ctx.api<{ pins?: WirePin[] }>(
      `/portal/customer-pinned-notes?customerId=${encodeURIComponent(c.id)}`,
      { method: "GET", signal: ac.signal },
    );
    if (resp && Array.isArray(resp.pins)) pins = resp.pins;
  } catch (err) {
    if (isAbortError(err)) return;
    if (!(err instanceof ApiError && err.status === 404)) {
      console.error("[customer] pinned notes load failed", err);
    }
  }

  renderPinnedNotesCard(card, pins);
  bindPinnedNotesHandlers(card, pins, ctx, c);
}

function bindPinnedNotesHandlers(
  card: HTMLDivElement,
  pins: WirePin[],
  ctx: PageContext,
  c: Customer,
): void {
  const input = card.querySelector<HTMLInputElement>('[data-cr-pin-input]');
  const addBtn = card.querySelector<HTMLButtonElement>('[data-cr-pin-add]');

  const addPin = async (): Promise<void> => {
    if (!input || !addBtn) return;
    const text = input.value.trim();
    if (!text) return;
    addBtn.disabled = true;
    input.disabled = true;
    try {
      const resp = await ctx.api<{ pins?: WirePin[] }>(
        `/portal/customer-pinned-notes`,
        { method: "POST", body: JSON.stringify({ customerId: c.id, text }) },
      );
      const fresh = (resp.pins ?? []).map((p) => ({ id: p.id, text: p.text, ts: p.ts } satisfies WirePin));
      pins.splice(0, pins.length, ...fresh);
      renderPinnedNotesCard(card, pins);
      bindPinnedNotesHandlers(card, pins, ctx, c);
      ctx.showToast("Note pinned", "success");
    } catch (err) {
      addBtn.disabled = false;
      input.disabled = false;
      ctx.showToast(
        err instanceof ApiError && err.status === 503 ? "Database unavailable" : "Could not pin note",
        "error",
      );
    }
  };

  addBtn?.addEventListener("click", () => { void addPin(); });
  input?.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void addPin();
    }
  });

  card.querySelectorAll<HTMLButtonElement>("[data-cr-pin-del]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset["crPinDel"];
      if (!id) return;
      void (async () => {
        // Optimistic remove (unpinning is low-stakes — no confirm dialog).
        const idx = pins.findIndex((p) => p.id === id);
        const snapshot = idx >= 0 ? pins[idx] : null;
        if (idx >= 0) pins.splice(idx, 1);
        renderPinnedNotesCard(card, pins);
        bindPinnedNotesHandlers(card, pins, ctx, c);
        try {
          await ctx.api<{ ok: boolean }>(
            `/portal/customer-pinned-notes/${encodeURIComponent(id)}`,
            { method: "DELETE" },
          );
        } catch (err) {
          if (snapshot) {
            pins.splice(Math.min(idx, pins.length), 0, snapshot);
            renderPinnedNotesCard(card, pins);
            bindPinnedNotesHandlers(card, pins, ctx, c);
          }
          const msg = err instanceof ApiError && err.status === 404
            ? "Note already removed"
            : "Could not unpin note";
          ctx.showToast(msg, "error");
        }
      })();
    });
  });
}

// ── 7. Notes ───────────────────────────────────────────────────────────────

interface WireNote {
  id: string;
  type: string;
  text: string;
  ts: number;
  authorName?: string;
}

function renderNotesCard(
  card: HTMLDivElement,
  notes: WireNote[],
  ctx: PageContext,
): void {
  const authorFallback = ctx.user?.retailerName ?? "You";
  const rows = notes
    .slice(0, 8)
    .map((n) => `
      <div class="cr-tl-item" data-cr-note-id="${esc(n.id)}">
        <div class="cr-tl-dot" style="background:#e5820022;border:2px solid #e58200"></div>
        <div class="cr-tl-content">
          <div class="cr-tl-text">${esc(n.text)}</div>
          <div class="cr-tl-time">
            <span>${esc(n.authorName || authorFallback)} · ${esc(fmtDateTime(n.ts))}</span>
            <button class="cr-tl-del" type="button" aria-label="Delete note"
                    data-cr-note-del="${esc(n.id)}">${SVG_TRASH}</button>
          </div>
        </div>
      </div>
    `)
    .join("");

  const list = notes.length === 0
    ? `<div class="cr-empty" style="padding:14px 0">${SVG_NOTE}No notes yet — add the first one below.</div>`
    : `<div class="cr-timeline">${rows}</div>`;

  const body = `
    ${list}
    <div class="cr-notes-compose">
      <textarea class="cr-notes-input" data-cr-note-input="1" rows="2"
                placeholder="Log a note about this customer…"></textarea>
      <button class="cr-action-btn primary cr-notes-add" data-cr-note-add="1" type="button">
        ${SVG_PLUS}<span>Add Note</span>
      </button>
    </div>
  `;

  card.innerHTML = cardHtml({
    title: "Notes",
    icon: SVG_NOTE,
    count: notes.length,
    body,
  });
}

async function populateNotes(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  const card = root.querySelector<HTMLDivElement>('[data-section="notes"]');
  if (!card) return;
  // Backed by customer_notes (kind='timeline') via /portal/customer-notes.
  // POST goes through /portal/customer-workflow/:cid/timeline (shared with
  // the Workflow & Operations Activity Timeline so both views stay
  // consistent). DELETE is wired to /portal/customer-notes/:id.

  let notes: WireNote[] = [];
  try {
    const resp = await ctx.api<{ notes?: WireNote[] }>(
      `/portal/customer-notes?customerId=${encodeURIComponent(c.id)}`,
      { method: "GET", signal: ac.signal },
    );
    if (resp && Array.isArray(resp.notes)) notes = resp.notes;
  } catch (err) {
    if (isAbortError(err)) return;
    if (!(err instanceof ApiError && err.status === 404)) {
      console.error("[customer] notes load failed", err);
    }
  }

  renderNotesCard(card, notes, ctx);
  bindNotesHandlers(card, notes, ctx, c);
}

function bindNotesHandlers(
  card: HTMLDivElement,
  notes: WireNote[],
  ctx: PageContext,
  c: Customer,
): void {
  const input = card.querySelector<HTMLTextAreaElement>('[data-cr-note-input]');
  const addBtn = card.querySelector<HTMLButtonElement>('[data-cr-note-add]');

  const addNote = async (): Promise<void> => {
    if (!input || !addBtn) return;
    const text = input.value.trim();
    if (!text) return;
    addBtn.disabled = true;
    input.disabled = true;
    try {
      // The workflow timeline POST is the authoritative write — it returns
      // the full updated timeline so we don't need a separate refetch.
      interface WireTl { id: string; type: string; text: string; ts: number; authorName?: string; }
      const resp = await ctx.api<{ timeline?: WireTl[] }>(
        `/portal/customer-workflow/${encodeURIComponent(c.id)}/timeline`,
        { method: "POST", body: JSON.stringify({ type: "note", text }) },
      );
      const fresh = (resp.timeline ?? []).map((t) => ({
        id: t.id,
        type: t.type,
        text: t.text,
        ts: t.ts,
        ...(t.authorName ? { authorName: t.authorName } : {}),
      } satisfies WireNote));
      notes.splice(0, notes.length, ...fresh);
      renderNotesCard(card, notes, ctx);
      bindNotesHandlers(card, notes, ctx, c);
      ctx.showToast("Note added", "success");
    } catch (err) {
      addBtn.disabled = false;
      input.disabled = false;
      if (err instanceof ApiError && err.status === 503) {
        ctx.showToast("Database unavailable", "error");
      } else {
        ctx.showToast("Could not add note", "error");
      }
    }
  };

  addBtn?.addEventListener("click", () => { void addNote(); });
  input?.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void addNote();
    }
  });

  card.querySelectorAll<HTMLButtonElement>("[data-cr-note-del]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset["crNoteDel"];
      if (!id) return;
      void (async () => {
        if (!(await confirmDialog("Remove this note? This can't be undone.", {
          title: "Remove note",
          confirmLabel: "Remove",
          destructive: true,
        }))) return;
        // Optimistic remove.
        const idx = notes.findIndex((n) => n.id === id);
        const snapshot = idx >= 0 ? notes[idx] : null;
        if (idx >= 0) notes.splice(idx, 1);
        renderNotesCard(card, notes, ctx);
        bindNotesHandlers(card, notes, ctx, c);
        try {
          await ctx.api<{ ok: boolean }>(
            `/portal/customer-notes/${encodeURIComponent(id)}`,
            { method: "DELETE" },
          );
          ctx.showToast("Note removed", "success");
        } catch (err) {
          // Roll back on failure so the user can retry.
          if (snapshot) {
            notes.splice(Math.min(idx, notes.length), 0, snapshot);
            renderNotesCard(card, notes, ctx);
            bindNotesHandlers(card, notes, ctx, c);
          }
          const msg = err instanceof ApiError && err.status === 404
            ? "Note already removed"
            : "Could not remove note";
          ctx.showToast(msg, "error");
        }
      })();
    });
  });
}

// ── Customer Brief (wizard submission) ──────────────────────────────────────
//
// Read-only surfacing of the full lead-gen wizard payload (stored intact in
// `customers.wizard_data`). The lead card previously flattened only a subset
// into the notes line; this shows everything the customer told us.

const SVG_CLIPBOARD =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="8" y="3" width="8" height="4" rx="1"/><path d="M9 5H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-3"/><path d="M9 12h6"/><path d="M9 16h4"/></svg>';

const briefDateFmt = new Intl.DateTimeFormat("en-GB", {
  day: "numeric", month: "short", year: "numeric",
});
function fmtBriefDate(iso: string): string {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? briefDateFmt.format(new Date(t)) : "";
}

function customerBriefHtml(c: Customer): string {
  // Always renders (Task #1) — manual leads start blank so the retailer can
  // build the same rich brief a wizard submission carries. Scalar fields are
  // inline-editable (persist into customers.wizard_data); the room GEOMETRY
  // (walls/doors/windows) and appliances stay read-only (the wizard's canvas).
  const wd = c.wizardData || {};
  const obj = (v: unknown): Record<string, unknown> =>
    v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  const str = (v: unknown): string => (v == null ? "" : String(v)).trim();
  const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
  const rd = obj(wd["roomDimensions"]);
  const sc = obj(wd["styleChoices"]);
  const ap = obj(sc["appliances"]);

  // Editable row — label + an editableField bound to a wizard_data path.
  const editRow = (label: string, opts: EditableOpts): string =>
    `<div class="cr-brief-row"><span class="cr-brief-k">${esc(label)}</span><span class="cr-brief-v">${editableField(opts)}</span></div>`;
  // Read-only row — only shown when it has a value (geometry / appliances).
  const roRow = (label: string, value: string): string => value
    ? `<div class="cr-brief-row"><span class="cr-brief-k">${esc(label)}</span><span class="cr-brief-v">${esc(value)}</span></div>`
    : "";

  const timeline = str(wd["timeline"]);
  const leadSource = str(wd["leadSource"]);
  const ceiling = str(rd["ceilingHeightMm"]);
  const doorRange = str(sc["doorRange"]);
  const colour = str(sc["colourName"]);
  const worktop = str(sc["worktop"]);
  const storage = arr(sc["storage"]).map(str).filter(Boolean);
  // Flat render keys — present on every wizard lead ever captured, including
  // the ones taken before this card had anywhere to show them.
  const handleStyle = str(wd["handle_style"]).toLowerCase();
  const handleFinish = str(wd["handle_finish"]).toLowerCase();
  const flooringType = str(wd["flooring_type"]).toLowerCase();
  // Read-mode display shows the complete answer even when the stored token
  // only carries half of it: "cup" + finish "brass" reads "Cup Handle — Brass",
  // while "brass_bar" already reads "Bar — Brass" on its own.
  const handleBase = labelFor(handleStyle, HANDLE_VALUES, HANDLE_LABELS);
  const finishLabel = labelFor(handleFinish, HANDLE_FINISH_VALUES, HANDLE_FINISH_LABELS);
  const handleDisplay = handleBase && finishLabel && HANDLE_NEEDS_FINISH_ROW.has(handleStyle)
    ? `${handleBase} — ${finishLabel}`
    : handleBase;

  const editRows = [
    editRow("Timeline", { field: "wizard", wzPath: "timeline", value: timeline, display: esc(timeline), options: TIMELINE_OPTS }),
    editRow("How they heard", { field: "wizard", wzPath: "leadSource", value: leadSource, display: esc(leadSource), options: LEADSOURCE_OPTS }),
    editRow("Ceiling height", { field: "wizard", wzPath: "roomDimensions.ceilingHeightMm", wzType: "number", value: ceiling, display: esc(ceiling ? `${ceiling} mm` : "") }),
    editRow("Door range", { field: "wizard", wzPath: "styleChoices.doorRange", value: doorRange, display: esc(doorRange) }),
    editRow("Colour", { field: "wizard", wzPath: "styleChoices.colourName", value: colour, display: esc(colour) }),
    editRow("Worktop", { field: "wizard", wzPath: "styleChoices.worktop", value: worktop, display: esc(worktop), options: WORKTOP_OPTS }),
    editRow("Handles", {
      field: "wizard", wzPath: "handle_style", value: handleStyle, display: esc(handleDisplay),
      options: withBlank(HANDLE_VALUES), optionLabels: withBlankLabels(HANDLE_LABELS),
    }),
    // Only for shapes whose token can't carry the metal — see the note by
    // HANDLE_NEEDS_FINISH_ROW. For a bar handle the finish is already part of
    // the Handles value above.
    ...(HANDLE_NEEDS_FINISH_ROW.has(handleStyle)
      ? [editRow("Handle finish", {
          field: "wizard", wzPath: "handle_finish", value: handleFinish, display: esc(finishLabel),
          options: withBlank(HANDLE_FINISH_VALUES), optionLabels: withBlankLabels(HANDLE_FINISH_LABELS),
        })]
      : []),
    editRow("Flooring", {
      field: "wizard", wzPath: "flooring_type", value: flooringType,
      display: esc(labelFor(flooringType, FLOORING_VALUES, FLOORING_LABELS)),
      options: withBlank(FLOORING_VALUES), optionLabels: withBlankLabels(FLOORING_LABELS),
    }),
    editRow("Accessories", { field: "wizard", wzPath: "styleChoices.storage", wzType: "array", value: storage.join(", "), display: esc(storage.join(", ")) }),
  ];

  // Read-only geometry summary (the wizard's drawn room) + appliances.
  const layout = [
    arr(rd["walls"]).length ? `${arr(rd["walls"]).length} walls` : "",
    arr(rd["doors"]).length ? `${arr(rd["doors"]).length} doors` : "",
    arr(rd["windows"]).length ? `${arr(rd["windows"]).length} windows` : "",
    arr(rd["boilers"]).length ? `${arr(rd["boilers"]).length} boiler(s)` : "",
  ].filter(Boolean).join(" · ");

  const applBits: string[] = [];
  const appl = (label: string, key: string): void => {
    const v = str(ap[key]);
    if (v) applBits.push(`${label}: ${v}`);
  };
  appl("Sink", "sink"); appl("Fridge", "fridge"); appl("Hob", "hob");
  appl("Oven", "oven"); appl("Extractor", "extractor"); appl("Microwave", "microwave");
  if (ap["wineCooler"] === true) applBits.push("Wine cooler");
  if (ap["boilingTap"] === true) applBits.push("Boiling tap");
  const lighting = arr(ap["lighting"]).map(str).filter(Boolean);
  if (lighting.length) applBits.push(`Lighting: ${lighting.join(", ")}`);

  const roRows = [
    roRow("Room layout", layout),
    roRow("Appliances", applBits.join(" · ")),
  ].filter(Boolean);

  const notes = str(wd["notes"]);
  const notesBlock =
    `<div class="cr-brief-notes"><span class="cr-brief-k">Customer notes</span>` +
    `<div class="cr-brief-v">${editableField({ field: "wizard", wzPath: "notes", multiline: true, value: notes, display: esc(notes) })}</div></div>`;

  const dateStr = fmtBriefDate(c.submittedAt);
  const fromWizard = c.source === "wizard";
  return `
    <div class="cr-card cr-brief-card" data-section="customer-brief">
      <div class="cr-card-head">
        <div class="cr-card-title">${SVG_CLIPBOARD}Customer Brief</div>
        <span class="cr-brief-badge">${fromWizard ? `From wizard${dateStr ? ` · ${esc(dateStr)}` : ""}` : "Lead details"}</span>
      </div>
      <div class="cr-card-body">
        <p class="cr-brief-intro">Project details — click any field to edit. Room geometry stays as drawn in the wizard.</p>
        ${editRows.join("")}
        ${roRows.join("")}
        ${notesBlock}
      </div>
    </div>`;
}

// ── Project type / journey control (Bug 4) ──────────────────────────────────
//
// Sets supply-only vs supply-and-install (controls which stages the customer
// sees in their portal journey) and the manual "project complete" flag (the
// only journey stage with no automatic trigger). Persists via the same
// PUT /api/crm/contacts/:id patch path as every other record field.

const SVG_LAYERS =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>';

/** Effective supply type for display — '' (unset) shows as supply_install,
 *  matching the customer-portal default. */
function effectiveSupply(c: Customer): "supply_install" | "supply_only" {
  return c.supplyType === "supply_only" ? "supply_only" : "supply_install";
}

function journeyControlHtml(c: Customer): string {
  const sup = effectiveSupply(c);
  const opt = (key: "supply_install" | "supply_only", label: string): string =>
    `<button type="button" class="cr-journey-opt${sup === key ? " cr-journey-opt--on" : ""}" data-supply="${key}">${label}</button>`;
  return `
    <div class="cr-card cr-journey-card" data-section="journey-control">
      <div class="cr-card-head">
        <div class="cr-card-title">${SVG_LAYERS}Project Type</div>
      </div>
      <div class="cr-card-body">
        <p class="cr-journey-hint">Controls which stages the customer sees in their kitchen journey.</p>
        <div class="cr-journey-seg" role="group" aria-label="Project type">
          ${opt("supply_install", "Supply &amp; Install")}
          ${opt("supply_only", "Supply Only")}
        </div>
        <label class="cr-journey-complete">
          <input type="checkbox" data-journey-complete${c.journeyComplete ? " checked" : ""} />
          <span>Mark project complete</span>
        </label>
      </div>
    </div>`;
}

function bindJourneyControl(root: HTMLDivElement, ctx: PageContext, c: Customer): void {
  const card = root.querySelector<HTMLDivElement>('[data-section="journey-control"]');
  if (!card) return;

  const syncSeg = (): void => {
    const sup = effectiveSupply(c);
    card.querySelectorAll<HTMLButtonElement>(".cr-journey-opt").forEach((b) => {
      b.classList.toggle("cr-journey-opt--on", b.dataset["supply"] === sup);
    });
  };

  card.querySelectorAll<HTMLButtonElement>(".cr-journey-opt").forEach((btn) => {
    btn.addEventListener("click", () => {
      const next = btn.dataset["supply"];
      if (next !== "supply_install" && next !== "supply_only") return;
      if (effectiveSupply(c) === next) return;
      // Optimistic — repaint immediately, revert on failure.
      card.querySelectorAll<HTMLButtonElement>(".cr-journey-opt").forEach((b) => {
        b.classList.toggle("cr-journey-opt--on", b === btn);
      });
      void patchCustomer(root, ctx, c, { supplyType: next }).then((ok) => {
        if (!ok) syncSeg();
      });
    });
  });

  const cb = card.querySelector<HTMLInputElement>("[data-journey-complete]");
  if (cb) {
    cb.addEventListener("change", () => {
      const want = cb.checked;
      void patchCustomer(root, ctx, c, { journeyComplete: want }).then((ok) => {
        if (!ok) cb.checked = !want;
      });
    });
  }
}

// ── 8. Lead score breakdown ────────────────────────────────────────────────

function scoreCardHtml(c: Customer, scoreCol: string): string {
  const score = c.score;
  const label = score >= 70 ? "Strong lead" : score >= 40 ? "Warm lead" : "Cold lead";

  return `
    <div class="cr-card cr-score-card">
      <div class="cr-card-head">
        <div class="cr-card-title">${SVG_BOLT}Lead Score</div>
        <span class="cr-card-count" style="color:${scoreCol}">${score}/100</span>
      </div>
      <div class="cr-card-body">
        <div class="cr-score-bar-wrap">
          <div class="cr-score-bar-fill" style="width:${score}%;background:linear-gradient(90deg,${scoreCol},#ffb84d)"></div>
        </div>
        <div class="cr-score-summary" style="color:${scoreCol}">${esc(label)}</div>
      </div>
    </div>
  `;
}

// ── 9. Documents ───────────────────────────────────────────────────────────
// Drag-and-drop document store for the customer record (Steve 2026-05-22).
// Accepts pdf/jpg/png/webp/docx. Backed by the same `design_files` table
// used by the Plans card — the underlying storage doesn't discriminate by
// intent, so a file uploaded here appears in Plans too and vice-versa.

interface DocFile {
  id: string;
  name: string;
  type: string;
  sizeBytes: number;
  url: string;
  visibility: "hidden" | "shared";
  uploadedAt: number;
}

function renderDocumentsCard(card: HTMLDivElement, docs: DocFile[]): void {
  const dropZone = `
    <label class="cr-plans-drop" data-cr-docs-drop="1">
      ${SVG_UPLOAD}
      <strong>Click to upload</strong> or drag and drop here<br>
      <span style="font-size:11px;opacity:.7">PDF, JPG, PNG, WEBP, DOCX · max 20 MB each</span>
      <input type="file" data-cr-docs-input="1"
             accept="${DOC_FILE_ACCEPT}" multiple style="display:none">
    </label>
  `;

  const rows = docs.length === 0
    ? `<div class="cr-empty" style="padding:12px 0">${SVG_DOC}No documents uploaded yet.</div>`
    : docs.map((d) => {
        const isShared = d.visibility === "shared";
        const privacyLabel = isShared ? "Shared" : "Private";
        const privacyIcon = isShared ? SVG_EYE : SVG_EYE_OFF;
        const privacyClass = isShared ? "shared" : "private";
        return `
          <div class="cr-file-row" data-cr-doc-id="${esc(d.id)}">
            <div class="cr-plan-icon">${SVG_DOC}</div>
            <div class="cr-file-info">
              <a class="cr-file-name" href="${esc(d.url)}" target="_blank" rel="noopener">${esc(d.name)}</a>
              <div class="cr-file-sub">
                <span>${esc(fmtSize(d.sizeBytes))} · ${esc(fmtDate(d.uploadedAt))}</span>
                <span class="cr-file-privacy ${privacyClass}">
                  ${privacyIcon}<span>${esc(privacyLabel)}</span>
                </span>
              </div>
            </div>
            <button class="cr-file-btn"
                    data-cr-doc-toggle="${esc(d.id)}"
                    type="button"
                    title="${isShared ? "Make private" : "Share with customer"}">
              ${isShared ? SVG_EYE_OFF : SVG_EYE}
            </button>
            <button class="cr-file-btn danger"
                    data-cr-doc-del="${esc(d.id)}"
                    type="button" aria-label="Remove document">
              ${SVG_TRASH}
            </button>
          </div>
        `;
      }).join("");

  card.innerHTML = cardHtml({
    title: "Documents",
    icon: SVG_DOC,
    count: docs.length,
    body: `${dropZone}<div class="cr-file-list" data-cr-doc-list="1">${rows}</div>`,
  });
}

/**
 * Redraw the Documents card and rewire it, WITHOUT throwing away where the
 * user was on the page.
 *
 * `renderDocumentsCard` replaces the card wholesale, which shortens the
 * page's content and lets the browser clamp the scroll container back towards
 * the top — so removing a document sent the customer record jumping. Steve
 * reported it 2026-09-10 as the same symptom he had just hit in the Quote
 * Builder's quote panel, and he was right: same mechanism, different page,
 * fixed independently in each because the helper used to live inside the
 * Quote Builder's own folder. It is now in @tkos/shared-ui, which is what
 * made this a three-line change.
 *
 * ALWAYS PAIRED. Rendering replaces the card's DOM, so the handlers must be
 * rebound every time or the buttons are wired to detached nodes. All six call
 * sites did both, one after the other; wrapping them together removes the
 * chance of a seventh doing only one. The first paint is a mount, not a
 * repaint, and the helper defers to it rather than restoring anything.
 */
function repaintDocumentsCard(
  card: HTMLDivElement,
  docs: DocFile[],
  ctx: PageContext,
  c: Customer,
): void {
  repaintPreservingScroll(card, () => {
    renderDocumentsCard(card, docs);
    bindDocumentsHandlers(card, docs, ctx, c);
  });
}

/** Synchronous: photos come pre-extracted in `customer.wizardPhotos`. */
function populateWizardPhotos(root: HTMLDivElement, c: Customer): void {
  const card = root.querySelector<HTMLDivElement>('[data-section="wizard-photos"]');
  if (!card) return;

  const photos = c.wizardPhotos;
  if (photos.length === 0) {
    card.innerHTML = cardHtml({
      title: "Wizard Photos",
      icon: SVG_DOC,
      count: 0,
      body: emptyStateHtml(SVG_DOC, "No photos uploaded with the wizard submission."),
    });
    return;
  }

  const thumbs = photos.map((p, i) => `
    <a class="cr-wizphoto"
       href="${esc(p.url)}"
       target="_blank"
       rel="noopener"
       data-cr-wizphoto-idx="${i}"
       title="${esc(p.name || "Photo")}">
      <img src="${esc(p.url)}" alt="${esc(p.name || "Customer photo")}" loading="lazy" />
    </a>
  `).join("");

  card.innerHTML = cardHtml({
    title: "Wizard Photos",
    icon: SVG_DOC,
    count: photos.length,
    body: `<div class="cr-wizphoto-grid">${thumbs}</div>`,
  });

  // Open photos in the in-portal lightbox rather than a new tab. The <a href>
  // is kept so middle/ctrl-click still opens the raw image; a plain click is
  // intercepted and routed to the lightbox over the whole photo set.
  const lbImages: LightboxImage[] = photos.map((p) => ({ url: p.url, name: p.name || "Photo", type: "Photo" }));
  card.querySelectorAll<HTMLAnchorElement>(".cr-wizphoto").forEach((a, i) => {
    a.addEventListener("click", (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      openLightbox({ images: lbImages, startIndex: i });
    });
  });
}

async function populateDocuments(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  const card = root.querySelector<HTMLDivElement>('[data-section="documents"]');
  if (!card) return;

  let docs: DocFile[] = [];
  try {
    interface WireDoc {
      id: string; name: string; type: string;
      sizeBytes: number; url: string;
      visibility?: string; uploadedAt: number;
      firstViewedAt?: number | null;
    }
    const resp = await ctx.api<{ documents?: WireDoc[] }>(
      `/portal/documents?customerId=${encodeURIComponent(c.id)}&kind=Other`,
      { method: "GET", signal: ac.signal },
    );
    if (resp && Array.isArray(resp.documents)) {
      docs = resp.documents.map((d) => ({
        id: d.id,
        name: d.name,
        type: d.type,
        sizeBytes: d.sizeBytes,
        url: d.url,
        visibility: d.visibility === "shared" ? "shared" : "hidden",
        uploadedAt: d.uploadedAt,
      }));
    }
  } catch (err) {
    if (isAbortError(err)) return;
    if (!(err instanceof ApiError && err.status === 404)) {
      console.error("[customer] documents load failed", err);
    }
  }

  repaintDocumentsCard(card, docs, ctx, c);
}

function bindDocumentsHandlers(
  card: HTMLDivElement,
  docs: DocFile[],
  ctx: PageContext,
  c: Customer,
): void {
  const drop = card.querySelector<HTMLLabelElement>('[data-cr-docs-drop]');
  const fileInput = card.querySelector<HTMLInputElement>('[data-cr-docs-input]');

  const upload = async (list: FileList | File[]): Promise<void> => {
    const arr = Array.from(list);
    for (const f of arr) {
      if (f.size > FILE_MAX_BYTES) {
        ctx.showToast(`${f.name} is over the 20 MB limit`, "error");
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const res = await fetch(
          `/api/portal/customer-files?customerId=${encodeURIComponent(c.id)}&visibility=hidden&kind=Other`,
          { method: "POST", credentials: "include", body: fd },
        );
        const data = (await res.json().catch(() => ({}))) as
          { ok?: boolean; file?: Partial<DocFile> & { id: string; name: string; url: string }; error?: string };
        if (!res.ok || !data.ok || !data.file) {
          throw new Error(data.error || `HTTP ${res.status}`);
        }
        const uploaded: DocFile = {
          id: data.file.id,
          name: data.file.name,
          type: data.file.type ?? f.type,
          sizeBytes: data.file.sizeBytes ?? f.size,
          url: data.file.url,
          visibility: data.file.visibility === "shared" ? "shared" : "hidden",
          uploadedAt: data.file.uploadedAt ?? Date.now(),
        };
        docs.unshift(uploaded);
        ctx.showToast(`Uploaded ${f.name}`, "success");
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed";
        ctx.showToast(`${f.name}: ${msg}`, "error");
      }
    }
    repaintDocumentsCard(card, docs, ctx, c);
  };

  if (drop) {
    const stop = (e: DragEvent): void => { e.preventDefault(); e.stopPropagation(); };
    ["dragenter", "dragover"].forEach((evt) => {
      drop.addEventListener(evt as keyof HTMLElementEventMap, (e) => {
        stop(e as DragEvent);
        drop.classList.add("is-dragover");
      });
    });
    ["dragleave", "dragend"].forEach((evt) => {
      drop.addEventListener(evt as keyof HTMLElementEventMap, (e) => {
        stop(e as DragEvent);
        drop.classList.remove("is-dragover");
      });
    });
    drop.addEventListener("drop", (e) => {
      stop(e);
      drop.classList.remove("is-dragover");
      const list = e.dataTransfer?.files;
      if (list && list.length) void upload(list);
    });
  }

  fileInput?.addEventListener("change", () => {
    if (fileInput.files && fileInput.files.length) {
      void upload(fileInput.files);
      fileInput.value = "";
    }
  });

  // Image documents open in the in-portal lightbox; PDFs / other docs keep
  // their open-in-new-tab anchor behaviour.
  const docImages = docs.filter((d) => isImageUrl(d.url) || isImageUrl(d.name));
  if (docImages.length) {
    const lb: LightboxImage[] = docImages.map((d) => ({ url: d.url, name: d.name }));
    card.querySelectorAll<HTMLDivElement>(".cr-file-row").forEach((row) => {
      const id = row.dataset["crDocId"];
      const idx = docImages.findIndex((d) => d.id === id);
      if (idx < 0) return;
      row.querySelector<HTMLAnchorElement>(".cr-file-name")?.addEventListener("click", (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        openLightbox({ images: lb, startIndex: idx });
      });
    });
  }

  card.querySelectorAll<HTMLButtonElement>("[data-cr-doc-toggle]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset["crDocToggle"];
      if (!id) return;
      const f = docs.find((x) => x.id === id);
      if (!f) return;
      const next: "hidden" | "shared" = f.visibility === "shared" ? "hidden" : "shared";
      void (async () => {
        const prev = f.visibility;
        f.visibility = next; // Optimistic.
        repaintDocumentsCard(card, docs, ctx, c);
        try {
          await ctx.api<{ ok: boolean }>(
            `/portal/customer-files/${encodeURIComponent(id)}`,
            {
              method: "PATCH",
              body: JSON.stringify({ visibility: next }),
            },
          );
          ctx.showToast(
            next === "shared" ? "Document shared with customer" : "Document set to private",
            "success",
          );
        } catch (err) {
          f.visibility = prev;
          repaintDocumentsCard(card, docs, ctx, c);
          const msg = err instanceof ApiError && err.status === 503
            ? "Database unavailable"
            : "Could not change privacy";
          ctx.showToast(msg, "error");
        }
      })();
    });
  });

  card.querySelectorAll<HTMLButtonElement>("[data-cr-doc-del]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset["crDocDel"];
      if (!id) return;
      const f = docs.find((x) => x.id === id);
      if (!f) return;
      void (async () => {
        if (!(await confirmDialog(`Remove "${f.name}"? This can't be undone.`, {
          title: "Remove document",
          confirmLabel: "Remove",
          destructive: true,
        }))) return;
        const idx = docs.findIndex((x) => x.id === id);
        const snapshot = idx >= 0 ? docs[idx] : null;
        if (idx >= 0) docs.splice(idx, 1);
        repaintDocumentsCard(card, docs, ctx, c);
        try {
          await ctx.api<{ ok: boolean }>(
            `/portal/customer-files/${encodeURIComponent(id)}`,
            { method: "DELETE" },
          );
          ctx.showToast("Document removed", "success");
        } catch (err) {
          if (snapshot) {
            docs.splice(Math.min(idx, docs.length), 0, snapshot);
            repaintDocumentsCard(card, docs, ctx, c);
          }
          const msg = err instanceof ApiError && err.status === 404
            ? "Document already removed"
            : "Could not remove document";
          ctx.showToast(msg, "error");
        }
      })();
    });
  });
}

// ── 9b. Snags ──────────────────────────────────────────────────────────────
//
// Per-customer slice of /api/portal/snags (dashboard_snags table). Server
// has no per-customer filter so we fetch the full open-snag list once and
// filter client-side by customerName === c.name (case-insensitive trim).
// Matches the dashboard widget's behaviour and lets the customer record act
// as the single source of truth without changing the snag write path.

interface WireSnag {
  id: string;
  customerName: string;
  title: string;
  content: string;
  priority: "high" | "medium" | "low" | string;
  resolvedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

function snagPriorityColor(p: string): string {
  if (p === "high")   return "#ef4444";
  if (p === "medium") return "#e58200";
  return "#94a3b8";
}

function renderSnagsCard(card: HTMLDivElement, snags: WireSnag[]): void {
  const rows = snags.length === 0
    ? `<div class="cr-empty" style="padding:12px 0">${SVG_SNAG}No snags raised against this customer.</div>`
    : snags.map((s) => {
        const colour = snagPriorityColor(s.priority);
        const resolved = !!s.resolvedAt;
        // Status reflects open/resolved (the user-facing model); the colour
        // dot still conveys priority.
        const status = resolved
          ? `<span class="cr-del-status" style="background:#22c55e22;color:#22c55e">Resolved</span>`
          : `<span class="cr-del-status" style="background:${colour}22;color:${colour}">Open</span>`;
        const resolveBtn = resolved
          ? ""
          : `<button type="button" class="cr-action-btn" data-role="snag-resolve" data-id="${esc(s.id)}" style="padding:4px 9px;font-size:11px;margin-left:8px">Resolve</button>`;
        return `
          <div class="cr-del-row">
            <div class="cr-del-dot" style="background:${resolved ? "#22c55e" : colour}"></div>
            <div class="cr-del-info">
              <div class="cr-del-name">${esc(s.title)}</div>
              <div class="cr-del-sub">${esc(fmtDate(s.createdAt))}${s.content && s.content !== s.title ? ` · ${esc(s.content.slice(0, 80))}${s.content.length > 80 ? "…" : ""}` : ""}</div>
            </div>
            <div style="display:flex;align-items:center">${status}${resolveBtn}</div>
          </div>
        `;
      }).join("");

  card.innerHTML = cardHtml({
    title: "Snags",
    icon: SVG_SNAG,
    count: snags.length,
    headExtra: `<button class="cr-action-btn primary" data-role="snag-add" style="padding:5px 10px;font-size:11px">${SVG_PLUS}Add snag</button>`,
    body: rows,
  });
}

async function populateSnags(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  const card = root.querySelector<HTMLDivElement>('[data-section="snags"]');
  if (!card) return;

  // Re-fetch + re-render. The dashboard "Needs Attention" widget reads the
  // same /api/portal/snags store, so an open snag added here appears there
  // automatically, and resolving one drops it from the widget.
  const refresh = async (): Promise<void> => {
    let snags: WireSnag[] = [];
    try {
      const resp = await ctx.api<{ snags?: WireSnag[] }>(
        "/portal/snags?includeResolved=1",
        { method: "GET", signal: ac.signal },
      );
      const all = Array.isArray(resp?.snags) ? resp.snags : [];
      const needle = (c.name || "").trim().toLowerCase();
      snags = needle
        ? all.filter((s) => (s.customerName || "").trim().toLowerCase() === needle)
        : [];
      snags.sort((a, b) => {
        const oa = a.resolvedAt ? 1 : 0;
        const ob = b.resolvedAt ? 1 : 0;
        if (oa !== ob) return oa - ob;
        return (b.createdAt || 0) - (a.createdAt || 0);
      });
    } catch (err) {
      if (isAbortError(err)) return;
      if (!(err instanceof ApiError && (err.status === 404 || err.status === 503))) {
        console.error("[customer] snags load failed", err);
      }
    }
    renderSnagsCard(card, snags);
  };

  // Delegated handlers, bound once on the persistent card element (survives
  // the innerHTML swaps in refresh()).
  card.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    if (t.closest("[data-role='snag-add']")) { void addSnag(); return; }
    const rb = t.closest<HTMLElement>("[data-role='snag-resolve']");
    const id = rb?.dataset["id"];
    if (id) { void resolveSnag(id); }
  });

  async function addSnag(): Promise<void> {
    const desc = await promptDialog(`Log a snag for ${c.name}`, {
      title: "Add snag",
      placeholder: "Describe the snag…",
    });
    if (!desc || !desc.trim()) return;
    const text = desc.trim();
    try {
      await ctx.api("/portal/snags", {
        method: "POST",
        body: JSON.stringify({
          customerName: c.name,
          title: text.slice(0, 200),
          content: text,
          priority: "medium",
        }),
      });
      ctx.showToast("Snag logged", "success");
      await refresh();
    } catch (err) {
      if (isAbortError(err)) return;
      ctx.showToast("Could not log snag", "error");
    }
  }

  async function resolveSnag(id: string): Promise<void> {
    try {
      await ctx.api(`/portal/snags/${encodeURIComponent(id)}/resolve`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      ctx.showToast("Snag resolved", "success");
      await refresh();
    } catch (err) {
      if (isAbortError(err)) return;
      ctx.showToast("Could not resolve snag", "error");
    }
  }

  await refresh();
}

// ── 9c. Payments & Invoices ────────────────────────────────────────────────
//
// Per-customer financial summary. Two data sources:
//   1. /portal/quotes — pulls the customer's quotes (already used by the
//      Quotes & Orders card). Approved/won quotes are treated as "ordered"
//      and contribute to the expected total. We don't have invoice rows
//      separately yet — quotes ARE the invoice surface in the modular build.
//   2. /stripe/transactions — Stripe Connect payment history. The endpoint
//      returns { items } with grossPence (pence) and a description carrying
//      "· <quote ref> — <customer>"; rows are matched to this customer by quote
//      ref (or name) and shown in pounds. (2026-10-11: the card read
//      `transactions` and `amount`, which never existed, so Paid was always £0.)
//   3. /payment-schedule/quotes/:ref — per ordered quote, the server's own
//      paid total (card stages + hand-recorded payments). This is what Paid
//      and Outstanding use, so a paid deposit counts even without a Stripe row.
//
// Renders:
//   • Header tiles: amount paid, expected total, outstanding balance
//   • Per-quote payment status (where we can match Stripe rows to a quote ref)
//   • Stripe transaction history (most recent 20)
//
// If Stripe isn't connected the card still renders quote totals — the
// transactions area shows an empty-state hint so the retailer knows the
// shortfall is "Stripe not connected" rather than "no payments".

interface WireQuoteForPayments {
  id: string;
  reference: string;
  projectName?: string;
  status: string;
  totalIncVat: number;
  sentAt?: number;
  createdAt: number;
}

interface TransactionsResp {
  items?: WireStripeItem[];
  transactions?: WireStripeItem[];
  stripeConnected?: boolean;
}

interface SchedStage {
  id: string;
  label: string;
  pct: number;
  amount: number | null;
  status?: string;
  paid?: boolean;
  /** Retailer-chosen date (YYYY-MM-DD) the request auto-fires; null = manual. */
  scheduledFor?: string | null;
}
interface QuoteSchedule {
  ref: string;
  totalIncVat: number;
  stages: SchedStage[];
}

function isOrderedStatus(s: string): boolean {
  return s === "approved" || s === "won" || s === "ordered";
}

function renderPaymentsCard(
  card: HTMLDivElement,
  quotes: WireQuoteForPayments[],
  txs: WireTransaction[],
  stripeConnected: boolean,
  schedule: QuoteSchedule | null,
  scheduleTotals: Map<string, number> = new Map(),
): void {
  const orderedQuotes = quotes.filter((q) => isOrderedStatus(q.status));
  const expectedTotal = orderedQuotes.reduce((sum, q) => sum + (q.totalIncVat || 0), 0);
  const paidTotal = orderedQuotes.reduce((sum, q) => sum + paidForQuote(q.reference, scheduleTotals, txs), 0);
  const outstanding = Math.max(expectedTotal - paidTotal, 0);

  const tiles = `
    <div class="cr-pay-tiles">
      <div class="cr-pay-tile">
        <div class="cr-pay-tile-lbl">Paid</div>
        <div class="cr-pay-tile-val" style="color:#22c55e">${esc(fmtMoney(paidTotal))}</div>
      </div>
      <div class="cr-pay-tile">
        <div class="cr-pay-tile-lbl">Expected (ordered)</div>
        <div class="cr-pay-tile-val">${esc(fmtMoney(expectedTotal))}</div>
      </div>
      <div class="cr-pay-tile">
        <div class="cr-pay-tile-lbl">Outstanding</div>
        <div class="cr-pay-tile-val" style="color:${outstanding > 0 ? "#e58200" : "#94a3b8"}">${esc(fmtMoney(outstanding))}</div>
      </div>
    </div>
  `;

  // Per-quote payment status: match Stripe rows to a quote by reference.
  const quoteRows = orderedQuotes.length === 0
    ? ""
    : `
      <div class="cr-pay-section-lbl">Quotes / invoices</div>
      ${orderedQuotes.map((q) => {
        const paid = paidForQuote(q.reference, scheduleTotals, txs);
        const due = (q.totalIncVat || 0) - paid;
        const label = paid <= 0
          ? "Unpaid"
          : due <= 0
            ? "Paid"
            : `${esc(fmtMoney(paid))} of ${esc(fmtMoney(q.totalIncVat))}`;
        const colour = paid <= 0 ? "#94a3b8" : due <= 0 ? "#22c55e" : "#e58200";
        return `
          <div class="cr-del-row">
            <div class="cr-del-dot" style="background:${colour}"></div>
            <div class="cr-del-info">
              <div class="cr-del-name">${esc(q.reference)}${q.projectName ? ` · ${esc(q.projectName)}` : ""}</div>
              <div class="cr-del-sub">${esc(fmtMoney(q.totalIncVat))} · ${esc(fmtDate(q.createdAt))}</div>
            </div>
            <span class="cr-del-status" style="background:${colour}22;color:${colour}">${label}</span>
          </div>
        `;
      }).join("")}
    `;

  // Stripe transaction history.
  const txRows = !stripeConnected
    ? `<div class="cr-empty" style="padding:10px 0">${SVG_CARD}Connect Stripe in Settings → Payments to see live transactions.</div>`
    : txs.length === 0
      ? `<div class="cr-empty" style="padding:10px 0">${SVG_CARD}No transactions recorded for this customer yet.</div>`
      : txs.slice(0, 20).map((t) => {
          const success = t.status === "succeeded" || t.status === "paid";
          const colour = success ? "#22c55e" : t.status === "refunded" ? "#94a3b8" : "#ef4444";
          return `
            <div class="cr-del-row">
              <div class="cr-del-dot" style="background:${colour}"></div>
              <div class="cr-del-info">
                <div class="cr-del-name">${esc(fmtMoney(t.amount))}${t.description ? ` · ${esc(t.description)}` : ""}</div>
                <div class="cr-del-sub">${esc(fmtDateTime(t.createdAt))}${t.quoteRef ? ` · ${esc(t.quoteRef)}` : ""}</div>
              </div>
              <span class="cr-del-status" style="background:${colour}22;color:${colour}">${esc(t.status)}</span>
            </div>
          `;
        }).join("");

  // Milestone payment schedule — with a "Request payment" action per stage.
  // Stages start 'pending' (created on quote acceptance), move to 'sent' when
  // the retailer requests them, and 'paid' once the customer pays.
  const scheduleSection = !schedule || schedule.stages.length === 0
    ? ""
    : `
      <div class="cr-pay-section-lbl">Payment schedule · ${esc(schedule.ref)}</div>
      ${schedule.stages.map((s) => {
        const amt = s.amount != null
          ? s.amount
          : (schedule.totalIncVat ? Math.round(schedule.totalIncVat * s.pct) / 100 : 0);
        const st = s.status || (s.paid ? "paid" : "pending");
        const colour = st === "paid" ? "#22c55e" : st === "sent" ? "#e58200" : "#94a3b8";
        const action = st === "paid"
          ? `<span class="cr-del-status" style="background:#22c55e22;color:#22c55e">Paid</span>`
          : `<button type="button" class="cr-pay-request" data-cr-request-payment data-stage="${esc(s.id)}">${st === "sent" ? "Resend" : "Request payment"}</button>`;
        // Auto-send scheduler — only for stages not yet sent/paid. Blank date =
        // send manually now ("Request payment"); a date = the request fires
        // automatically at 9am on that day.
        const dateVal = (s.scheduledFor || "").slice(0, 10);
        const scheduler = (st === "paid" || st === "sent")
          ? ""
          : `
            <div class="cr-pay-sched">
              ${dateVal
                ? `<span class="cr-pay-sched-lbl">Auto-send on</span>
                   <input type="date" class="cr-pay-sched-input" data-cr-schedule data-stage="${esc(s.id)}" data-amount="${esc(String(amt))}" data-label="${esc(s.label)}" value="${esc(dateVal)}" />
                   <span class="cr-pay-sched-on">scheduled · ${esc(fmtDate(dateVal))}</span><button type="button" class="cr-pay-sched-clear" data-cr-schedule-clear data-stage="${esc(s.id)}">Clear</button>`
                // No date saved: say so in words. The date box stays hidden until
                // asked for — Safari draws an EMPTY date box with today's date,
                // which read as "Auto-send on <today>" (2026-10-11).
                : `<span class="cr-pay-sched-none">No auto-send, manual only</span>
                   <button type="button" class="cr-pay-sched-set" data-cr-schedule-set data-stage="${esc(s.id)}">Set a date…</button>
                   <input type="date" class="cr-pay-sched-input" data-cr-schedule data-stage="${esc(s.id)}" data-amount="${esc(String(amt))}" data-label="${esc(s.label)}" value="" hidden />`}
            </div>`;
        return `
          <div class="cr-del-row">
            <div class="cr-del-dot" style="background:${colour}"></div>
            <div class="cr-del-info">
              <div class="cr-del-name">${esc(s.label)} · ${esc(fmtMoney(amt))}</div>
              <div class="cr-del-sub">${s.pct}% · ${esc(st)}</div>
              ${scheduler}
            </div>
            ${action}
          </div>
        `;
      }).join("")}
    `;

  const txSection = `
    <div class="cr-pay-section-lbl">Stripe transactions</div>
    ${txRows}
  `;

  const body = `${tiles}${scheduleSection}${quoteRows}${txSection}`;

  card.innerHTML = cardHtml({
    title: "Payments &amp; Invoices",
    icon: SVG_CARD,
    count: orderedQuotes.length,
    body,
  });
}

async function populatePayments(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  const card = root.querySelector<HTMLDivElement>('[data-section="payments"]');
  if (!card) return;

  let quotes: WireQuoteForPayments[] = [];
  let txs: WireTransaction[] = [];
  let stripeConnected = false;

  // Quotes — same endpoint the Quotes card uses, with customerName fallback.
  try {
    const qs = new URLSearchParams({ customerId: c.id });
    if (c.name) qs.set("customerName", c.name);
    const resp = await ctx.api<{ quotes?: WireQuoteForPayments[] }>(
      `/portal/quotes?${qs.toString()}`,
      { method: "GET", signal: ac.signal },
    );
    if (resp && Array.isArray(resp.quotes)) quotes = resp.quotes;
  } catch (err) {
    if (isAbortError(err)) return;
    if (!(err instanceof ApiError && err.status === 404)) {
      console.error("[customer] payments/quotes load failed", err);
    }
  }

  // Stripe transactions — filter client-side by customer name. The transactions
  // endpoint accepts limit but not a customer filter; we ask for a generous
  // page and filter locally so a single round-trip covers the card.
  try {
    const resp = await ctx.api<TransactionsResp>(
      "/stripe/transactions?limit=100",
      { method: "GET", signal: ac.signal },
    );
    stripeConnected = resp?.stripeConnected !== false;
    const raw = Array.isArray(resp?.items) ? resp.items : Array.isArray(resp?.transactions) ? resp.transactions : [];
    const refs = new Set(quotes.map((q) => q.reference));
    txs = raw
      .map(normaliseStripeItem)
      .filter((t) => txBelongsTo(t, c.id, c.name || "", refs))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  } catch (err) {
    if (isAbortError(err)) return;
    // 404/501 → Stripe endpoint not wired. 503 → Stripe down. Treat both as
    // "not connected" for UI purposes and let the card render with the
    // connect-hint empty state.
    if (err instanceof ApiError && (err.status === 404 || err.status === 501 || err.status === 503)) {
      stripeConnected = false;
    } else {
      console.error("[customer] payments/stripe load failed", err);
    }
  }

  // Milestone payment schedule for this customer's accepted (or most recent)
  // quote — created on acceptance. Only render a STORED schedule (quote != null),
  // not the default-stage fallback the endpoint returns for un-scheduled quotes.
  // Also: each ordered quote's server-side paid total, for the tiles.
  let schedule: QuoteSchedule | null = null;
  const scheduleTotals = new Map<string, number>();
  const target = quotes.find((q) => isOrderedStatus(q.status)) || quotes[0];
  const toFetch = new Map<string, WireQuoteForPayments>();
  for (const q of quotes) if (isOrderedStatus(q.status)) toFetch.set(q.reference, q);
  if (target) toFetch.set(target.reference, target);
  for (const q of toFetch.values()) {
    try {
      const resp = await ctx.api<{ quote?: unknown; stages?: SchedStage[]; totals?: { paid?: number } }>(
        `/payment-schedule/quotes/${encodeURIComponent(q.reference)}`,
        { method: "GET", signal: ac.signal },
      );
      if (resp && resp.quote && Array.isArray(resp.stages)) {
        if (typeof resp.totals?.paid === "number") scheduleTotals.set(q.reference, resp.totals.paid);
        if (q === target) schedule = { ref: q.reference, totalIncVat: q.totalIncVat || 0, stages: resp.stages };
      }
    } catch (err) {
      if (isAbortError(err)) return;
    }
  }

  renderPaymentsCard(card, quotes, txs, stripeConnected, schedule, scheduleTotals);

  if (schedule) {
    const ref = schedule.ref;
    card.querySelectorAll<HTMLButtonElement>("[data-cr-request-payment]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const stageId = btn.dataset["stage"];
        if (!stageId) return;
        // Point-of-collection prompt: platform access is no longer gated on
        // Stripe, so a retailer can reach this schedule without a payment rail
        // connected. Don't send a request the customer can't pay — nudge them
        // to connect first (Stripe is the wired customer-collection rail today).
        if (!stripeConnected) {
          void confirmDialog(
            "You haven't connected a way to get paid yet. Connect Stripe in Settings → Payments to request and collect customer payments.",
            { title: "Connect payments first", confirmLabel: "Go to Payments", cancelLabel: "Not now" },
          ).then((go) => {
            if (go) ctx.navigate("settings", { tab: "payments" });
          });
          return;
        }
        btn.disabled = true;
        btn.textContent = "Sending…";
        void (async () => {
          try {
            const resp = await ctx.api<{ ok: boolean }>("/portal/payments/request", {
              method: "POST",
              body: JSON.stringify({
                quoteId: ref,
                stageId,
                retailerId: ctx.user?.retailerId,
                retailerName: ctx.user?.retailerName,
                // customerId lets the server resolve the REAL (unmasked) email/
                // phone — c.email/c.phone are masked in the retailer portal.
                customerId: c.id,
                customerName: c.name,
                customerEmail: c.email,
                customerPhone: c.phone,
              }),
            });
            if (!resp.ok) throw new Error("request failed");
            ctx.showToast("Payment request sent to customer", "success");
            ctx.track({ eventType: "action", feature: "payments", customData: { action: "request-payment" } });
            void populatePayments(root, ctx, ac, c);
          } catch {
            btn.disabled = false;
            btn.textContent = "Request payment";
            ctx.showToast("Couldn't send payment request", "error");
          }
        })();
      });
    });

    // Save / clear a stage's auto-send date. Persists contact + retailer
    // identity so the daily 9am scheduler can fire the request and notify the
    // retailer. No charge is taken — the customer still pays manually.
    const saveSchedule = (stageId: string, scheduledFor: string): void => {
      void (async () => {
        try {
          const resp = await ctx.api<{ ok: boolean }>(
            `/payment-schedule/quotes/${encodeURIComponent(ref)}/payments/${encodeURIComponent(stageId)}/schedule`,
            {
              method: "POST",
              body: JSON.stringify({
                scheduledFor,
                retailerId: ctx.user?.retailerId,
                retailerName: ctx.user?.retailerName,
                customerId: c.id,
                customerName: c.name,
                customerEmail: c.email,
                customerPhone: c.phone,
              }),
            },
          );
          if (!resp.ok) throw new Error("save failed");
          ctx.showToast(
            scheduledFor ? "Auto-send date saved" : "Auto-send cancelled",
            "success",
          );
          void populatePayments(root, ctx, ac, c);
        } catch {
          ctx.showToast("Couldn't save the send date", "error");
        }
      })();
    };
    card.querySelectorAll<HTMLButtonElement>("[data-cr-schedule-set]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const inp = card.querySelector<HTMLInputElement>(`[data-cr-schedule][data-stage="${CSS.escape(btn.dataset["stage"] || "")}"]`);
        if (!inp) return;
        inp.hidden = false;
        btn.hidden = true;
        inp.focus();
        try { (inp as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); } catch { /* not supported */ }
      });
    });
    card.querySelectorAll<HTMLInputElement>("[data-cr-schedule]").forEach((inp) => {
      inp.addEventListener("change", () => {
        const stageId = inp.dataset["stage"];
        if (!stageId || !inp.value) return; // clearing goes through the Clear button
        void (async () => {
          // Saving a date makes the system send a real payment request on its
          // own, so name exactly what will go, to whom and when (2026-10-11).
          const ok = await confirmDialog(
            autoSendConfirmText(Number(inp.dataset["amount"]) || 0, inp.dataset["label"] || "payment", c.name || "the customer", inp.value),
            { title: "Send this payment request automatically?", confirmLabel: "Yes, schedule it" },
          );
          if (ok) saveSchedule(stageId, inp.value);
          else void populatePayments(root, ctx, ac, c); // put the box back as it was
        })();
      });
    });
    card.querySelectorAll<HTMLButtonElement>("[data-cr-schedule-clear]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const stageId = btn.dataset["stage"];
        if (stageId) saveSchedule(stageId, "");
      });
    });
  }
}

// ── 10. Activity timeline ──────────────────────────────────────────────────

async function populateTimeline(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  const card = root.querySelector<HTMLDivElement>('[data-section="timeline"]');
  if (!card) return;

  // Derive timeline from whatever the other sections already fetched.
  // We re-hit calendar + deliveries (cheap, cached upstream) so this card
  // is independent of card-population order.
  const events: Array<{ ts: number; text: string; color: string }> = [];

  // Stage change as the seed entry.
  events.push({
    ts: Date.now(),
    text: `Stage: ${c.stage}`,
    color: stageColor(c.stage),
  });

  if (c.lastTouch && c.lastTouch !== "—") {
    const ts = toMs(c.lastTouch);
    if (ts != null) {
      events.push({ ts, text: `Last touch — ${c.lastTouch}`, color: "#3b82f6" });
    }
  }

  // Deliveries → timeline
  try {
    const resp = await ctx.api<{ deliveries?: ServerDelivery[] }>(
      "/portal/deliveries",
      { method: "GET", signal: ac.signal },
    );
    if (resp && Array.isArray(resp.deliveries)) {
      const lc = c.name.toLowerCase();
      const firstName = lc.split(/\s+/)[0] ?? "";
      for (const d of resp.deliveries) {
        const candidate =
          typeof d.customer === "string"
            ? d.customer
            : d.customer && typeof d.customer === "object"
            ? d.customer.name ?? ""
            : "";
        if (!candidate.toLowerCase().includes(firstName)) continue;
        const ts = toMs(d.eta ?? null) ?? Date.now();
        events.push({
          ts,
          text: `Delivery: ${d.supplier ?? d.ref ?? "Order"}`,
          color: "#22c55e",
        });
      }
    }
  } catch (err) {
    if (isAbortError(err)) return;
    // 404 already logged elsewhere
  }

  events.sort((a, b) => b.ts - a.ts);
  const trimmed = events.slice(0, 20);

  const body =
    trimmed.length === 0
      ? emptyStateHtml(SVG_BOLT, "No activity recorded yet.")
      : `<div class="cr-timeline">${trimmed
          .map((ev) => {
            const ts = new Date(ev.ts).toLocaleString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });
            return `
              <div class="cr-tl-item">
                <div class="cr-tl-dot" style="background:${esc(ev.color)}22;border:2px solid ${esc(ev.color)}"></div>
                <div class="cr-tl-content">
                  <div class="cr-tl-text">${esc(ev.text)}</div>
                  <div class="cr-tl-time">${esc(ts)}</div>
                </div>
              </div>
            `;
          })
          .join("")}</div>`;

  card.innerHTML = cardHtml({
    title: "Activity Timeline",
    icon: SVG_BOLT,
    count: trimmed.length,
    body,
  });
}

// ── 11. Next Action prompt ────────────────────────────────────────────────
//
// Single-line, rule-based suggestion the retailer should act on now. Reads
// the customer's pipeline stage, last-touch age, latest quote status/age,
// upcoming installation/survey events, and open snags. AI-powered version
// will replace the rule engine later; same render contract.
//
// The rule engine returns ONE NextAction. Rules are evaluated top-to-bottom
// and the first match wins, so most-urgent-first ordering is the spec.

interface NextActionWireQuote {
  id: string;
  reference: string;
  status: string;
  totalIncVat: number;
  sentAt?: number;
  createdAt: number;
}

interface NextActionContext {
  c: Customer;
  quotes: NextActionWireQuote[];
  openSnag: WireSnag | null;
  upcomingInstallationMs: number | null;
  hasSiteSurveyBooking: boolean;
  hasScheduledInstallation: boolean;
}

interface NextAction {
  headline: string;
  sub?: string;
  buttonLabel?: string;
  /** Action token consumed by the card click handler. */
  action?:
    | { kind: "focus"; target: "messages" | "bookings" | "snags" }
    | { kind: "nav-quote"; ref: string }
    | { kind: "nav"; page: "operations" | "calendar" | "messages" | "quote-builder" };
  tone?: "default" | "muted";
}

function daysBetween(fromMs: number, toMs: number): number {
  return Math.floor((toMs - fromMs) / 86_400_000);
}

function pickNextAction(ctxData: NextActionContext): NextAction {
  const { c, quotes, openSnag, upcomingInstallationMs,
          hasSiteSurveyBooking, hasScheduledInstallation } = ctxData;
  const now = Date.now();
  // Supply-only jobs have no installation/site-survey stage (migration 0034),
  // so the install-centric prompts (rules 2/3/4) must not fire — they get a
  // delivery-led action instead.
  const supplyOnly = effectiveSupply(c) === "supply_only";

  // 1. Open snag — highest priority. Customer has an issue.
  if (openSnag) {
    return {
      headline: `Resolve snag — ${openSnag.title || "open issue"}`,
      sub: openSnag.priority === "high"
        ? "High priority — respond today"
        : "Customer is waiting on a resolution",
      buttonLabel: "Open in Operations",
      action: { kind: "nav", page: "operations" },
    };
  }

  // 2. Installation imminent without a site survey booked. (Supply-only jobs
  //    have no installation, so this never applies to them.)
  if (!supplyOnly && upcomingInstallationMs != null && !hasSiteSurveyBooking) {
    const days = Math.max(0, daysBetween(now, upcomingInstallationMs));
    return {
      headline: `Book survey — installation in ${days} day${days === 1 ? "" : "s"}`,
      sub: "No site survey is booked yet for this installation",
      buttonLabel: "Open calendar",
      action: { kind: "nav", page: "calendar" },
    };
  }

  // 3. Installed — ask for a review.
  if (c.stage === "Installed") {
    return {
      headline: supplyOnly
        ? "Request Google review — order complete"
        : "Request Google review — installation complete",
      sub: "Reviews close the loop and feed the dashboard rating widget",
      buttonLabel: "Send review request",
      action: { kind: "focus", target: "messages" },
    };
  }

  // 4a. Won + supply-only — no install to book; arrange delivery and balance.
  if (c.stage === "Won" && supplyOnly) {
    return {
      headline: "Arrange delivery & request balance",
      sub: "Supply-only order — arrange delivery or collection and request the final balance",
      buttonLabel: "Message customer",
      action: { kind: "focus", target: "messages" },
    };
  }

  // 4b. Won (supply & install) but no installation scheduled yet.
  if (c.stage === "Won" && !hasScheduledInstallation) {
    return {
      headline: "Schedule installation",
      sub: "Customer has signed off — book the install date",
      buttonLabel: "Open calendar",
      action: { kind: "nav", page: "calendar" },
    };
  }

  // 5. Approved — quote signed, request deposit.
  if (c.stage === "Approved") {
    return {
      headline: "Request deposit — quote approved",
      sub: "Customer has approved the quote; request the deposit to lock in",
      buttonLabel: "Message customer",
      action: { kind: "focus", target: "messages" },
    };
  }

  // 6. Quoted, latest sent quote ageing.
  if (c.stage === "Quoted") {
    const sentQuotes = quotes
      .filter((q) => q.status === "sent" && typeof q.sentAt === "number")
      .sort((a, b) => (b.sentAt ?? 0) - (a.sentAt ?? 0));
    const latest = sentQuotes[0];
    if (latest) {
      const days = daysBetween(latest.sentAt ?? now, now);
      if (days >= 5) {
        return {
          headline: `Chase quote — sent ${days} days ago, no response`,
          sub: `Reference ${latest.reference}`,
          buttonLabel: "Open quote",
          action: { kind: "nav-quote", ref: latest.reference },
        };
      }
      return {
        headline: `Awaiting customer response — quote sent ${days} day${days === 1 ? "" : "s"} ago`,
        sub: `Reference ${latest.reference}`,
        buttonLabel: "Open quote",
        action: { kind: "nav-quote", ref: latest.reference },
        tone: "muted",
      };
    }
    // Stage is Quoted but no sent quote on file — Lost-in-translation case.
    return {
      headline: "Send the quote to the customer",
      sub: "Stage is Quoted but no quote has been sent yet",
      buttonLabel: "Open quote builder",
      action: { kind: "nav", page: "quote-builder" },
    };
  }

  // 7. Lead / Qualified — follow-up by last-touch age.
  if (c.stage === "Lead" || c.stage === "Qualified") {
    const lastMs = toMs(c.lastTouch);
    if (lastMs != null) {
      const days = daysBetween(lastMs, now);
      if (days >= 5) {
        return {
          headline: `Follow up — no contact in ${days} day${days === 1 ? "" : "s"}`,
          sub: c.stage === "Lead"
            ? "Warm the lead before it goes cold"
            : "Qualified — ready for a quote",
          buttonLabel: "Send follow up message",
          action: { kind: "focus", target: "messages" },
        };
      }
    }
    return {
      headline: c.stage === "Lead"
        ? "Send welcome message"
        : "Build the quote — qualified, no quote yet",
      sub: c.stage === "Lead"
        ? "New lead — open the conversation"
        : "Move them to Quoted",
      buttonLabel: c.stage === "Lead" ? "Message customer" : "Open quote builder",
      action: c.stage === "Lead"
        ? { kind: "focus", target: "messages" }
        : { kind: "nav", page: "quote-builder" },
    };
  }

  // 8. Lost — closed opportunity.
  if (c.stage === "Lost") {
    return {
      headline: "No action — opportunity closed",
      sub: "Move to Won later if the customer comes back",
      tone: "muted",
    };
  }

  // 9. Fallback — generic poke.
  return {
    headline: "Review customer record",
    sub: `Stage: ${c.stage}`,
    buttonLabel: "Message customer",
    action: { kind: "focus", target: "messages" },
  };
}

function renderNextActionCard(card: HTMLDivElement, na: NextAction): void {
  const btn = na.action && na.buttonLabel
    ? `<button type="button" class="cr-next-action-btn" data-cr-na-action="1">${esc(na.buttonLabel)}</button>`
    : "";
  const subHtml = na.sub
    ? `<div class="cr-next-action-sub">${esc(na.sub)}</div>`
    : "";
  card.innerHTML = `
    <div class="cr-card-head">
      <div class="cr-card-title">${SVG_BOLT}Next Action</div>
    </div>
    <div class="cr-card-body cr-next-action-body${na.tone === "muted" ? " is-muted" : ""}">
      <div class="cr-next-action-headline">${esc(na.headline)}</div>
      ${subHtml}
      ${btn}
    </div>
  `;
}

function bindNextAction(card: HTMLDivElement, root: HTMLDivElement, c: Customer, na: NextAction): void {
  const btn = card.querySelector<HTMLButtonElement>("[data-cr-na-action='1']");
  if (!btn || !na.action) return;
  btn.addEventListener("click", () => {
    const a = na.action!;
    if (a.kind === "focus") {
      const target = root.querySelector<HTMLElement>(`[data-section="${a.target}"]`);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        // For Messages, also try to focus the compose input.
        if (a.target === "messages") {
          const input = target.querySelector<HTMLTextAreaElement | HTMLInputElement>(
            "textarea, input[type='text']",
          );
          if (input) window.setTimeout(() => input.focus(), 320);
        }
      }
      return;
    }
    if (a.kind === "nav-quote") {
      navigate("quote-builder", { quote: a.ref, customer: c.name, customerId: c.id });
      return;
    }
    if (a.kind === "nav") {
      if (a.page === "quote-builder") {
        navigate("quote-builder", { customer: c.name, customerId: c.id });
      } else {
        navigate(a.page);
      }
    }
  });
}

async function populateNextAction(
  root: HTMLDivElement,
  ctx: PageContext,
  ac: AbortController,
  c: Customer,
): Promise<void> {
  const card = root.querySelector<HTMLDivElement>('[data-section="next-action"]');
  if (!card) return;

  // Fire all four fetches in parallel. Each one is best-effort: a failed
  // fetch just means that signal contributes nothing to the rule engine,
  // never that the whole card fails.
  const needle = c.name.trim().toLowerCase();
  const firstName = needle.split(/\s+/)[0] ?? "";

  const qs = new URLSearchParams({ customerId: c.id });
  if (c.name) qs.set("customerName", c.name);

  const fetchQuotes = ctx.api<{ quotes?: NextActionWireQuote[] }>(
    `/portal/quotes?${qs.toString()}`,
    { method: "GET", signal: ac.signal },
  ).then((r) => Array.isArray(r?.quotes) ? r.quotes : []).catch((): NextActionWireQuote[] => []);

  const fetchSnags = ctx.api<{ snags?: WireSnag[] }>(
    "/portal/snags?includeResolved=0",
    { method: "GET", signal: ac.signal },
  ).then((r) => {
    const list = Array.isArray(r?.snags) ? r.snags : [];
    return list.filter((s) => (s.customerName || "").trim().toLowerCase() === needle);
  }).catch((): WireSnag[] => []);

  const fetchBookings = ctx.api<{ bookings?: ServerBooking[] }>(
    "/portal/bookings",
    { method: "GET", signal: ac.signal },
  ).then((r) => {
    const list = Array.isArray(r?.bookings) ? r.bookings : [];
    return list.filter((b) =>
      String(b.customer ?? "").toLowerCase().includes(firstName),
    );
  }).catch((): ServerBooking[] => []);

  const fetchEvents = ctx.api<{ events?: ServerCalendarEvent[] }>(
    "/portal/calendar",
    { method: "GET", signal: ac.signal },
  ).then((r) => {
    const list = Array.isArray(r?.events) ? r.events : [];
    return list.filter((e) =>
      String(e.customer ?? "").toLowerCase().includes(firstName),
    );
  }).catch((): ServerCalendarEvent[] => []);

  const [quotes, snags, bookings, events] = await Promise.all([
    fetchQuotes, fetchSnags, fetchBookings, fetchEvents,
  ]);

  if (ac.signal.aborted) return;

  const now = Date.now();

  // Highest-priority unresolved snag — `includeResolved=0` should already
  // filter, but defend against backends that ignore the flag.
  const openSnag = snags
    .filter((s) => !s.resolvedAt)
    .sort((a, b) => {
      const rank = (p: string): number => (p === "high" ? 0 : p === "medium" ? 1 : 2);
      return rank(a.priority) - rank(b.priority);
    })[0] ?? null;

  // Earliest upcoming installation, plus flags for site-survey and any
  // scheduled installation event in the future.
  let upcomingInstallationMs: number | null = null;
  let hasSiteSurveyBooking = false;
  let hasScheduledInstallation = false;

  for (const e of events) {
    const d = e.date && e.time ? `${e.date}T${e.time}` : e.date ?? null;
    const t = toMs(d);
    if (t == null || t < now) continue;
    const type = String(e.type ?? "").toLowerCase();
    const title = String(e.title ?? "").toLowerCase();
    if (type.includes("install") || title.includes("install")) {
      hasScheduledInstallation = true;
      if (upcomingInstallationMs == null || t < upcomingInstallationMs) {
        upcomingInstallationMs = t;
      }
    }
    if (type.includes("survey") || title.includes("survey")) {
      hasSiteSurveyBooking = true;
    }
  }
  for (const b of bookings) {
    const t = toMs((b.when ?? b.scheduled_at ?? null) as number | string | null);
    if (t == null || t < now) continue;
    const bt = String(b.booking_type ?? b.type ?? "").toLowerCase();
    if (bt.includes("survey")) hasSiteSurveyBooking = true;
    if (bt.includes("install")) {
      hasScheduledInstallation = true;
      if (upcomingInstallationMs == null || t < upcomingInstallationMs) {
        upcomingInstallationMs = t;
      }
    }
  }

  // Only treat as "imminent" if within 14 days.
  if (upcomingInstallationMs != null && upcomingInstallationMs - now > 14 * 86_400_000) {
    upcomingInstallationMs = null;
  }

  const na = pickNextAction({
    c,
    quotes,
    openSnag,
    upcomingInstallationMs,
    hasSiteSurveyBooking,
    hasScheduledInstallation,
  });

  renderNextActionCard(card, na);
  bindNextAction(card, root, c, na);
}

// ── Register ────────────────────────────────────────────────────────────────

registerPage(customerPage);
